import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, addQuarters, subQuarters, addYears, subYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, eachDayOfInterval } from 'date-fns';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, Loader2, CalendarIcon, Search, X, Eye, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useScheduler, BookingBatchUpdate } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useFilters } from '@/contexts/FilterContext';
import { CalendarView, Resource, Booking, Absence, BankHoliday, BookingStatus, WorkingHoursProfile, RecurrenceSettings, ProjectDetails, BookingType } from '@/types';
import { isCostCentreValidForProject, isCustomerValidForProject } from '@/lib/project-cost-centres';
import { userCharIndex, validateUserCharBooking } from '@/lib/user-char-lookups';
import { BryntumSchedulerTimeline, BryntumSchedulerTimelineHandle } from './BryntumSchedulerTimeline';
import { ComprehensiveBookingModal } from './ComprehensiveBookingModal';
import { CalendarFilterDrawer, CalendarFilterDrawerTrigger } from './CalendarFilterDrawer';
import { BookingDragConfirmDialog } from './BookingDragConfirmDialog';
import { DeleteBookingConfirmDialog } from './DeleteBookingConfirmDialog';
import { DeleteBookingsConfirmDialog } from './DeleteBookingsConfirmDialog';
import { EditBookingConfirmDialog } from './EditBookingConfirmDialog';
import { EditDemandBookingConfirmDialog } from './EditDemandBookingConfirmDialog';
import { MoveDemandBookingConfirmDialog } from './MoveDemandBookingConfirmDialog';
import { ReassignDemandBookingConfirmDialog } from './ReassignDemandBookingConfirmDialog';
import { BookingSaveCancelledError } from '@/lib/booking-save-cancelled';
import { canCopyBooking, canMoveBooking } from '@/lib/bookingSelection';
import { canApplyAsBatchPatch, classifySeriesEdit, hasRecurrenceOptionsChanged, hasScheduleChanged, seriesDetailChanges, shiftRecurrenceWithStart } from '@/lib/seriesEdit';
import { getNavMonths } from '@/lib/datePickerNav';
import {
  autoAdjustGroupDrag,
  autoAdjustGroupEditTarget,
  autoAdjustGroupIds,
  autoAdjustGroupPieces,
  autoAdjustGroupSpan,
  isAutoAdjustGroupPiece,
  planAutoAdjustGroupDrags,
  planAutoAdjustGroupEdit,
  widenedAutoAdjustGroupSpan,
  withAutoAdjustGroupRecurrence,
  withReadAutoAdjustGroupRows,
} from '@/lib/autoAdjustGroup';
import { autoAdjustsAroundNonWorkingDays } from '@/utils/booking-working-segments';
import { bookingMatchesSearch } from '@/utils/booking-search';
import { CurrentTimezoneSelector } from './CurrentTimezoneSelector';
import { QuickAddBookingModal, QuickAddBookingData } from './QuickAddBookingModal';
import { MoveBookingDialog } from '@/components/booking/MoveBookingDialog';
import { AIBatchSuggestModal } from '@/components/unallocated/AIBatchSuggestModal';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useCompany } from '@/contexts/CompanyContext';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useBookingDrag } from '@/hooks/use-booking-drag';
import { useDateRangeBookings } from '@/hooks/use-date-range-bookings';
import { useEventsPaintLatch } from '@/hooks/use-events-paint-latch';
import { convertBookingTimezone } from '@/utils/timezone-utils';
import { getUnallocatedAwareGroupName, UNALLOCATED_RESOURCE_ID } from '@/utils/unallocated-booking';
import { RESOURCE_PAGE_SIZE } from '@/utils/resource-window';
import { buildBookingResourceMoveFields } from '@/utils/booking-resource-move';
import { canAssignBookingToResource, checkContractWindow, type ResourceAvailabilityResult } from '@/utils/resource-availability';
import { perfStart } from '@/utils/perf-log';
import { planCapacityAwareReassignments } from '@/utils/booking-conflict';
import { generateRecurrenceDates as generateRecurrenceDatesPure } from '@/utils/recurrence-dates';
import {
  buildOccurrenceScopedBooking,
  buildOccurrenceUpdate,
  splitOccurrenceResourceIds,
} from '@/utils/occurrence-resource-scope';
import {
  buildReassignmentMutations,
  buildReductionMutations,
  buildDelayMutations,
  type BookingFieldUpdate,
  type ReductionMutations,
} from '@/utils/resolve-conflict-distribution';
import { reassignedResourceFields } from '@/utils/reassign-booking-resource';
import { toast as sonnerToast } from 'sonner';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import { HeatmapLegend } from './HeatmapLegend';
import { BurnoutRiskPanelWrapper, WhatIfCommitResult } from './BurnoutRiskPanel';
import { computeBurnoutRisk } from '@/lib/utils/burnout-risk';
import { WhatIfScenario, type PlannerNavContext } from '@/types';
import { HelpButton } from '@/components/help/HelpButton';
import { WhatIfReductionSummary, type WhatIfReductionSummaryData } from './WhatIfReductionSummary';
import { formatMinuteOfDay } from '@/utils/whatif-reduction';
import { useConflictResolutionSettings } from '@/hooks/useConflictResolutionSettings';
import { getCurrentResourceBookingTypeUsages, absencesApi, bookingsApi, recurrenceSettingsApi, isForbiddenError, type BookingTypeUsageDto, type RecurrenceSettingsApi } from '@/services/api';
import { isBookingValidationError, reportBookingValidationError } from '@/lib/booking-validation-alert';
import { type PlanPreviewScenario } from '@/types/resourceRequest';
import { formatInTimeZone } from 'date-fns-tz';
import { dayKey } from '@/lib/slotEditValidation';
import {
  clampDatesToContract,
  hasContractWindow,
  isDayWithinContract,
  outOfContractRanges,
} from '@/lib/contractWindow';
import { computePastedBookingTimes } from '@/utils/paste-booking-times';
import {
  buildDemandPlanGroupDeleteRequest,
  buildDemandPlanGroupUpdatePayload,
  isLinkedDemandBooking,
  normalizeDemandReassignScope,
  propagateDemandBookingTemporalUpdate,
  shouldPromptDemandReassign,
} from '@/lib/demand-booking';

/** Day view starts at 15-minute intervals, every other view at its coarsest zoom. */
const defaultZoomLevel = (view: CalendarView): number => (view === 'day' ? 1 : 0);

const FALLBACK_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
  saturday: 0, sunday: 0,
};

const isBookingTypeFieldCompulsory = (
  bookingType: BookingType | undefined,
  fieldName: string,
): boolean =>
  bookingType?.fieldConfigurations?.some(
    configuration => configuration.fieldName?.toLowerCase() === fieldName.toLowerCase()
      && configuration.isCompulsory,
  ) ?? false;

// Helper function to format a Date as ISO-like string using LOCAL time values
// NO "Z" suffix - this prevents JavaScript from interpreting as UTC and applying timezone offset
const formatDateAsLocalISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  // No Z suffix to prevent UTC interpretation
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};

// A single occurrence of a recurring series, reduced to the fields the
// "all future bookings" flows need. Occurrences can come either from the
// timeline or from the API, which return differently shaped bookings.
interface SeriesOccurrence {
  id: string;
  startTime: string;
}

// Maps a persisted RecurrenceSettings row onto the shape the booking form uses.
const mapRecurrenceSettingsApi = (rs: RecurrenceSettingsApi): RecurrenceSettings => {
  let daysOfWeek: string[] | undefined;
  let monthlyRecurrenceType: RecurrenceSettings['monthlyRecurrenceType'];
  let dayOfMonth: number | undefined;
  let relativeWeekday: RecurrenceSettings['relativeWeekday'];

  if (rs.pattern === 'weekly' && rs.daysOfWeek) {
    try {
      const parsed = JSON.parse(rs.daysOfWeek);
      daysOfWeek = Array.isArray(parsed) ? parsed : [rs.daysOfWeek];
    } catch {
      daysOfWeek = [rs.daysOfWeek];
    }
  }

  if (rs.pattern === 'monthly') {
    if (rs.weekOfMonth && rs.daysOfWeek) {
      monthlyRecurrenceType = 'relative-weekday';
      const occurrenceMap: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
        1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'last',
      };
      relativeWeekday = {
        occurrence: occurrenceMap[rs.weekOfMonth] || 'first',
        weekday: rs.daysOfWeek,
      };
    } else if (rs.dayOfMonth) {
      monthlyRecurrenceType = 'day-of-month';
      dayOfMonth = rs.dayOfMonth;
    }
  }

  return {
    pattern: rs.pattern as RecurrenceSettings['pattern'],
    interval: rs.interval,
    endType: rs.endType as RecurrenceSettings['endType'],
    occurrences: rs.endAfterOccurrences,
    endDate: rs.endDate,
    daysOfWeek,
    monthlyRecurrenceType,
    dayOfMonth,
    relativeWeekday,
  };
};

/**
 * The details of an edited occurrence as a patch for the occurrences following it.
 * Everything that identifies a booking or ties it to its series is left out, so each
 * occurrence keeps its own id, recurrence links and tenancy — the fields a batch
 * update rejects outright.
 */
const seriesDetailPatch = (booking: Partial<Booking>): Partial<Booking> => {
  const {
    id: _id,
    recurrence: _recurrence,
    recurrenceGroupId: _recurrenceGroupId,
    isRecurrenceParent: _isRecurrenceParent,
    parentBookingId: _parentBookingId,
    demandPlanGroupId: _demandPlanGroupId,
    companyId: _companyId,
    ...details
  } = booking;
  return details;
};

/**
 * A proposed day's start/end are UTC wall-clock timestamps (the planner and the
 * plan drawer both read them in UTC). Bookings on the timeline carry naive
 * wall-clock strings, so drop the zone rather than re-offsetting the times.
 * Returns null when the timestamp cannot be parsed.
 */
const planPreviewWallClock = (utcIso: string): string | null => {
  try {
    const parsed = parseISO(utcIso);
    if (isNaN(parsed.getTime())) return null;
    return formatInTimeZone(parsed, 'UTC', "yyyy-MM-dd'T'HH:mm:ss");
  } catch {
    return null;
  }
};

// A booking write the API rejected for a reason it can state: HTTP 403 because the caller
// lacks the required bookingPermissions, or HTTP 400 because the booking breaks a rule
// such as a booking type that obeys the working profile and bank holidays — the latter is
// shown in a dialog. Returns true when the rejection has been reported, so callers skip
// their generic "Please try again." toast.
const handleBookingWriteRejected = (error: unknown, action: 'create' | 'modify'): boolean => {
  if (reportBookingValidationError(error)) return true;
  if (!isForbiddenError(error)) return false;
  sonnerToast.error('Permission Denied', {
    description: `You don't have permission to ${action} this booking.`,
  });
  return true;
};

interface ResourceCalendarProps {
  initialHeatmapMode?: boolean;
  /** Pre-selects a project filter when the heatmap is opened from Project Staffing */
  initialProjectFilter?: string | null;
  /** What-if scenario handed off from the heatmap panel — activates what-if mode */
  whatIfScenario?: WhatIfScenario | null;
  /** Called when the scenario is committed (true) or discarded (false) */
  onWhatIfComplete?: (navigateBackToHeatmap?: boolean) => void;
  /**
   * Called when a rebalance scenario is committed, carrying the before/after
   * utilisation so the heatmap's burnout panel can render the resolved success
   * banner (PRD 7.4 step 5 / AC-WI3).
   */
  onWhatIfCommitted?: (result: WhatIfCommitResult) => void;
  /** Heatmap-mode only: called when the burnout panel wants to navigate to calendar */
  onNavigateToCalendarWithWhatIf?: (scenario: WhatIfScenario) => void;
  /** Heatmap-mode only: a commit result lifted from the calendar view — seeds the resolved banner */
  initialResolvedCommit?: WhatIfCommitResult | null;
  /** Heatmap-mode only: called once the initialResolvedCommit has been applied */
  onResolvedCommitConsumed?: () => void;
  /** Plan preview scenario from Open Demand — shows ghost preview bookings on the timeline */
  planPreviewScenario?: PlanPreviewScenario | null;
  /** Called when the user dismisses the plan preview banner */
  onPlanPreviewClose?: () => void;
  /** One-shot view + date range applied when arriving from the Resource Allocation report */
  plannerNavContext?: PlannerNavContext | null;
  /** Called once plannerNavContext has been applied so it doesn't re-apply */
  onPlannerNavContextConsumed?: () => void;
  /** Navigates to the Integrations settings tab — used by the no-company empty state */
  onNavigateToIntegrations?: () => void;
}

export function ResourceCalendar({
  initialHeatmapMode = false,
  initialProjectFilter,
  whatIfScenario,
  onWhatIfComplete,
  onWhatIfCommitted,
  onNavigateToCalendarWithWhatIf,
  initialResolvedCommit,
  onResolvedCommitConsumed,
  planPreviewScenario,
  onPlanPreviewClose,
  plannerNavContext,
  onPlannerNavContextConsumed,
  onNavigateToIntegrations,
}: ResourceCalendarProps) {
  const { calendarView, setCalendarView, timelineGroupingMode, setTimelineGroupingMode, selectedDate, setSelectedDate, isLoading: isContextLoading, isGroupsLoading, refreshResourcesAndGroups, refreshProjects, demoBookingTypeUsages, removeBookingsFromState, reloadAutoAdjustGroups, invalidateRecurrenceSettings } = useScheduler();
  const { resources, bookings: allBookings, clients, projectDetails, costCentres, schedulerGroups, settings, addBooking, createBatchBookings, updateBooking, deleteBooking, deleteBookingsBatch, updateBookingsBatch, updateSeriesFuture, currentMode } = useScreenData('calendar');
  const { applyFilters, setFilterStats, hasActiveFilters, filters, hasActiveAdvancedFilters, advancedFilters, showFilteredBookings, updateFilter } = useFilters();
  const { hasCompanyAccess, isLoading: isCompanyLoading, currentCompany } = useCompany();
  const { user } = useUser();
  const { canCreateBooking, canEditBooking, canDeleteBooking, canOverrideReadOnly, canViewIntegrations, canManageIntegrations } = usePermissions();
  const userCanCreateBooking = canCreateBooking();
  const userCanEditBooking = canEditBooking();
  const userCanDeleteBooking = canDeleteBooking();
  const userCanOverrideReadOnly = canOverrideReadOnly();
  // Preview mode is read-only: while previewing a proposed plan on the calendar,
  // real bookings must not be created, edited, moved, or resized — only the
  // dashed preview ghosts are shown, and the plan is applied from the plan
  // drawer (Accept plan). See handlers below and the timeline permission props.
  const isPreviewMode = !!planPreviewScenario;
  const blockIfPreview = (): boolean => {
    if (!isPreviewMode) return false;
    sonnerToast.info('Preview mode', {
      description: 'Exit preview to add, edit, or move bookings.',
    });
    return true;
  };
  const [isInitialized, setIsInitialized] = useState(false);

  // Track whether the first context data load has completed so the in-calendar
  // overlay is suppressed while the full-page TimelineLoadingOverlay is active.
  const hasCompletedFirstLoad = useRef(false);
  if (!isContextLoading && !isCompanyLoading && currentMode !== 'demo') {
    hasCompletedFirstLoad.current = true;
  }
  const canLoadCalendarRanges = !isContextLoading || hasCompletedFirstLoad.current;

  // ── Booking Type Usage permissions (for restricting timeline interactions) ──
  const [bookingTypeUsages, setBookingTypeUsages] = useState<BookingTypeUsageDto[] | null>(null);
  useEffect(() => {
    if (currentMode === 'demo') {
      setBookingTypeUsages(demoBookingTypeUsages);
      return;
    }
    let cancelled = false;
    getCurrentResourceBookingTypeUsages()
      .then(usages => { if (!cancelled) setBookingTypeUsages(usages); })
      .catch(() => { if (!cancelled) setBookingTypeUsages([]); });
    return () => { cancelled = true; };
  }, [currentMode, demoBookingTypeUsages]);

  // Pre-apply project filter when navigating here from Project Staffing
  useEffect(() => {
    if (initialProjectFilter) {
      updateFilter('projects', [initialProjectFilter]);
    }
    // Only run once on mount — intentionally omitting updateFilter from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local state for pending recurring bookings (optimistic UI, no API calls)
  // Declared early because it's used in the bookings useMemo below
  const [pendingRecurringBookings, setPendingRecurringBookings] = useState<Booking[]>([]);

  // Loading overlay state for timeline during add/update booking
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  // A delete is waited on behind the same overlay as a save, so the timeline is never
  // interactive while the booking (or every piece of a split one) is still being removed.
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);

  // Re-fetch resources, scheduler groups, and projects when the Planner tab is
  // activated (i.e. this component mounts), so company access changes and the
  // latest project custom field values are reflected immediately when the user
  // navigates back to the Planner. Skipped while the context's own load is still
  // running (app start-up and company switches), where it would only repeat the
  // requests fetchData is already making.
  useEffect(() => {
    if (isContextLoading) return;
    refreshResourcesAndGroups();
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  // Bumped when a hand-off asks for fresh bookings (see plannerNavContext
  // below) so the timeline never renders the range it happens to hold.
  const [bookingRefreshKey, setBookingRefreshKey] = useState(0);

  const {
    bookings: dateRangeBookings,
    refetchLiveBookings,
    removeBookingFromCache,
    isLoading: isLoadingBookings,
    dateRange,
  } = useDateRangeBookings({
    calendarView,
    selectedDate,
    allBookings,
    mode: currentMode,
    enabled: canLoadCalendarRanges,
    refreshKey: bookingRefreshKey,
  });

  // Merge real bookings with pending recurring bookings for display
  const bookings = useMemo(() => [
    ...dateRangeBookings,
    ...pendingRecurringBookings,
  ], [dateRangeBookings, pendingRecurringBookings]);

  const { isPaintingEvents, handleEventsRendered } = useEventsPaintLatch(isLoadingBookings);

  // ── Absences: fetch from backend when in live mode, fall back to mock ──
  const [absences, setAbsences] = useState<Absence[]>(mockAbsences);
  useEffect(() => {
    if (currentMode === 'demo') {
      setAbsences(mockAbsences);
      return;
    }
    if (!canLoadCalendarRanges || isCompanyLoading || !currentCompany?.id) return;

    let cancelled = false;
    const startIso = dateRange.startDate.toISOString();
    const endIso = dateRange.endDate.toISOString();
    absencesApi.getByDateRange(startIso, endIso, currentCompany?.id)
      .then(data => { if (!cancelled) setAbsences(data); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [currentMode, dateRange.startDate, dateRange.endDate, currentCompany?.id, isCompanyLoading, canLoadCalendarRanges]);

  const [zoomLevel, setZoomLevel] = useState(() => defaultZoomLevel(calendarView));
  // Reset the zoom when the view changes, during render rather than in an
  // effect: an effect would let one render reach the timeline with the previous
  // view's zoom, mounting Bryntum on that zoom's view preset.  Swapping the
  // preset on the live instance afterwards keeps the axis the discarded preset
  // stretched to the viewport, which surfaced as an extra day column (next
  // Monday) after switching Day → Week.
  const previousCalendarViewRef = useRef(calendarView);
  if (previousCalendarViewRef.current !== calendarView) {
    previousCalendarViewRef.current = calendarView;
    setZoomLevel(defaultZoomLevel(calendarView));
  }
  const [isFilterTrayOpen, setIsFilterTrayOpen] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingModalMode, setBookingModalMode] = useState<'create' | 'edit'>('create');
  const [editingBooking, setEditingBooking] = useState<Booking | undefined>(undefined);
  const [preselectedResource, setPreselectedResource] = useState<Resource | undefined>(undefined);
    const [preselectedDate, setPreselectedDate] = useState<Date | undefined>(undefined);
    const [preselectedEndDate, setPreselectedEndDate] = useState<Date | undefined>(undefined);
    const [preselectedStartTime, setPreselectedStartTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
    const [preselectedEndTime, setPreselectedEndTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
  const [preselectedBookingData, setPreselectedBookingData] = useState<Partial<Booking> | undefined>(undefined);
  
  // Quick Add Booking modal state
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [quickAddPreselectedDate, setQuickAddPreselectedDate] = useState<Date | undefined>(undefined);
  const [quickAddPreselectedResource, setQuickAddPreselectedResource] = useState<Resource | undefined>(undefined);
  const [quickAddDefaultStartTime, setQuickAddDefaultStartTime] = useState<string>('09:00');
  const [quickAddDefaultEndTime, setQuickAddDefaultEndTime] = useState<string>('17:00');
  
  // Copied booking state for paste functionality
  const [copiedBooking, setCopiedBooking] = useState<Booking | null>(null);
  
  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  // The bookings of a Ctrl/Cmd-click multi-selection awaiting bulk-delete confirmation.
  const [bookingsToDelete, setBookingsToDelete] = useState<Booking[]>([]);
  // Ids of recurring occurrences currently being deleted in the background via
  // "delete all future". While an id is in this set its event is rendered
  // greyed-out and locked: it cannot be edited, right-clicked, or dragged.
  const [lockedBookingIds, setLockedBookingIds] = useState<Set<string>>(new Set());
  const isBookingLocked = useCallback(
    (id: string | number) => lockedBookingIds.has(String(id)),
    [lockedBookingIds]
  );
  
  // Edit confirmation dialog state for recurring bookings
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState<Booking | null>(null);
  const [showDemandEditConfirm, setShowDemandEditConfirm] = useState(false);
  const [pendingDemandBookingUpdate, setPendingDemandBookingUpdate] = useState<{
    id: string | number;
    originalBooking: Booking;
    update: Partial<Booking>;
    detailScope: 'this' | 'demand-all';
  } | null>(null);
  // Tracks whether the user chose "this booking" or "all future bookings" in the edit dialog
  const [editScope, setEditScope] = useState<'this' | 'all-future' | 'demand-all' | null>(null);
  // Tracks the specific resource row that was clicked when opening the edit modal
  const [editContextResourceId, setEditContextResourceId] = useState<string | undefined>(undefined);

  // Move booking dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [bookingToMove, setBookingToMove] = useState<Booking | null>(null);
  const [bookingToMovePieces, setBookingToMovePieces] = useState<Booking[]>([]);
  const [showDemandMoveConfirm, setShowDemandMoveConfirm] = useState(false);
  const [demandMoveScope, setDemandMoveScope] = useState<'this' | 'all' | null>(null);

  // Resolve conflict — batch assignment dialog state
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [bookingsToResolve, setBookingsToResolve] = useState<Booking[]>([]);

  
  // Timezone state - initialize based on whether timezones are enabled
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    settings.enableTimezones ? (settings.defaultTimezone || 'Europe/London') : 'Europe/London'
  );
  
  // Keep the timeline timezone in sync with the system default timezone.
  // When timezone feature is disabled, always use Europe/London so no offset is applied.
  // When enabled, the timeline defaults to the system default configured in Settings.
  // The planner dropdown selection is intentionally session-only: it is not persisted
  // and does not change the system default, so a full page refresh reverts to the
  // system default timezone.
  useEffect(() => {
    if (!settings.enableTimezones) {
      setSelectedTimezone('Europe/London');
      return;
    }
    setSelectedTimezone(settings.defaultTimezone || 'Europe/London');
  }, [settings.defaultTimezone, settings.enableTimezones]);
  
  // Update quick add default times from business hours settings
  useEffect(() => {
    const formatTime = (hour: number) => {
      return `${String(hour).padStart(2, '0')}:00`;
    };
    
    if (settings.businessHoursStart !== undefined) {
      setQuickAddDefaultStartTime(formatTime(settings.businessHoursStart));
    }
    if (settings.businessHoursEnd !== undefined) {
      setQuickAddDefaultEndTime(formatTime(settings.businessHoursEnd));
    }
  }, [settings.businessHoursStart, settings.businessHoursEnd]);
  
  
  // Date picker popover state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  // The month the picker displays. Held here so the month/year dropdowns move the
  // grid, and reset to the date in view whenever the popover reopens.
  const [datePickerMonth, setDatePickerMonth] = useState<Date>(selectedDate);

  // The window the picker's month/year dropdowns can navigate between.
  const datePickerNavMonths = useMemo(() => getNavMonths(selectedDate), [selectedDate]);
  
  // Timeline grouping mode ("view") is held in SchedulerContext so the selection
  // persists across the planner and heatmap screens (each mounts its own
  // ResourceCalendar). Initialisation and settings-default syncing live there.
  const groupingMode = timelineGroupingMode;
  const setGroupingMode = setTimelineGroupingMode;

  // Track whether user has manually changed the grouping to skip initial render
  const [hasUserChangedGrouping, setHasUserChangedGrouping] = useState(false);

  // Dynamically measure the Bryntum column header height so the grouping Select
  // can be perfectly vertically centred regardless of theme or time-axis row count.
  const [groupingSelectTop, setGroupingSelectTop] = useState<number>(14);
  useEffect(() => {
    const measure = () => {
      // The left-column header cell spans the full height of the time axis header
      const headerCell = document.querySelector('.b-grid-header-container .b-grid-cell');
      if (headerCell) {
        const h = (headerCell as HTMLElement).offsetHeight;
        if (h > 0) setGroupingSelectTop(Math.round((h - 32) / 2) + 65);
      }
    };
    // Try immediately, then retry as Bryntum renders asynchronously
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    const timer = setTimeout(measure, 500);
    const timer2 = setTimeout(measure, 1500);
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
      clearTimeout(timer2);
      observer.disconnect();
    };
  }, []);

  // Refetch bookings when grouping mode changes so the new view has fresh data
  useEffect(() => {
    if (hasUserChangedGrouping && currentMode === 'live') {
      refetchLiveBookings();
    }
  }, [groupingMode, hasUserChangedGrouping, currentMode, refetchLiveBookings]);

  // Heatmap mode state
  const [isHeatmapMode, setIsHeatmapMode] = useState(initialHeatmapMode);


  // ── What-if mode ─────────────────────────────────────────────────────────
  // When a scenario arrives from the heatmap panel, navigate the calendar to
  // the target week and switch to week view so both booking rows are visible.
  useEffect(() => {
    if (whatIfScenario) {
      setSelectedDate(whatIfScenario.targetWeekStart);
      setCalendarView('week');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario]);

  // ── Planner nav context ───────────────────────────────────────────────────
  // Arriving from the Resource Allocation report's "View in Calendar Planner"
  // link: adopt the report's period as the planner view and jump to the range
  // start, then clear the context so later visits keep default behaviour.
  // `refreshBookings` marks a hand-off that follows a change made on the other
  // screen (a committed conflict resolution), so the range is re-read rather
  // than served from what the hook already holds for it.
  useEffect(() => {
    if (!plannerNavContext) return;
    setCalendarView(plannerNavContext.view);
    setSelectedDate(plannerNavContext.startDate);
    if (plannerNavContext.refreshBookings) {
      setBookingRefreshKey((k) => k + 1);
    }
    onPlannerNavContextConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerNavContext]);

  // ── Capacity-aware rebalance plan (shared by preview + commit) ────────────
  // Resolve where each surplus booking would actually move: fill the suggested
  // candidate up to ≤100%, spill overflow to the next free resource (same
  // role/department first), and flag any booking that fits nowhere. Computing
  // this once means the ghost preview and the committed reassignment always
  // agree — the preview never shows a resource that isn't actually free.
  // A `daySlice` marks an assignment that only covers one day of a multi-day
  // booking that was split across resources (see resolve-conflict-distribution).
  type PlanDaySlice = NonNullable<
    NonNullable<WhatIfScenario['bookingAssignments']>[number]['daySlice']
  >;
  type PlanAssignment = { bookingId: string; resourceId: string; daySlice?: PlanDaySlice };
  const rebalancePlan = useMemo(() => {
    if (!whatIfScenario || whatIfScenario.type !== 'rebalance') return null;
    const bookingIds = whatIfScenario.atRiskBookingIds ?? [];
    const targetResourceId = whatIfScenario.candidateResourceId;
    if (bookingIds.length === 0 || !targetResourceId) return null;

    const preferredId = String(targetResourceId);

    // Prefer the capacity-aware distribution computed upstream (single resource
    // when one can absorb everything, otherwise spread). This is the single
    // source of truth shared with the "View suggestions" overlay, so preview,
    // commit and the overlay always target the same resource(s).
    const planned = whatIfScenario.bookingAssignments;
    if (planned && planned.length > 0) {
      const assignments: PlanAssignment[] = planned.map((a) => ({
        bookingId: a.bookingId,
        resourceId: a.candidateResourceId,
        daySlice: a.daySlice,
      }));
      const placed = new Set(assignments.map((a) => a.bookingId));
      const blockedBookings = bookingIds
        .filter((id) => !placed.has(String(id)))
        .map((id) => bookings.find((b) => String(b.id) === String(id)))
        .filter((b): b is Booking => !!b);
      return { preferredId, assignments, blockedBookings, plannedAssignments: planned };
    }

    const originalId = String(whatIfScenario.atRiskResourceId ?? '');
    const preferredResource = resources.find((r) => String(r.id) === preferredId);
    const fallbackIds = resources
      .filter(
        (r) =>
          r.id != null &&
          String(r.id) !== 'unallocated' &&
          String(r.id) !== preferredId &&
          String(r.id) !== originalId,
      )
      .sort((a, b) => {
        const aMatch =
          preferredResource &&
          (a.role === preferredResource.role || a.department === preferredResource.department)
            ? 0
            : 1;
        const bMatch =
          preferredResource &&
          (b.role === preferredResource.role || b.department === preferredResource.department)
            ? 0
            : 1;
        if (aMatch !== bMatch) return aMatch - bMatch;
        return String(a.name).localeCompare(String(b.name));
      })
      .map((r) => String(r.id));

    const { assignments: capacityPlan, blockedBookings } = planCapacityAwareReassignments(
      bookingIds,
      [preferredId, ...fallbackIds],
      bookings,
    );
    const assignments: PlanAssignment[] = capacityPlan.map((a) => ({
      bookingId: a.bookingId,
      resourceId: a.resourceId,
    }));
    return { preferredId, assignments, blockedBookings, plannedAssignments: null };
  }, [whatIfScenario, resources, bookings]);

  // A delayed booking sits outside the week the calendar has navigated to, so
  // in-place changes are resolved against every known booking rather than the
  // date-range slice currently rendered.
  const bookingsById = useMemo(() => {
    const byId = new Map<string, Booking>();
    [...allBookings, ...bookings].forEach((b) => byId.set(String(b.id), b));
    return byId;
  }, [allBookings, bookings]);

  // Pieces read back after the form opened widen the span it holds, so a split booking is
  // still edited as the whole booking it is even when part of it was outside the loaded
  // range at the moment the form opened.
  useEffect(() => {
    if (!isBookingModalOpen || bookingModalMode !== 'edit' || !editingBooking) return;

    const piece = bookingsById.get(String(editingBooking.id));
    if (!piece?.autoAdjustGroupId) return;

    // Only ever widens: the pieces held cover the loaded range alone, so the days this
    // booking made up in a later month are missing from them and the span they cover stops
    // short of the booking's end.
    const span = widenedAutoAdjustGroupSpan(
      editingBooking,
      autoAdjustGroupPieces(piece, [...bookingsById.values()]),
    );
    if (!span) return;

    setEditingBooking(prev =>
      prev ? { ...prev, startTime: span.startTime, endTime: span.endTime } : prev);
  }, [isBookingModalOpen, bookingModalMode, editingBooking, bookingsById]);

  // A split booking is moved as a whole: the move dialog offers, and validates, the span
  // its pieces cover together rather than the span of the piece it was opened on.
  const bookingToMoveSpan = useMemo(() => {
    if (!bookingToMove || !isAutoAdjustGroupPiece(bookingToMove)) return null;
    // The pieces read from the API when the dialog opened are held here as well as in the
    // timeline's own bookings, which cover the loaded range alone.
    const pieces = autoAdjustGroupPieces(
      bookingToMove,
      withReadAutoAdjustGroupRows(
        [...bookingsById.values()],
        bookingToMovePieces,
        autoAdjustGroupIds([bookingToMove]),
      ),
    );
    return pieces.length > 1 ? autoAdjustGroupSpan(pieces) : null;
  }, [bookingToMove, bookingToMovePieces, bookingsById]);

  /** Whether the move is one the server re-plans around non-working days. */
  const bookingToMoveSkipsNonWorkingDays = useMemo(() => (
    bookingToMove != null && autoAdjustsAroundNonWorkingDays(settings.bookingTypes.find(
      type => String(type.id) === String(bookingToMove.bookingTypeId),
    ))
  ), [bookingToMove, settings.bookingTypes]);

  // In-place booking changes carried by the scenario: "reduce hours" shortens
  // the booking, "delay start" slides it to the chosen slot. Both keep the
  // booking on its current resource, so they are merged per booking and
  // applied as plain field updates (never reassignments).
  // A reduced multi-day booking keeps its first day and gets a new booking for
  // each remaining day (see buildReductionMutations).
  const reductionMutations = useMemo<ReductionMutations>(() => {
    if (!whatIfScenario || whatIfScenario.type !== 'rebalance' || !whatIfScenario.bookingReductions?.length) {
      return { updates: [], creates: [], details: [] };
    }
    return buildReductionMutations(whatIfScenario.bookingReductions, Array.from(bookingsById.values()));
  }, [whatIfScenario, bookingsById]);

  const inPlaceBookingUpdates = useMemo<BookingFieldUpdate[]>(() => {
    if (!whatIfScenario || whatIfScenario.type !== 'rebalance') return [];
    const source = Array.from(bookingsById.values());
    const merged = new Map<string, Partial<Booking>>();
    [
      ...reductionMutations.updates,
      ...buildDelayMutations(whatIfScenario.bookingDelays ?? [], source),
    ].forEach((u) => {
      merged.set(u.bookingId, { ...(merged.get(u.bookingId) ?? {}), ...u.changes });
    });
    return Array.from(merged, ([bookingId, changes]) => ({ bookingId, changes }));
  }, [whatIfScenario, bookingsById, reductionMutations]);

  const atRiskBookingsAfterChange = useCallback((riskBookings: Booking[], movedIds: Set<string>) => {
    const changedById = new Map(
      inPlaceBookingUpdates.map((u) => [String(u.bookingId), u.changes]),
    );
    if (movedIds.size === 0 && changedById.size === 0) return riskBookings;
    return [
      ...riskBookings.map((b) => {
        const id = String(b.id);
        if (movedIds.has(id)) return { ...b, resourceId: '__whatif_moved__' };
        const changes = changedById.get(id);
        return changes ? { ...b, ...changes } : b;
      }),
      ...reductionMutations.creates,
    ];
  }, [inPlaceBookingUpdates, reductionMutations]);

  // Totals for the reduce-hours preview banner: booked hours before/after,
  // the hours asked for, and the at-risk resource's peak utilisation.
  const reductionSummary = useMemo<WhatIfReductionSummaryData | null>(() => {
    const { details } = reductionMutations;
    if (!whatIfScenario || details.length === 0) return null;
    const beforeHours = details.reduce((sum, d) => sum + d.beforeHours, 0);
    const afterHours = details.reduce((sum, d) => sum + d.afterHours, 0);
    const targetHours = details.reduce((sum, d) => sum + d.targetHours, 0);
    // The Reduce slider is a per-week figure, so also total the target week.
    const weekStart = startOfWeek(new Date(whatIfScenario.targetWeekStart), { weekStartsOn: 1 });
    const weekEndKey = format(addDays(weekStart, 7), 'yyyy-MM-dd');
    const weekStartKey = format(weekStart, 'yyyy-MM-dd');
    let weekBeforeHours = 0;
    let weekAfterHours = 0;
    let weekTargetHours = 0;
    details.forEach((d) => {
      const inWeek = d.days.filter((day) => day.dateKey >= weekStartKey && day.dateKey < weekEndKey);
      const before = inWeek.reduce((sum, day) => sum + day.beforeHours, 0);
      weekBeforeHours += before;
      weekAfterHours += inWeek.reduce((sum, day) => sum + day.afterHours, 0);
      if (d.beforeHours > 0) weekTargetHours += (before * d.targetHours) / d.beforeHours;
    });
    const week = weekBeforeHours > 0
      ? {
          label: `W${format(weekStart, 'I')}`,
          beforeHours: weekBeforeHours,
          afterHours: weekAfterHours,
          targetHours: weekTargetHours,
        }
      : null;
    const atRiskResource = resources.find((r) => String(r.id) === String(whatIfScenario.atRiskResourceId ?? ''));
    let beforePct: number | null = null;
    let afterPct: number | null = null;
    if (atRiskResource) {
      const profile = settings.globalWorkingProfile ?? FALLBACK_WORKING_PROFILE;
      const riskBookings = Array.from(bookingsById.values());
      beforePct = Math.round(computeBurnoutRisk(atRiskResource, riskBookings, profile, new Date(), 8).peakUtilisation);
      afterPct = Math.round(computeBurnoutRisk(
        atRiskResource, atRiskBookingsAfterChange(riskBookings, new Set()), profile, new Date(), 8,
      ).peakUtilisation);
    }
    return { details, beforeHours, afterHours, targetHours, week, beforePct, afterPct };
  }, [whatIfScenario, reductionMutations, resources, settings.globalWorkingProfile, bookingsById, atRiskBookingsAfterChange]);

  // Scenario handed to the timeline/banner with ghost tiles placed on the
  // resources that will actually receive each booking (not the over-utilised
  // suggested candidate).
  const effectiveWhatIfScenario = useMemo(() => {
    if (!whatIfScenario || whatIfScenario.type !== 'rebalance') return whatIfScenario;

    // Reduce / delay preview: the booking stays on its own row, ghosted at its
    // proposed new size/dates so the change is visible before committing.
    if (inPlaceBookingUpdates.length > 0) {
      const reductionById = new Map(reductionMutations.details.map((d) => [d.bookingId, d]));
      const ghostAssignments = inPlaceBookingUpdates.flatMap((u) => {
        const bk = bookingsById.get(String(u.bookingId));
        if (!bk) return [];
        const next = { ...bk, ...u.changes };
        const reduction = reductionById.get(String(bk.id));
        // A reduced multi-day booking is one ghost spanning all of its days;
        // the tile label and tooltip carry the per-day hours.
        const lastDay = reduction?.days[reduction.days.length - 1];
        const endDate = reduction && lastDay && reduction.days.length > 1
          ? new Date(`${lastDay.dateKey}T${formatMinuteOfDay(lastDay.afterEndMin)}:00`)
          : new Date(next.endTime);
        return [{
          bookingId: String(bk.id),
          resourceId: String(bk.resourceId ?? ''),
          startDate: new Date(next.startTime),
          endDate,
          reduction,
        }];
      });
      return ghostAssignments.length > 0
        ? { ...whatIfScenario, ghostAssignments }
        : whatIfScenario;
    }

    if (!rebalancePlan) return whatIfScenario;
    const { assignments } = rebalancePlan;
    if (assignments.length === 0) return whatIfScenario;

    const ghostAssignments = assignments.map((a) => {
      // A split day carries its own start/end — draw the ghost on that single
      // day. Otherwise size it to the whole source booking.
      if (a.daySlice) {
        return {
          bookingId: a.bookingId,
          resourceId: a.resourceId,
          startDate: new Date(a.daySlice.startTime),
          endDate: new Date(a.daySlice.endTime),
        };
      }
      const bk = bookings.find((b) => String(b.id) === a.bookingId);
      return {
        bookingId: a.bookingId,
        resourceId: a.resourceId,
        startDate: bk ? new Date(bk.startTime) : new Date(whatIfScenario.targetWeekStart),
        endDate: bk ? new Date(bk.endTime) : new Date(whatIfScenario.targetWeekStart),
      };
    });

    const targetIds = Array.from(new Set(assignments.map((a) => a.resourceId)));
    const primaryId = targetIds[0];
    const primaryName =
      resources.find((r) => String(r.id) === String(primaryId))?.name ??
      whatIfScenario.candidateResourceName;
    const candidateResourceName =
      targetIds.length === 1 ? primaryName : `${targetIds.length} resources`;

    return {
      ...whatIfScenario,
      candidateResourceId: primaryId,
      candidateResourceName,
      ghostAssignments,
    };
  }, [whatIfScenario, rebalancePlan, inPlaceBookingUpdates, reductionMutations, bookingsById, bookings, resources]);

  const handleWhatIfCommit = useCallback(async () => {
    if (!whatIfScenario) return;

    if (whatIfScenario.type === 'rebalance') {
      // ── Persist the move: reassign each surplus booking to the resource the
      // capacity-aware plan chose (same plan that drove the ghost preview). ──
      const weekLabel = whatIfScenario.affectedWeekStarts?.[0]
        ? `W${Math.ceil((whatIfScenario.affectedWeekStarts[0].getTime() - new Date(whatIfScenario.affectedWeekStarts[0].getFullYear(), 0, 1).getTime()) / 604800000)}`
        : '';
      const project = whatIfScenario.projectName ?? 'Project';

      // Track what actually moved + where, so the resolved success banner can
      // report the before/after utilisation for the at-risk resource.
      let committedCandidateName = whatIfScenario.candidateResourceName ?? 'resource';
      let movedBookingIds: string[] = [];

      // Reduce / delay: persist the field changes on the bookings themselves.
      if (inPlaceBookingUpdates.length > 0) {
        await Promise.all([
          ...inPlaceBookingUpdates.map((u) => updateBooking(u.bookingId, u.changes)),
          ...reductionMutations.creates.map((c) => addBooking(c)),
        ]);
        const count = inPlaceBookingUpdates.length;
        const plural = count > 1 ? 's' : '';
        const delayed = (whatIfScenario.bookingDelays?.length ?? 0) > 0;
        sonnerToast.success(
          delayed
            ? `${count} booking${plural} delayed${weekLabel ? ` · ${weekLabel}` : ''} confirmed`
            : `${count} booking${plural} reduced${weekLabel ? ` · ${weekLabel}` : ''} confirmed`,
          { duration: 4000 },
        );
      }

      if (rebalancePlan) {
        const { preferredId, assignments, blockedBookings } = rebalancePlan;
        const nameOf = (id: string) =>
          resources.find((r) => String(r.id) === String(id))?.name ?? 'another resource';
        const candidateName = whatIfScenario.candidateResourceName ?? nameOf(preferredId);
        committedCandidateName = candidateName;
        movedBookingIds = assignments.map((a) => String(a.bookingId));

        if (assignments.length > 0) {
          // Split days become new per-day bookings on their target resource and
          // the original multi-day booking is deleted; whole moves stay a simple
          // resource update. Same helper the report commit uses.
          const commitAssignments =
            rebalancePlan.plannedAssignments ??
            assignments.map((a) => ({
              bookingId: a.bookingId,
              candidateResourceId: a.resourceId,
              candidateResourceName: nameOf(a.resourceId),
              daySlice: a.daySlice,
            }));
          const { updates, creates, deletes } = buildReassignmentMutations(
            commitAssignments,
            bookings,
            () => crypto.randomUUID(),
          );
          await Promise.all([
            ...updates.map((u) =>
              updateBooking(
                u.bookingId,
                reassignedResourceFields(
                  bookings.find((b) => String(b.id) === String(u.bookingId)),
                  u.resourceId,
                ),
              ),
            ),
            ...creates.map((c) => addBooking(c)),
          ]);
          await Promise.all(deletes.map((d) => deleteBooking(d)));
        }

        if (blockedBookings.length > 0) {
          const plural = blockedBookings.length > 1;
          sonnerToast.warning(
            `${blockedBookings.length} booking${plural ? 's' : ''} not moved — no suitable resource found`,
            { duration: 6000 },
          );
        }

        if (assignments.length === 0) {
          // Nowhere had room — stay on the scenario so the user can adjust.
          return;
        }

        // Distinct destination resources, in placement order — drives both the
        // success toast and the banner name so a multi-resource split reads as
        // "2 bookings redistributed to James and Priya" rather than a single
        // "moved to X".
        const targetIds = Array.from(new Set(assignments.map((a) => a.resourceId)));
        const targetNames = targetIds.map(nameOf);
        if (targetIds.length > 1) {
          const list =
            targetNames.length === 2
              ? `${targetNames[0]} and ${targetNames[1]}`
              : `${targetNames.slice(0, -1).join(', ')} and ${targetNames[targetNames.length - 1]}`;
          committedCandidateName = list;
          sonnerToast.success(
            `${assignments.length} bookings redistributed to ${list}${weekLabel ? ` · ${weekLabel}` : ''} confirmed`,
            { duration: 4000 },
          );
        } else {
          committedCandidateName = targetNames[0] ?? candidateName;
          sonnerToast.success(
            `${project} moved to ${committedCandidateName}${weekLabel ? ` · ${weekLabel}` : ''} confirmed`,
            { duration: 4000 },
          );
        }
      } else if (inPlaceBookingUpdates.length === 0) {
        const candidate = whatIfScenario.candidateResourceName ?? 'resource';
        committedCandidateName = candidate;
        movedBookingIds = (whatIfScenario.atRiskBookingIds ?? []).map(String);
        sonnerToast.success(`${project} moved to ${candidate}${weekLabel ? ` · ${weekLabel}` : ''} confirmed`, {
          duration: 4000,
        });
      }

      // ── Resolved success banner (PRD 7.4 step 5 / AC-WI3) ──────────────────
      // Derive the at-risk resource's peak utilisation before and after the
      // moved bookings leave its row, reusing the same computation that drives
      // the burnout panel's UtilBar. This updates resolvedIds/resolvedSwap on
      // the heatmap and renders the "Rebalancing committed" banner.
      const atRiskId = String(whatIfScenario.atRiskResourceId ?? '');
      const atRiskResource = resources.find((r) => String(r.id) === atRiskId);
      if (onWhatIfCommitted && atRiskResource) {
        const profile: WorkingHoursProfile = settings.globalWorkingProfile ?? FALLBACK_WORKING_PROFILE;
        // Utilisation is compared over every known booking so a delay that
        // moves work outside the visible range is still reflected.
        const riskBookings = Array.from(bookingsById.values());
        const beforeRisk = computeBurnoutRisk(atRiskResource, riskBookings, profile, new Date(), 8);
        const afterBookings = atRiskBookingsAfterChange(riskBookings, new Set(movedBookingIds));
        const afterRisk = computeBurnoutRisk(atRiskResource, afterBookings, profile, new Date(), 8);
        const reducedCount = whatIfScenario.bookingReductions?.length ?? 0;
        const delayedCount = whatIfScenario.bookingDelays?.length ?? 0;
        onWhatIfCommitted({
          atRiskResourceId: atRiskId,
          atRiskName: whatIfScenario.atRiskResourceName ?? atRiskResource.name,
          candidateName: committedCandidateName,
          outcomeLabel:
            reducedCount > 0 && delayedCount > 0 ? 'hours reduced and start delayed'
            : reducedCount > 0 ? 'hours reduced'
            : delayedCount > 0 ? 'start delayed'
            : undefined,
          beforePct: Math.round(beforeRisk.peakUtilisation),
          afterPct: Math.round(afterRisk.peakUtilisation),
        });
      }
    } else {
      const name = whatIfScenario.freeResourceName ?? 'Resource';
      sonnerToast.success(`Booking assigned to ${name} · W${whatIfScenario.freeFromWeek ?? ''} confirmed`, {
        duration: 4000,
      });
    }

    onWhatIfComplete?.(true); // true = navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, onWhatIfComplete, onWhatIfCommitted, updateBooking, addBooking, rebalancePlan, inPlaceBookingUpdates, reductionMutations, atRiskBookingsAfterChange, resources, bookings, settings.globalWorkingProfile]);

  const handleWhatIfDiscard = useCallback(() => {
    onWhatIfComplete?.(true); // discard → also navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onWhatIfComplete]);

  // Ref for Bryntum scheduler to revert events on discard
  const schedulerTimelineRef = useRef<BryntumSchedulerTimelineHandle>(null);

  // Ref to the outermost scheduler DOM element — used by BurnoutRiskPanel for DOM injection
  const schedulerDomRef = useRef<HTMLDivElement | null>(null);
  
  // Capacity risk feature flag
  const { whatIfEnabled } = useConflictResolutionSettings();

  // Drag and drop state
  const { cancelDrag } = useBookingDrag();
  const [showDragConfirm, setShowDragConfirm] = useState(false);
  const [showDemandReassignConfirm, setShowDemandReassignConfirm] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState<{
    booking: Booking;
    originalResource: Resource;
    newResource: Resource;
    originalStartTime: Date;
    originalEndTime: Date;
    newStartTime: Date;
    newEndTime: Date;
    isResize?: boolean;
    /** Project view only: set when the drop also moves the booking to another project */
    newProject?: ProjectDetails;
  } | null>(null);

  // Search matches each booking against its resource/project/cost centre, which
  // as linear `find` calls is O(bookings × lookups) on every keystroke.
  const resourcesById = useMemo(
    () => new Map(resources.map(r => [String(r.id), r])),
    [resources],
  );
  const projectDetailsById = useMemo(
    () => new Map(projectDetails.map(p => [String(p.id), p])),
    [projectDetails],
  );
  const costCentresById = useMemo(
    () => new Map(costCentres.map(cc => [String(cc.id), cc])),
    [costCentres],
  );

  // Apply filters to resources and bookings
  const filteredData = useMemo(() => {
    const result = applyFilters(resources, bookings, settings, projectDetails, costCentres, dateRange, mockBankHolidays, absences);
    
    // Apply booking search query filter
    let searchFilteredBookings = result.filteredBookings;
    if (bookingSearchQuery.trim()) {
      const query = bookingSearchQuery.toLowerCase();
      
      searchFilteredBookings = result.filteredBookings.filter(booking =>
        bookingMatchesSearch(booking, query, {
          resourcesById,
          projectDetailsById,
          costCentresById,
        }),
      );
    }
    
    // If showFilteredBookings is true, include all bookings but mark filtered ones
    if (showFilteredBookings) {
      const filteredBookingIds = new Set(searchFilteredBookings.map(b => b.id));
      
      // Add isFiltered flag to all bookings
      const allBookingsWithFlag = bookings.map(booking => ({
        ...booking,
        isFiltered: !filteredBookingIds.has(booking.id)
      }));
      
      return {
        ...result,
        filteredBookings: allBookingsWithFlag
      };
    }
    
    return {
      ...result,
      filteredBookings: searchFilteredBookings
    };
  }, [resources, bookings, applyFilters, settings, projectDetails, costCentres, resourcesById, projectDetailsById, costCentresById, showFilteredBookings, currentMode, bookingSearchQuery, dateRange, absences]);

  // Update filter stats in a separate effect to avoid setState during render
  useEffect(() => {
    setFilterStats(filteredData.stats);
  }, [filteredData.stats, setFilterStats]);

  // ── Vertical resource paging ──────────────────────────────────────────
  // Every filtered resource is handed to the timeline so it can build all of
  // the group rows, and the timeline pages the rows of each group in on its
  // own. resetToken is bumped on every reset so the timeline can tell a reset
  // apart from a page append and only then drop back to a first page per group
  // with the rows scrolled to the top.
  const [resourceWindowResetToken, setResourceWindowResetToken] = useState(0);

  // Resources behind the rows the timeline currently holds. Only these need
  // per-day records computing for them (see resourceTimeRanges below).
  const [loadedResourceIds, setLoadedResourceIds] = useState<Set<string>>(() => new Set());
  const handleLoadedResourceIdsChange = useCallback((resourceIds: string[]) => {
    setLoadedResourceIds(new Set(resourceIds));
  }, []);

  // Any view change — search, filters, grouping, calendar view, heatmap, zoom
  // or the navigated date range — starts the rows from the top again.
  // The resource array itself is deliberately not the reset trigger: it is
  // rebuilt on every booking change, which would collapse the window while the
  // user is scrolled down it.
  const resourceWindowResetKey = useMemo(
    () => JSON.stringify([
      filters,
      advancedFilters,
      bookingSearchQuery,
      groupingMode,
      currentMode,
      calendarView,
      isHeatmapMode,
      zoomLevel,
      dateRange.startDate,
      dateRange.endDate,
    ]),
    [
      filters,
      advancedFilters,
      bookingSearchQuery,
      groupingMode,
      currentMode,
      calendarView,
      isHeatmapMode,
      zoomLevel,
      dateRange.startDate,
      dateRange.endDate,
    ],
  );
  const previousResourceWindowResetKeyRef = useRef(resourceWindowResetKey);
  if (previousResourceWindowResetKeyRef.current !== resourceWindowResetKey) {
    previousResourceWindowResetKeyRef.current = resourceWindowResetKey;
    setResourceWindowResetToken(current => current + 1);
    console.log('[scroll-load] resource rows reset to a first page per group', {
      pageSize: RESOURCE_PAGE_SIZE,
    });
  }

  // Opening the planner (including coming back to it from another page or tab)
  // starts at the first page with the rows scrolled to the top.
  useEffect(() => {
    setResourceWindowResetToken(current => current + 1);
    console.log('[scroll-load] planner opened, resource rows reset to a first page per group', {
      pageSize: RESOURCE_PAGE_SIZE,
    });
  }, []);

  const windowedResources = useMemo(
    () => filteredData.filteredResources.filter(resource => loadedResourceIds.has(String(resource.id))),
    [filteredData.filteredResources, loadedResourceIds],
  );

  // ── Resource Time Ranges ──────────────────────────────────────────────
  // Compute resourceTimeRanges here (absences, bank holidays, non-working
  // weekdays) and pass them down to BryntumSchedulerTimeline as a prop so
  // the timeline component stays presentation-only.
  // Weekends are intentionally excluded — no shading is applied for them.
  const resourceTimeRanges = useMemo(() => {
    const endBuild = perfStart('build resourceTimeRanges');
    const ranges: Array<{
      id: string;
      resourceId: string;
      name: string;
      startDate: Date;
      endDate: Date;
      timeRangeColor?: string;
      cls?: string;
      style?: string;
    }> = [];

    // Only the rows the timeline has paged in are drawn, and a range costs a
    // record per resource per day: over a year-long range the full resource
    // list is hundreds of thousands of records that never reach the DOM.
    const filteredResources = windowedResources;
    const filteredBookings = filteredData.filteredBookings;

    // Wait until resources have been loaded into state before computing
    // ranges.  This prevents premature empty ranges that never get
    // repopulated because the Bryntum store refresh already fired.
    if (filteredResources.length === 0) {
      return ranges;
    }

    const windowedResourceIds = new Set(filteredResources.map(resource => String(resource.id)));
    const windowedResourceById = new Map(filteredResources.map(resource => [String(resource.id), resource]));
    // Days outside a resource's contract are covered by one out-of-contract
    // range, so the per-day shading is not stacked on top of it.
    const isOutsideContract = (resourceId: string | number, date: Date) =>
      !isDayWithinContract(windowedResourceById.get(String(resourceId)), dayKey(date));

    // Project ids per resource, built once: the per-resource-per-day loops
    // below would otherwise rescan every booking in the range.
    const projectIdsByResource = new Map<string, Set<string>>();
    if (groupingMode === 'project-resource') {
      filteredBookings.forEach(booking => {
        if (!booking.resourceId || !booking.projectId) return;
        const key = String(booking.resourceId);
        const projectIds = projectIdsByResource.get(key);
        if (projectIds) {
          projectIds.add(booking.projectId);
        } else {
          projectIdsByResource.set(key, new Set([booking.projectId]));
        }
      });
    }

    const bankHolidays: BankHoliday[] = mockBankHolidays;

    // Get colors from settings
    const absenceColor = settings.absenceColor || '#94a3b8';
    const publicHolidayColor = settings.publicHolidayColor || '#fbbf24';
    const businessHoursStart = settings.businessHoursStart || 9;
    const businessHoursEnd = settings.businessHoursEnd || 17;

    // Process absences (only if showAbsence is enabled)
    if (settings.showAbsence !== false) {
      absences.forEach((absence) => {
        // Skip annual-leave (dashed) and public-holiday (line) absences —
        // they are rendered as event bars instead of background ranges.
        const reasonNorm = absence.reason.toLowerCase().replace(/[-_]/g, ' ');
        if (
          reasonNorm.includes('annual leave') ||
          reasonNorm.includes('public holiday') ||
          reasonNorm.includes('bank holiday')
        ) return;
        if (!windowedResourceIds.has(String(absence.resourceId))) return;
        const absenceStartDate = new Date(absence.startDate);
        const absenceEndDate = new Date(absence.endDate);

        const currentDate = new Date(absenceStartDate);
        while (currentDate <= absenceEndDate) {
          if (isOutsideContract(absence.resourceId, currentDate)) {
            currentDate.setDate(currentDate.getDate() + 1);
            continue;
          }
          let rangeStart: Date;
          let rangeEnd: Date;

          switch (absence.type) {
            case 'full-day':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
              break;
            case 'half-day-am':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(businessHoursStart, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(12, 0, 0, 0);
              break;
            case 'half-day-pm':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(12, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(businessHoursEnd, 0, 0, 0);
              break;
            case 'hourly':
              if (absence.startTime && absence.endTime) {
                const [startHour, startMin] = absence.startTime.split(':').map(Number);
                const [endHour, endMin] = absence.endTime.split(':').map(Number);
                rangeStart = new Date(currentDate);
                rangeStart.setHours(startHour, startMin, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(endHour, endMin, 0, 0);
              } else {
                rangeStart = new Date(currentDate);
                rangeStart.setHours(0, 0, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(23, 59, 59, 999);
              }
              break;
            default:
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
          }

          if (groupingMode === 'project-resource') {
            const projectsForResource = projectIdsByResource.get(String(absence.resourceId)) ?? new Set<string>();
            projectsForResource.forEach(projectId => {
              ranges.push({
                id: `absence-${absence.id}-${projectId}-${currentDate.toISOString().split('T')[0]}`,
                resourceId: `${projectId}-${absence.resourceId}`,
                name: absence.reason || 'Absence',
                startDate: rangeStart,
                endDate: rangeEnd,
                timeRangeColor: absenceColor,
                cls: 'absence-range',
              });
            });
          } else if (groupingMode === 'role-resource') {
            const resource = filteredResources.find(r => String(r.id) === String(absence.resourceId));
            const roleName = resource
              ? getUnallocatedAwareGroupName(resource, resource.role || 'Unassigned')
              : 'Unassigned';
            ranges.push({
              id: `absence-${absence.id}-${roleName}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: `${roleName}-${absence.resourceId}`,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          } else {
            ranges.push({
              id: `absence-${absence.id}-${absence.resourceId}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: absence.resourceId,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
    }

    // Process bank holidays (only if showPublicHolidays is enabled)
    if (settings.showPublicHolidays !== false) {
      bankHolidays.forEach((holiday) => {
        const holidayDate = new Date(holiday.date);
        const rangeStart = new Date(holidayDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(holidayDate);
        rangeEnd.setHours(23, 59, 59, 999);

        if (groupingMode === 'project-resource') {
          const projectResourceCombinations = new Set<string>();
          projectIdsByResource.forEach((projectIds, resourceId) => {
            if (!windowedResourceIds.has(resourceId)) return;
            if (isOutsideContract(resourceId, holidayDate)) return;
            projectIds.forEach(projectId => projectResourceCombinations.add(`${projectId}-${resourceId}`));
          });
          projectResourceCombinations.forEach(combinedId => {
            ranges.push({
              id: `holiday-${holiday.id}-${combinedId}`,
              resourceId: combinedId,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else if (groupingMode === 'role-resource') {
          filteredResources.forEach((resource) => {
            if (isOutsideContract(resource.id, holidayDate)) return;
            const roleName = getUnallocatedAwareGroupName(resource, resource.role || 'Unassigned');
            ranges.push({
              id: `holiday-${holiday.id}-${roleName}-${resource.id}`,
              resourceId: `${roleName}-${resource.id}`,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else {
          filteredResources.forEach((resource) => {
            if (isOutsideContract(resource.id, holidayDate)) return;
            ranges.push({
              id: `holiday-${holiday.id}-${resource.id}`,
              resourceId: resource.id,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        }
      });
    }

    // ── Non-working weekdays ───────────────────────────────────────────

    // Determine visible date range
    let visibleStart: Date;
    let visibleEnd: Date;
    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();

    switch (calendarView) {
      case 'day':
        visibleStart = new Date(validDate);
        visibleStart.setHours(0, 0, 0, 0);
        visibleEnd = new Date(validDate);
        visibleEnd.setHours(23, 59, 59, 999);
        break;
      case 'week':
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
        break;
      case 'month':
        visibleStart = startOfMonth(validDate);
        visibleEnd = endOfMonth(validDate);
        break;
      case 'quarter':
        visibleStart = startOfQuarter(validDate);
        visibleEnd = endOfQuarter(validDate);
        break;
      case 'year':
        visibleStart = startOfYear(validDate);
        visibleEnd = endOfYear(validDate);
        break;
      default:
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
    }

    const isVisibleStartValid = visibleStart instanceof Date && !isNaN(visibleStart.getTime());
    const isVisibleEndValid = visibleEnd instanceof Date && !isNaN(visibleEnd.getTime());
    if (!isVisibleStartValid || !isVisibleEndValid || visibleStart.getTime() >= visibleEnd.getTime()) {
      return ranges;
    }

    const visibleDays = eachDayOfInterval({ start: visibleStart, end: visibleEnd });
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

    // Weekdays of the range grouped by day of week, with their range bounds
    // resolved once.  Walking all 365 days of a yearly range per resource is
    // what makes the coarse presets crawl, and only the day of week decides
    // whether a day is shaded.
    const weekdaysByDow = new Map<number, Array<{ rangeStart: Date; rangeEnd: Date; dayStr: string }>>();
    visibleDays.forEach(day => {
      const dow = day.getDay();
      // Skip weekends — we only shade non-working *weekdays*
      if (dow === 0 || dow === 6) return;
      const rangeStart = new Date(day);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(day);
      rangeEnd.setHours(23, 59, 59, 999);
      const entry = { rangeStart, rangeEnd, dayStr: day.toISOString().split('T')[0] };
      const days = weekdaysByDow.get(dow);
      if (days) {
        days.push(entry);
      } else {
        weekdaysByDow.set(dow, [entry]);
      }
    });

    const nonWorkingDows = (profile: WorkingHoursProfile | undefined): number[] =>
      [1, 2, 3, 4, 5].filter(dow => (profile?.[dayNames[dow]] || 0) === 0);

    const globalNonWorkingDows = nonWorkingDows(settings.globalWorkingProfile);

    // Timeline row ids a resource is drawn on in the current grouping mode.
    const rowIdsFor = (resource: Resource): string[] => {
      if (groupingMode === 'project-resource') {
        const projectIds = projectIdsByResource.get(String(resource.id));
        return projectIds && projectIds.size > 0
          ? [...projectIds].map(projectId => `${projectId}-${resource.id}`)
          : [String(resource.id)];
      }
      if (groupingMode === 'role-resource') {
        return [`${getUnallocatedAwareGroupName(resource, resource.role || 'Unassigned')}-${resource.id}`];
      }
      return [String(resource.id)];
    };

    // Outside the contract: at most one range before its start and one after
    // its end per row, clamped to the visible range.
    filteredResources.forEach((resource) => {
      const contractRanges = outOfContractRanges(resource, visibleStart, visibleEnd);
      if (contractRanges.length === 0) return;
      const rowIds = rowIdsFor(resource);
      contractRanges.forEach(range => {
        rowIds.forEach(rowId => {
          ranges.push({
            id: `out-of-contract-${range.kind}-${rowId}`,
            resourceId: rowId,
            name: range.label,
            startDate: range.startDate,
            endDate: range.endDate,
            cls: `out-of-contract-range out-of-contract-${range.kind}`,
          });
        });
      });
    });

    // Non-working weekdays: days where resource has 0 working hours.
    filteredResources.forEach((resource) => {
      const dows = resource.useCustomWorkingProfile && resource.customWorkingProfile
        ? nonWorkingDows(resource.customWorkingProfile)
        : globalNonWorkingDows;
      if (dows.length === 0) return;

      const projectsForResource = groupingMode === 'project-resource'
        ? projectIdsByResource.get(String(resource.id))
        : undefined;
      const roleName = groupingMode === 'role-resource'
        ? getUnallocatedAwareGroupName(resource, resource.role || 'Unassigned')
        : '';

      dows.forEach(dow => {
        (weekdaysByDow.get(dow) ?? []).forEach(({ rangeStart, rangeEnd, dayStr }) => {
          if (!isDayWithinContract(resource, dayKey(rangeStart))) return;
          if (groupingMode === 'project-resource') {
            if (!projectsForResource || projectsForResource.size === 0) {
              ranges.push({
                id: `non-working-${resource.id}-${dayStr}`,
                resourceId: String(resource.id),
                name: '',
                startDate: rangeStart,
                endDate: rangeEnd,
                cls: 'custom-style',
              });
            } else {
              projectsForResource.forEach(projectId => {
                ranges.push({
                  id: `non-working-${resource.id}-${projectId}-${dayStr}`,
                  resourceId: `${projectId}-${resource.id}`,
                  name: '',
                  startDate: rangeStart,
                  endDate: rangeEnd,
                  cls: 'custom-style',
                });
              });
            }
          } else if (groupingMode === 'role-resource') {
            ranges.push({
              id: `non-working-${resource.id}-${roleName}-${dayStr}`,
              resourceId: `${roleName}-${resource.id}`,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          } else {
            ranges.push({
              id: `non-working-${resource.id}-${dayStr}`,
              resourceId: resource.id,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          }
        });
      });
    });

    endBuild({ ranges: ranges.length });
    return ranges;
  }, [
    windowedResources, filteredData.filteredBookings, settings.absenceColor,
    settings.publicHolidayColor,
    settings.showAbsence, settings.showPublicHolidays, settings.globalWorkingProfile,
    groupingMode, selectedDate, calendarView, absences,
  ]);

  // ── Time Ranges (global, non-resource-specific) ──────────────────────
  // Non-business hours in Day view are rendered as global timeRanges
  // (column-spanning) rather than per-resource resourceTimeRanges.
  const timeRanges = useMemo(() => {
    const ranges: Array<{
      id: string;
      name: string;
      startDate: Date;
      endDate: Date;
      cls?: string;
    }> = [];

    if (calendarView !== 'day') return ranges;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;
    const visStartRaw = settings.visibleHoursStart ?? 7;
    const visEndRaw = settings.visibleHoursEnd ?? 19;

    const toHM = (v: number) => ({ h: Math.floor(v), m: Math.round((v - Math.floor(v)) * 60) });
    const bizStartHM = toHM(bizStartRaw);
    const bizEndHM = toHM(bizEndRaw);
    const visStartHM = toHM(visStartRaw);
    const visEndHM = toHM(visEndRaw);

    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();
    const dayDate = new Date(validDate);

    // Before business hours: visStart → bizStart
    if (visStartRaw < bizStartRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(visStartHM.h, visStartHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(bizStartHM.h, bizStartHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-am',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    // After business hours: bizEnd → visEnd
    if (bizEndRaw < visEndRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(bizEndHM.h, bizEndHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(visEndHM.h, visEndHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-pm',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    return ranges;
  }, [
    settings.businessHoursStart, settings.businessHoursEnd,
    settings.visibleHoursStart, settings.visibleHoursEnd,
    selectedDate, calendarView,
  ]);

  // Initialize component
  useEffect(() => {
    
    // Use setTimeout to ensure the component renders after the initial mount
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 0);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // ── Legacy localStorage cleanup ──────────────────────────────────────
  // Remove any leftover localStorage background task keys from the previous
  // client-side approach. All recurring booking creation is now handled
  // server-side via the batch API endpoint.
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('eb-bg-task-')) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
    } catch { /* ignore */ }
  }, []);



  // ── What-if visual overlay ────────────────────────────────────────────────
  // Ghost tiles and source-booking highlighting are handled entirely inside
  // BryntumSchedulerTimeline via its whatIfScenario prop + eventStore API.
  // A lightweight CSS stylesheet is still injected here for the moving/ghost
  // CSS classes that Bryntum applies to the rendered event elements.

  // For assign-free: auto-open the booking modal with the free resource pre-selected
  const whatIfAssignOpenedRef = useRef(false);
  useEffect(() => {
    if (
      whatIfScenario?.type === 'assign-free' &&
      !whatIfAssignOpenedRef.current &&
      resources.length > 0
    ) {
      const freeResource = resources.find(
        (r) => String(r.id) === String(whatIfScenario.freeResourceId)
      );
      if (freeResource) {
        whatIfAssignOpenedRef.current = true;
        const tid = window.setTimeout(() => {
          setPreselectedResource(freeResource);
          setPreselectedDate(whatIfScenario.targetWeekStart);
          setBookingModalMode('create');
          setEditingBooking(undefined);
          setIsBookingModalOpen(true);
        }, 400);
        return () => window.clearTimeout(tid);
      }
    }
    if (!whatIfScenario) {
      whatIfAssignOpenedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, resources]);

  // ── Plan Preview: auto-select view + centre on booking range ────────────────
  // Rules:
  //   ≤ 7 days  → Weekly view
  //   8–31 days → Monthly view
  //   ≥ 32 days → Quarterly view
  // selectedDate is set to the midpoint of the range so the bookings are
  // centred / immediately visible without manual scrolling.
  useEffect(() => {
    if (!planPreviewScenario) return;
    const slots = planPreviewScenario.scenario.slots;
    if (slots.length === 0) return;
    try {
      const startDates = slots.map(s => parseISO(s.startDate));
      const endDates   = slots.map(s => parseISO(s.endDate));

      const earliest = startDates.reduce((min, d) => (d < min ? d : min));
      const latest   = endDates.reduce((max, d)   => (d > max ? d : max));

      if (isNaN(earliest.getTime()) || isNaN(latest.getTime())) return;

      // Total span in whole days (inclusive: same-day = 1 day)
      const spanDays = Math.round(
        (latest.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

      // Pick the most appropriate view
      const targetView: CalendarView =
        spanDays <= 7  ? 'week'    :
        spanDays <= 31 ? 'month'   :
                         'quarter';

      // Centre selectedDate in the middle of the booking range
      const midpoint = new Date(
        (earliest.getTime() + latest.getTime()) / 2
      );

      setCalendarView(targetView);
      setSelectedDate(midpoint);
    } catch (_e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPreviewScenario]);

  // ── Tour: open/close booking form on demand ─────────────────────────────────
  // MUST be above the isInitialized early-return so hook count is stable
  useEffect(() => {
    const open = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        projectId?: string;
        requiredRole?: string;
        status?: string;
        durationType?: string;
        description?: string;
        startDate?: Date;
        endDate?: Date;
      } | undefined;
      setBookingModalMode('create');
      setEditingBooking(undefined);
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedResource(undefined);
      if (detail) {
        setPreselectedDate(detail.startDate);
        setPreselectedEndDate(detail.endDate);
        setPreselectedBookingData({
          durationType: (detail.durationType as 'full-day' | 'half-day' | 'hourly') ?? 'full-day',
          projectId: detail.projectId,
          status: detail.status as BookingStatus | undefined,
          requiredRole: detail.requiredRole,
          description: detail.description,
        });
      } else {
        setPreselectedDate(undefined);
        setPreselectedEndDate(undefined);
        setPreselectedBookingData({ durationType: 'full-day' });
      }
      setIsBookingModalOpen(true);
    };
    const close = () => setIsBookingModalOpen(false);
    window.addEventListener('tour:open-booking-form', open);
    window.addEventListener('tour:close-booking-form', close);
    return () => {
      window.removeEventListener('tour:open-booking-form', open);
      window.removeEventListener('tour:close-booking-form', close);
    };
  }, []);

  // ── Plan Preview ghost bookings ──────────────────────────────────────────────
  // Convert proposed slots into ephemeral Booking objects (never saved) so the
  // Bryntum timeline can render them as a semi-transparent overlay.
  // NOTE: this useMemo MUST live before the isInitialized early-return so that
  // hook call order is stable across renders (Rules of Hooks).
  const previewGhostBookings = useMemo((): Booking[] => {
    if (!planPreviewScenario) return [];
    return planPreviewScenario.scenario.slots.flatMap(slot => {
      const dailyBreakdown = slot.dailyBreakdown;
      if (dailyBreakdown && dailyBreakdown.length > 0) {
        // One ghost per day, spanning the exact free window the planner picked
        // for that day — the same hours the drawer's breakdown lists and the
        // ones allocation will book. Rendering these as full days overstated
        // the commitment (a 3h morning slot looked like a whole day).
        return dailyBreakdown.map((day, idx) => {
          const start = planPreviewWallClock(day.startTime);
          const end   = planPreviewWallClock(day.endTime);
          const hasWindow = start !== null && end !== null;

          return {
            id: `plan-preview-${slot.id}-day-${idx}`,
            resourceId: slot.resourceId,
            resourceIds: [slot.resourceId],
            projectId: planPreviewScenario.requestId,
            projectName: planPreviewScenario.requestProjectName,
            description: `${planPreviewScenario.requestRole} — Proposed (${day.hours}h)`,
            startTime: hasWindow ? start : `${day.date}T00:00:00`,
            endTime:   hasWindow ? end   : `${day.date}T23:59:59`,
            color: 'var(--accent-teal-500)',
            durationType: hasWindow ? ('hourly' as const) : ('full-day' as const),
            durationHours: day.hours,
            status: 'planned' as const,
            priority: 'medium' as const,
            billingClassification: 'billable' as const,
            workType: 'onsite' as const,
            notifyReminders: false,
            isPlanPreview: true,
          } as Booking & { isPlanPreview: boolean };
        });
      }
      // No daily breakdown — one ghost for the whole slot
      return [{
        id: `plan-preview-${slot.id}`,
        resourceId: slot.resourceId,
        resourceIds: [slot.resourceId],
        projectId: planPreviewScenario.requestId,
        projectName: planPreviewScenario.requestProjectName,
        description: `${planPreviewScenario.requestRole} — Proposed (${slot.hours}h)`,
        startTime: `${slot.startDate}T00:00:00`,
        endTime: `${slot.endDate}T23:59:59`,
        color: 'var(--accent-teal-500)',
        durationType: 'full-day' as const,
        status: 'planned' as const,
        priority: 'medium' as const,
        billingClassification: 'billable' as const,
        workType: 'onsite' as const,
        notifyReminders: false,
        isPlanPreview: true,
      } as Booking & { isPlanPreview: boolean }];
    });
  }, [planPreviewScenario]);

  // Combined resource time ranges (non-working days, absences, bank holidays)
  const combinedResourceTimeRanges = resourceTimeRanges;

  // Show loading state during initialization — but skip when the full-page
  // TimelineLoadingOverlay is already visible (first load in live mode).
  if (!isInitialized && hasCompletedFirstLoad.current) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return subDays(prev, 1);
        case 'week':
          return subWeeks(prev, 1);
        case 'month':
          return subMonths(prev, 1);
        case 'quarter':
          return subQuarters(prev, 1);
        case 'year':
          return subYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return addDays(prev, 1);
        case 'week':
          return addWeeks(prev, 1);
        case 'month':
          return addMonths(prev, 1);
        case 'quarter':
          return addQuarters(prev, 1);
        case 'year':
          return addYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Get max zoom level based on current view
  const getMaxZoomLevel = () => {
    switch (calendarView) {
      case 'day':
        return 1; // Day view: 0 = hourly, 1 = 15 min (only 2 levels)
      case 'week':
        return 4; // Week view: 0 = daily, 1 = 12hr, 2 = 6hr, 3 = 3hr, 4 = 1hr
      default:
        return 4; // Other views can have up to 5 levels
    }
  };

  // Get zoom level labels based on current view
  const getZoomLabels = () => {
    switch (calendarView) {
      case 'day':
        return ['Min', 'Max']; // Only 2 levels for day view
      case 'week':
        return ['Min', 'Low', 'Med', 'High', 'Max'];
      default:
        return ['Min', 'Low', 'Med', 'High', 'Max'];
    }
  };

  const handleZoomIn = () => {
    const maxZoom = getMaxZoomLevel();
    setZoomLevel((prev) => Math.min(maxZoom, prev + 1));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(0, prev - 1));
  };

  const handleTimezoneChange = (timezone: string) => {
    // Session-only selection: update the timeline display and new-booking default,
    // but do not persist it or change the system default timezone in Settings.
    setSelectedTimezone(timezone);
    sonnerToast.success('Timezone Updated', {
      description: `Timeline is now displaying times in ${timezone}. This will be used for new bookings.`,
    });
  };


  const handleCreateBooking = () => {
    if (blockIfPreview()) return;
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    // Reset preselected values when using the button
    setBookingModalMode('create');
    setEditingBooking(undefined);
    const plannerDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();
    setPreselectedDate(plannerDate);
    setPreselectedEndDate(plannerDate);

    // If "Default to logged-in resource" is enabled, pre-select the current user's resource
    const shouldDefaultToLoggedIn = settings.userDefaultToLoggedInResource ?? settings.defaultToLoggedInResource;
    if (shouldDefaultToLoggedIn && user?.id) {
      const loggedInResource = resources.find(r => String(r.id) === String(user.id));
      setPreselectedResource(loggedInResource ?? undefined);
    } else {
      setPreselectedResource(undefined);
    }
    
    // Scenario 4a: If in day view, populate start/end times from business hours settings
    // Scenario 1: If not in day view, set duration type to 'full-day'
    if (calendarView === 'day') {
      // Day view: use business hours from admin settings
      const startHour = settings.businessHoursStart ?? 9;
      const endHour = settings.businessHoursEnd ?? 17;
      setPreselectedStartTime({ hour: startHour, minute: 0 });
      setPreselectedEndTime({ hour: endHour, minute: 0 });
      setPreselectedBookingData({ durationType: 'hourly' });
    } else {
      // Non-day view: full-day booking
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ durationType: 'full-day' });
    }
    
    setIsBookingModalOpen(true);
  };

  /**
   * The bookings to read a split booking's span, pieces and resources from. A group is read
   * from the API so it is complete: the timeline holds only the pieces inside the range it
   * loaded, and a booking whose made-up days were pushed into a later month is on screen as
   * the part of it that range covers — editing or moving that part alone would have the
   * server plan the booking over the shorter span the pieces on screen cover. The bookings
   * held locally stand in when the read fails, and carry the groups it did not cover.
   */
  const readAutoAdjustGroupPieces = async (
    bookings: readonly Booking[],
  ): Promise<Booking[]> => {
    const held = [...bookingsById.values()];
    const groupIds = autoAdjustGroupIds(bookings);
    if (groupIds.length === 0) return held;

    let rows: Booking[] = [];
    try {
      rows = await reloadAutoAdjustGroups(groupIds);
    } catch (err) {
      console.error('Failed to read back the pieces of a split booking:', err);
    }

    return withReadAutoAdjustGroupRows(held, rows, groupIds);
  };

  const openEditModal = async (
    booking: Booking,
    scope: 'this' | 'all-future' | 'demand-all' | null,
    contextResourceId?: string
  ) => {
    // The whole booking is what is edited or moved, so its pieces are read from the API
    // first: the days it made up at the end can fall outside the range the timeline loaded,
    // which leaves the pieces held locally covering only the part of it on screen.
    const pieces = await readAutoAdjustGroupPieces([booking]);

    setEditScope(scope);
    setBookingModalMode('edit');
    // A split booking is edited as the one booking it is: the form opens on the span its
    // pieces cover together, whichever piece the user opened.
    setEditingBooking(autoAdjustGroupEditTarget(booking, pieces));
    setEditContextResourceId(contextResourceId);
    setPreselectedResource(undefined);
    setPreselectedDate(undefined);
    setPreselectedEndDate(undefined);
    setPreselectedStartTime(undefined);
    setPreselectedEndTime(undefined);
    setPreselectedBookingData(undefined);
    setIsBookingModalOpen(true);
  };

  /**
   * Project view: the booking was dropped on a project that does not offer its current
   * cost centre, so the move can only be saved once the user picks a new one. The edit
   * modal opens with the dragged values (new project, project name, resource and times)
   * and a blank cost centre; nothing is persisted until Save.
   */
  const openProjectMoveCostCentreModal = (
    booking: Booking,
    draggedUpdate: Partial<Booking>,
    newProject: ProjectDetails
  ) => {
    const costCentreValid = isCostCentreValidForProject(
      booking.costCentreId,
      newProject.id,
      projectDetails,
      costCentres,
      currentMode,
    );
    const customerValid = isCustomerValidForProject(
      booking.clientId ?? booking.customer,
      newProject.id,
      projectDetails,
      clients,
      currentMode,
    );

    // Snap the dragged event back until the move is saved from the modal.
    schedulerTimelineRef.current?.forceStoreRefresh();
    void openEditModal(
      {
        ...booking,
        ...draggedUpdate,
        projectId: String(newProject.id),
        projectName: newProject.name,
        ...(costCentreValid ? {} : { costCentreId: null, costCentreTask: '' }),
        ...(customerValid ? {} : { clientId: null, customer: '' }),
      },
      null
    );
    sonnerToast.info('Project details required', {
      description: `Review the cost centre and customer for ${newProject.name}, then save to complete the move.`,
    });
  };

  const handleEditBooking = async (clickedBooking: Booking, contextResourceId?: string) => {
    if (blockIfPreview()) return;

    // Locked occurrences are being deleted in the background — block editing.
    if (isBookingLocked(clickedBooking.id)) return;

    // A split booking is one booking, so it is edited as the booking the user sees: an
    // occurrence of a series whenever its group belongs to one, whichever piece was clicked.
    const booking = withAutoAdjustGroupRecurrence(
      clickedBooking,
      autoAdjustGroupPieces(clickedBooking, [...bookingsById.values()]),
    );

    // Role lacks the editBooking permission. If the user can still delete, open
    // the form in view-only ("delete-only") mode so they can remove the booking;
    // otherwise there is nothing to do — tell the user.
    if (!userCanEditBooking) {
      if (!userCanDeleteBooking) {
        sonnerToast.error('Permission Denied', {
          description: "You don't have permission to edit this booking.",
        });
        return;
      }
      await openEditModal(booking, booking.recurrenceGroupId ? 'this' : null, contextResourceId);
      return;
    }

    // Store the contextResourceId for recurring booking edit flow
    setEditContextResourceId(contextResourceId);

    if (isLinkedDemandBooking(booking) && !booking.recurrenceGroupId) {
      setBookingToEdit(booking);
      setShowDemandEditConfirm(true);
      return;
    }

    // If the booking is part of a recurrence group, check if it has future bookings.
    // The whole series is resolved (the timeline only holds the occurrences inside the
    // visible date range), so the choice is offered whenever something follows.
    if (booking.recurrenceGroupId) {
      const bookingStart = new Date(booking.startTime);
      // The other pieces of this occurrence are the same booking, not what follows it.
      const ownPieceIds = new Set(
        autoAdjustGroupPieces(booking, [...bookingsById.values()]).map(piece => String(piece.id)),
      );
      const futureBookingsInGroup = (await getSeriesOccurrences(booking.recurrenceGroupId)).filter(
        o => !ownPieceIds.has(String(o.id)) && new Date(o.startTime) > bookingStart
      );

      if (futureBookingsInGroup.length === 0) {
        // Last occurrence — open directly in edit mode (no dialog)
        await openEditModal(booking, 'this', contextResourceId);
      } else {
        // Has future bookings — show confirmation dialog
        setBookingToEdit(booking);
        setShowEditConfirm(true);
      }
    } else {
      // Non-recurring booking — open directly
      await openEditModal(booking, null, contextResourceId);
    }
  };

  const handleEditThisBooking = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      void openEditModal(bookingToEdit, 'this', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleEditAllFutureBookings = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      void openEditModal(bookingToEdit, 'all-future', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleCancelEditConfirm = () => {
    setShowEditConfirm(false);
    setBookingToEdit(null);
  };

  const handleEditThisDemandBooking = () => {
    setShowDemandEditConfirm(false);
    if (bookingToEdit) {
      void openEditModal(bookingToEdit, 'this', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleEditAllDemandBookings = () => {
    setShowDemandEditConfirm(false);
    if (bookingToEdit) {
      void openEditModal(bookingToEdit, 'demand-all', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleCancelDemandEditConfirm = () => {
    setShowDemandEditConfirm(false);
    setBookingToEdit(null);
  };

  const handleDeleteBooking = (booking: Booking) => {
    // The pieces of a split booking are the one booking being deleted, so the dialog is
    // decided for the group: an occurrence of a series still offers the choice between
    // this booking and all future ones, whichever piece was clicked.
    setBookingToDelete(withAutoAdjustGroupRecurrence(
      booking,
      autoAdjustGroupPieces(booking, [...bookingsById.values()]),
    ));
    setShowDeleteConfirm(true);
  };

  const handleDeleteBookings = (selected: Booking[]) => {
    if (selected.length === 0) return;
    if (selected.length === 1) {
      handleDeleteBooking(selected[0]);
      return;
    }
    setBookingsToDelete(selected);
  };

  const handleCancelBulkDelete = () => setBookingsToDelete([]);

  // Each selected booking is deleted as its own record: a recurring occurrence in
  // the selection goes on its own, and a multi-resource booking goes for every
  // resource — the selection is what the user picked, so nothing is widened.
  const handleConfirmBulkDelete = async () => {
    const targets = bookingsToDelete;
    setBookingsToDelete([]);
    if (targets.length === 0) return;

    // The whole selection goes in one request; the response says which ids the
    // server actually deleted.
    let deleted: Array<string | number> = [];
    let errors: string[] = [];
    setIsDeletingBooking(true);
    try {
      ({ deleted, errors } = await deleteBookingsBatch(targets.map(booking => booking.id)));
    } catch (err) {
      console.error('❌ Failed to delete the selected bookings:', err);
      sonnerToast.error(`Failed to delete ${targets.length} booking${targets.length === 1 ? '' : 's'}`);
      return;
    } finally {
      setIsDeletingBooking(false);
    }

    for (const id of deleted) removeBookingFromCache(String(id));

    if (deleted.length > 0) {
      sonnerToast.success('Bookings Deleted', {
        description: `${deleted.length} booking${deleted.length === 1 ? '' : 's'} ${deleted.length === 1 ? 'has' : 'have'} been deleted.`,
      });
    }
    if (errors.length > 0) {
      sonnerToast.error(`Failed to delete ${errors.length} booking${errors.length === 1 ? '' : 's'}`, {
        description: errors.join('\n'),
      });
    }

    if (currentMode === 'live') refetchLiveBookings();
  };

  const handleConfirmDelete = async () => {
    const target = bookingToDelete;
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
    if (!target) return;

    // A split booking goes as a whole: its pieces are the one booking the user deleted.
    // They go in one request rather than one per piece, so deleting a booking split
    // over several weeks costs the same as deleting an ordinary one.
    const pieces = autoAdjustGroupPieces(target, [...bookingsById.values()]);
    setIsDeletingBooking(true);
    try {
      if (pieces.length > 1) {
        await deleteBookingsBatch(pieces.map(piece => piece.id));
      } else {
        await deleteBooking(target.id);
      }
    } catch (err) {
      console.error('❌ Failed to delete the booking:', err);
      sonnerToast.error('Failed to delete booking');
      return;
    } finally {
      setIsDeletingBooking(false);
    }

    for (const piece of pieces) removeBookingFromCache(piece.id);
    sonnerToast.success('Booking Deleted', {
      description: 'The booking has been successfully deleted.',
    });
  };

  // Delete only the selected recurring booking occurrence. An occurrence split over the
  // days it skips goes as a whole: its pieces are the one occurrence the user deleted.
  const handleDeleteOnlyThis = async () => {
    const target = bookingToDelete;
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
    if (!target) return;

    const pieces = autoAdjustGroupPieces(target, [...bookingsById.values()]);
    setIsDeletingBooking(true);
    try {
      if (pieces.length > 1) {
        await deleteBookingsBatch(pieces.map(piece => piece.id));
      } else {
        await deleteBooking(target.id);
      }
    } catch (err) {
      console.error('❌ Failed to delete the booking occurrence:', err);
      sonnerToast.error('Failed to delete booking');
      return;
    } finally {
      setIsDeletingBooking(false);
    }

    for (const piece of pieces) removeBookingFromCache(piece.id);
    sonnerToast.success('Booking Deleted', {
      description: 'The selected booking occurrence has been deleted.',
    });
  };

  const handleDeleteOnlyThisDemand = async () => {
    const target = bookingToDelete;
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
    if (!target) return;

    setIsDeletingBooking(true);
    try {
      await deleteBooking(target.id);
    } catch (err) {
      console.error('❌ Failed to delete the open demand booking:', err);
      sonnerToast.error('Failed to delete booking');
      return;
    } finally {
      setIsDeletingBooking(false);
    }

    removeBookingFromCache(target.id);
    sonnerToast.success('Open Demand Booking Deleted', {
      description: 'The selected booking has been deleted.',
    });
  };

  const handleDeleteAllLinkedDemand = async () => {
    if (!bookingToDelete?.demandPlanGroupId) return;

    const groupId = bookingToDelete.demandPlanGroupId;
    setShowDeleteConfirm(false);
    setBookingToDelete(null);

    setIsDeletingBooking(true);
    if (currentMode === 'demo') {
      const groupBookings = [...new Map(
        [...allBookings, ...bookings]
          .filter(booking => booking.demandPlanGroupId === groupId)
          .map(booking => [String(booking.id), booking]),
      ).values()];
      const results = await Promise.allSettled(groupBookings.map(booking => deleteBooking(booking.id)));
      setIsDeletingBooking(false);
      const deleted = groupBookings.filter((_, index) => results[index].status === 'fulfilled');
      deleted.forEach(booking => removeBookingFromCache(booking.id));
      removeBookingsFromState(deleted.map(booking => booking.id));
      sonnerToast.success('Linked Demand Bookings Deleted', {
        description: `${deleted.length} linked demand booking${deleted.length === 1 ? '' : 's'} deleted.`,
      });
      const failed = results.length - deleted.length;
      if (failed > 0) {
        sonnerToast.error('Some linked demand bookings could not be deleted', {
          description: `${failed} booking${failed === 1 ? '' : 's'} failed.`,
        });
      }
      return;
    }

    try {
      const response = await bookingsApi.deleteDemandPlanGroup(
        groupId,
        buildDemandPlanGroupDeleteRequest()
      );
      for (const deletedId of response.deleted) {
        removeBookingFromCache(String(deletedId));
      }
      const deletedCount = response.deleted.length;
      sonnerToast.success('Linked Demand Bookings Deleted', {
        description: `${deletedCount} linked demand booking${deletedCount === 1 ? '' : 's'} deleted.`,
      });
      if (response.errors.length > 0) {
        sonnerToast.error('Some linked demand bookings could not be deleted', {
          description: `${response.errors.length} booking${response.errors.length === 1 ? '' : 's'} failed.`,
        });
      }
      if (currentMode === 'live') refetchLiveBookings();
    } catch (error) {
      console.error('Failed to delete linked demand bookings:', error);
      sonnerToast.error('Failed to delete linked demand bookings');
    } finally {
      setIsDeletingBooking(false);
    }
  };

  // Delete the selected booking and all future occurrences in the same recurrence group.
  //
  // The clicked (current) occurrence is deleted directly and removed from the timeline
  // immediately. Every other future occurrence is then deleted in the background with a
  // single series-delete request the server completes on its own — the UI is not blocked
  // while this runs, and the wait no longer grows with the series. The remaining
  // occurrences stay locked (greyed-out, non-interactive) until their deletes have actually
  // settled, and only then is the timeline refetched, so occurrences never flash back.
  const handleDeleteAllFuture = async () => {
    if (!bookingToDelete || !bookingToDelete.recurrenceGroupId) return;

    const groupId = bookingToDelete.recurrenceGroupId;
    // An occurrence split over the days it skips is several rows, and all of them are the
    // occurrence the user clicked: they are deleted with it, and the series delete that
    // follows starts from the day the occurrence begins rather than the piece clicked.
    const clickedPieces = autoAdjustGroupPieces(bookingToDelete, [...bookingsById.values()]);
    const clickedIds = new Set(clickedPieces.map(piece => String(piece.id)));
    const clickedId = String(bookingToDelete.id);
    const selectedStartTimeIso = clickedPieces.reduce(
      (earliest, piece) => (new Date(piece.startTime) < new Date(earliest) ? piece.startTime : earliest),
      bookingToDelete.startTime,
    );
    const selectedStartTime = new Date(selectedStartTimeIso).getTime();

    // Every future occurrence known here: the date-range fetch (`bookings`) plus, in demo
    // mode, the whole series (`allBookings`, which is empty in live mode, where the server
    // deletes the series itself).
    const idToStart = new Map<string, number>();
    for (const b of [...allBookings, ...bookings]) {
      if (b.recurrenceGroupId === groupId) {
        idToStart.set(String(b.id), new Date(b.startTime).getTime());
      }
    }
    const visibleFutureIds = Array.from(idToStart.entries())
      .filter(([id, start]) => !clickedIds.has(id) && start >= selectedStartTime)
      .map(([id]) => id);

    // Close the dialog and lock the other future occurrences so the user cannot edit,
    // right-click, or drag them while they are being deleted in the background.
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
    if (visibleFutureIds.length > 0) {
      setLockedBookingIds(new Set(visibleFutureIds));
    }

    // 1) Delete the current (clicked) occurrence and remove it from the timeline now.
    setIsDeletingBooking(true);
    try {
      if (clickedPieces.length > 1) {
        await deleteBookingsBatch(clickedPieces.map(piece => piece.id));
      } else {
        await deleteBooking(clickedId);
      }
      for (const id of clickedIds) removeBookingFromCache(id);
    } catch (err) {
      console.error(`❌ Failed to delete booking ${clickedId}:`, err);
    } finally {
      setIsDeletingBooking(false);
    }

    sonnerToast.success('Recurring Bookings Deleted', {
      description: 'This booking was deleted. All future occurrences are being deleted in the background.',
    });

    // 2) Delete every other future occurrence in the background — the UI is not blocked.
    void (async () => {
      // Augment with the full series from the backend so occurrences outside the visible
      // date range are also removed. Only adds to the set; failure falls back to visible.
      if (currentMode === 'live') {
        try {
          const fetched = await bookingsApi.getByRecurrenceGroupId(groupId);
          for (const b of fetched) {
            idToStart.set(String(b.id), new Date(b.startTime).getTime());
          }
        } catch (err) {
          console.error('Failed to fetch full recurrence group; deleting visible occurrences only:', err);
        }
      }

      const futureIds = Array.from(idToStart.entries())
        .filter(([id, start]) => !clickedIds.has(id) && start >= selectedStartTime)
        .map(([id]) => id);

      await deleteSeriesOccurrences(groupId, selectedStartTimeIso, futureIds, clickedId);
      for (const id of futureIds) removeBookingFromCache(id);

      // The occurrences left behind now end the day before the one that was deleted.
      await shortenSeriesRepeatEnd(groupId, selectedStartTimeIso);

      // Unlock (any survivors become interactive again) and reconcile the timeline once
      // every delete has settled, so nothing flashes back onto the timeline.
      setLockedBookingIds(new Set());
      if (currentMode === 'live') refetchLiveBookings();
    })();
  };

  // Delete only the selected resource's booking from a multi-resource booking.
  // With the single-booking model, we remove this resource from the booking's
  // resourceIds and update rather than deleting the whole booking.
  const handleDeleteForThisResource = async () => {
    if (!bookingToDelete) return;

    setIsDeletingBooking(true);
    try {
      const updatedResourceIds = bookingToDelete.resourceIds
        ? bookingToDelete.resourceIds.filter(rid => String(rid) !== String(bookingToDelete.resourceId))
        : [];

      if (updatedResourceIds.length === 0) {
        // Last resource — delete the booking entirely
        await deleteBooking(bookingToDelete.id);
        removeBookingFromCache(bookingToDelete.id);
      } else {
        // Update the booking to remove this resource; set primary to the next available
        await updateBooking(String(bookingToDelete.id), {
          resourceId: updatedResourceIds[0],
          resourceIds: updatedResourceIds,
        });
      }

      sonnerToast.success('Booking Deleted', {
        description: 'The booking has been deleted for the selected resource.',
      });
      if (currentMode === 'live') refetchLiveBookings();
    } catch (err) {
      console.error('Failed to delete booking for this resource:', err);
      sonnerToast.error('Failed to delete booking');
    } finally {
      setIsDeletingBooking(false);
    }

    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete booking for all resources in a multi-resource booking.
  // With the single-booking model, just delete the one booking record.
  const handleDeleteForAllResources = async () => {
    if (!bookingToDelete) return;

    setIsDeletingBooking(true);
    try {
      await deleteBooking(bookingToDelete.id);
      removeBookingFromCache(bookingToDelete.id);

      sonnerToast.success('Booking Deleted', {
        description: 'Successfully deleted booking for all resources.',
      });
    } catch (err) {
      console.error(`Failed to delete booking ${bookingToDelete.id}:`, err);
      sonnerToast.error('Failed to delete booking');
    } finally {
      setIsDeletingBooking(false);
    }

    setShowDeleteConfirm(false);
    setBookingToDelete(null);

    // Reconcile the timeline with the server in the background — the booking is already
    // gone from the local cache, so the UI must not wait on the full-range refetch.
    void (async () => {
      if (currentMode === 'live') await refetchLiveBookings();
    })();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleMoveBooking = async (booking: Booking) => {
    if (blockIfPreview()) return;
    if (!canMoveBooking(booking, false, [...bookingsById.values()])) return;
    // The dialog offers the span the booking's pieces cover, so they are read before it
    // opens: part of a booking whose days were made up in a later month is off screen.
    setBookingToMovePieces(await readAutoAdjustGroupPieces([booking]));
    setBookingToMove(booking);
    setDemandMoveScope(null);
    if (isLinkedDemandBooking(booking)) {
      setShowDemandMoveConfirm(true);
      return;
    }
    setShowMoveDialog(true);
  };

  // ── Resolve Conflict via Batch Assignment Modal ────────────────────────────
  // `cellBookings` is the surplus only — the anchor booking that stays with the
  // original resource has already been excluded upstream. Every booking handed
  // to the modal is therefore meant to be reassigned.
  const handleResolveConflict = (cellBookings: Booking[]) => {
    setBookingsToResolve(cellBookings);
    setShowResolveDialog(true);
  };

  const handleResolveConfirm = async (assignments: { bookingId: string; resourceId: string }[]) => {
    let successCount = 0;
    const refused: string[] = [];
    for (const { bookingId, resourceId } of assignments) {
      const booking = bookingsById.get(String(bookingId));
      const resource = resources.find(r => String(r.id) === String(resourceId));
      if (booking && resource) {
        const contract = checkContractWindow(resource, new Date(booking.startTime), new Date(booking.endTime));
        if (!contract.allowed) {
          refused.push(contract.message ?? `${resource.name} is outside their contract`);
          continue;
        }
      }
      try {
        await updateBooking(bookingId, { resourceId });
        successCount++;
      } catch { /* continue with remaining */ }
    }
    if (successCount > 0) {
      sonnerToast.success(`${successCount} conflict${successCount !== 1 ? 's' : ''} resolved`, {
        description: `${successCount} booking${successCount !== 1 ? 's' : ''} successfully reallocated.`,
      });
    }
    if (refused.length > 0) {
      sonnerToast.error(`Cannot reassign ${refused.length} booking${refused.length === 1 ? '' : 's'}`, {
        description: refused.join('\n'),
      });
    }
    if (successCount + refused.length < assignments.length) {
      sonnerToast.error(`${assignments.length - successCount - refused.length} reassignment(s) failed`, {
        description: 'Please try again or edit those bookings manually.',
      });
    }
    setShowResolveDialog(false);
    setBookingsToResolve([]);
  };

  const handleConfirmMove = async (
    bookingId: string | number,
    newStartTime: Date,
    newEndTime: Date,
    newResourceId: string,
    newProjectId: string | null,
    newCostCentreId: string | null
  ) => {
    if (!bookingToMove) return;

    try {
      // The pieces of a split booking move as one: the move is saved on the piece the
      // group starts on, covering the whole span, and the server re-plans it from there.
      const movedBooking = bookingToMoveSpan?.anchor ?? bookingToMove;
      const movedBookingId = bookingToMoveSpan ? movedBooking.id : bookingId;
      const movedGroupPieceIds = bookingToMoveSpan
        ? autoAdjustGroupPieces(movedBooking, [...bookingsById.values()]).map(piece => String(piece.id))
        : [];
      const newProject = projectDetails.find(
        project => String(project.id) === String(newProjectId)
      );
      const targetResource = resources.find(
        resource => String(resource.id) === String(newResourceId),
      );
      const resourceChanged = String(bookingToMove.resourceId ?? '') !== String(newResourceId);
      if (targetResource) {
        const availability = resourceChanged
          ? checkResourceAvailability(targetResource, newStartTime, newEndTime)
          : checkContractWindow(targetResource, newStartTime, newEndTime);
        if (!availability.allowed && !isPlannedAwayByAutoAdjust(movedBooking, availability)) {
          sonnerToast.error('Cannot Move Booking', { description: availability.message });
          return;
        }
      }

      const updatedBooking: Partial<Booking> = {
        ...movedBooking,
        // A move onto another resource carries that resource's role, and re-keys
        // the booking's per-resource assignment, while leaving its skills alone.
        ...(targetResource
          ? buildBookingResourceMoveFields({
            booking: movedBooking,
            targetResource,
            isLive: currentMode === 'live',
          })
          : { resourceId: newResourceId }),
        projectId: newProjectId,
        projectName: newProject?.name ?? (newProjectId === null ? '' : bookingToMove.projectName),
        costCentreId: newCostCentreId,
        startTime: formatDateAsLocalISO(newStartTime),
        endTime: formatDateAsLocalISO(newEndTime),
      };

      if (isLinkedDemandBooking(bookingToMove)) {
        if (!demandMoveScope) {
          throw new Error('Open Demand move scope was not selected.');
        }
        await performDemandBookingUpdate(
          bookingId,
          bookingToMove,
          updatedBooking,
          demandMoveScope === 'all' ? 'demand-all' : 'this',
          demandMoveScope
        );
      } else {
        await updateBooking(String(movedBookingId), updatedBooking);
        // A re-planned split booking covers the working days the server chose, not the
        // ones its pieces held: the rows the re-plan replaced still sit in the date-range
        // cache the timeline draws from, so they are dropped from it (the pieces the save
        // kept are read back into the booking state and drawn from there), the range is
        // re-read and the store re-synced. Without it a piece stays on its old dates
        // until the page is refreshed.
        for (const pieceId of movedGroupPieceIds) removeBookingFromCache(pieceId);
        // The re-read has to land before the store is re-synced, or the store is synced
        // against the rows as they stood before the move.
        if (currentMode === 'live') await refetchLiveBookings();
        if (bookingToMoveSpan) {
          setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
        }

        sonnerToast.success('Booking moved', {
          description: 'The booking has been successfully moved.',
        });
      }
    } catch (error) {
      console.error('Error moving booking:', error);
      if (!handleBookingWriteRejected(error, 'modify')) {
        sonnerToast.error('Failed to move booking', {
          description: 'An error occurred while moving the booking.',
        });
      }
    } finally {
      setShowMoveDialog(false);
      setBookingToMove(null);
      setDemandMoveScope(null);
    }
  };

  const handleCancelMove = () => {
    setShowMoveDialog(false);
    setBookingToMove(null);
    setDemandMoveScope(null);
  };

  const handleDuplicateBooking = (booking: Booking) => {
    // Create a copy of the booking without the ID
    const duplicatedBooking: Omit<Booking, 'id'> = {
      resourceId: booking.resourceId,
      projectId: booking.projectId,
      projectName: booking.projectName,
      costCentreId: booking.costCentreId,
      description: booking.description,
      startTime: booking.startTime,
      endTime: booking.endTime,
      timezone: booking.timezone,
      color: booking.color,
      durationType: booking.durationType,
      halfDayPeriod: booking.halfDayPeriod,
      recurrence: booking.recurrence,
      billingClassification: booking.billingClassification,
      workType: booking.workType,
      taskDescription: booking.taskDescription,
      bookingNotes: booking.bookingNotes,
      status: booking.status,
      priority: booking.priority,
      attachments: booking.attachments,
      notifyReminders: booking.notifyReminders,
    };

    const contractError = contractRefusal(duplicatedBooking);
    if (contractError) {
      sonnerToast.error('Cannot Duplicate Booking', { description: contractError });
      return;
    }
    
    addBooking(duplicatedBooking);
    sonnerToast.success('Booking Duplicated', {
      description: 'A copy of the booking has been created.',
    });
  };

  const handleStatusChange = async (booking: Booking, newStatus: string, statusColor: string) => {
    try {
      if (filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(newStatus)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, newStatus]);
      }
      
      // Preserve color tag override — only fall back to status color when no tag is active
      const hasColorTag = settings.colorOverrides?.some(co => co.color === booking.color);
      await updateBooking(booking.id, {
        ...booking,
        status: newStatus as Booking['status'],
        color: hasColorTag ? booking.color : statusColor,
      });
      
      if (currentMode === 'live') refetchLiveBookings();
      
      const statusConfig = settings.statusConfigs.find(s => String(s.id) === String(newStatus));
      const statusDisplayName = statusConfig?.customText || statusConfig?.text || newStatus;
      
      sonnerToast.success('Status Updated', {
        description: `Booking status changed to "${statusDisplayName}"`,
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      if (handleBookingWriteRejected(error, 'modify')) return;
      sonnerToast.error('Failed to Update Status', {
        description: 'Please try again.',
      });
    }
  };

  const handleStatusChangeBookings = async (selected: Booking[], newStatus: string, statusColor: string) => {
    if (selected.length === 0) return;
    if (selected.length === 1) {
      await handleStatusChange(selected[0], newStatus, statusColor);
      return;
    }

    if (filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(newStatus)) {
      updateFilter('bookingStatuses', [...filters.bookingStatuses, newStatus]);
    }

    // One request for the whole selection: each booking carries only its own
    // status/colour change, so the server leaves every other field alone.
    let updated: Array<string | number> = [];
    let errors: string[] = [];
    try {
      ({ updated, errors } = await updateBookingsBatch(selected.map(booking => {
        const hasColorTag = settings.colorOverrides?.some(co => co.color === booking.color);
        return {
          id: booking.id,
          status: newStatus as Booking['status'],
          color: hasColorTag ? booking.color : statusColor,
        };
      })));
    } catch (error) {
      if (handleBookingWriteRejected(error, 'modify')) return;
      console.error('Error updating the selected bookings\' status:', error);
      sonnerToast.error(`Failed to update ${selected.length} booking${selected.length === 1 ? '' : 's'} status`);
      return;
    }

    const statusConfig = settings.statusConfigs.find(s => String(s.id) === String(newStatus));
    const statusDisplayName = statusConfig?.customText || statusConfig?.text || newStatus;

    if (updated.length > 0) {
      sonnerToast.success('Statuses Updated', {
        description: `${updated.length} booking${updated.length === 1 ? '' : 's'} changed to "${statusDisplayName}".`,
      });
    }
    if (errors.length > 0) {
      sonnerToast.error(`Failed to update ${errors.length} booking${errors.length === 1 ? '' : 's'} status`, {
        description: errors.join('\n'),
      });
    }

    if (currentMode === 'live') refetchLiveBookings();
  };

  const handleSlotClick = (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => {
    if (blockIfPreview()) return;
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    setPreselectedResource(resource);
    setPreselectedDate(date);
    
    // Use projectId from bookingData if provided (from BryntumSchedulerTimeline in project-resource mode)
    // This ensures the project is pre-selected when clicking on a day in project & resource view
    const projectIdToPreselect = bookingData?.projectId;
    // Customer name from bookingData (BryntumSchedulerTimeline in customer mode) so
    // the customer field is pre-populated when clicking a cell in Customer view.
    const customerToPreselect = bookingData?.customer;
    
    // Scenario 2: Timeline click - set duration type based on calendar view
    // Day view: hourly booking with specific times
    // Non-day view: full-day booking (ignore Bryntum's time, use 00:00:00)
    if (calendarView === 'day') {
      // Day view: Add 15 minutes for end time
      let endHour: number;
      let endMinute: number;
      endMinute = startTime.minute + 15;
      if (endMinute >= 60) {
        endHour = startTime.hour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startTime.hour;
      }
      
      setPreselectedStartTime(startTime);
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect,
        customer: customerToPreselect 
      });
    } else {
      // Non-day views: full-day booking - don't set times (use 00:00:00)
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect,
        customer: customerToPreselect 
      });
    }
    
    setIsBookingModalOpen(true);
  };

  const handleDragSelect = (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => {
    if (blockIfPreview()) return;
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    setPreselectedResource(resource);
    setPreselectedDate(date);
    setPreselectedEndDate(endDate || date);
    setPreselectedStartTime(startTime);
    setPreselectedEndTime(endTime);
    
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && String(resource.id).includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = String(resource.id).split('-');
      if (parts.length >= 2 && !String(resource.id).startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    
    // Scenario 3 & 4b: Timeline drag - set duration type based on calendar view
    // Day view (Scenario 4b): hourly booking with times from timeline
    // Non-day view (Scenario 3): full-day booking (ignore Bryntum's time, use 00:00:00)
      if (calendarView === 'day' || bookingData?.durationType === 'hourly') {
        // Hourly selection (either Day view or zoomed hourly in other views): use dragged times
        setPreselectedStartTime(startTime);
        setPreselectedEndTime(endTime);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'hourly',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      } else {
        // Non-hourly in non-day views: treat as full-day, ignore times
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'full-day',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      }
    
    setIsBookingModalOpen(true);
  };

  const handlePasteBooking = async (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    bookingData?: Booking
  ) => {
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    if (!bookingData) {
      sonnerToast.error('No Booking to Paste', {
        description: 'Please copy a booking first.',
      });
      return;
    }
    
    // Move both edges of the copied booking by the same number of calendar days
    // so the pasted copy keeps the source booking's day span and time-of-day.
    const pastedTimes = computePastedBookingTimes(bookingData, date);
    
    // Create a copy of the booking with new resource and times
    const pastedBooking: Omit<Booking, 'id'> = {
      resourceId: resource.id,
      projectId: bookingData.projectId,
      projectName: bookingData.projectName,
      costCentreId: bookingData.costCentreId,
      description: bookingData.description,
      startTime: pastedTimes.startTime,
      endTime: pastedTimes.endTime,
      timezone: bookingData.timezone,
      color: bookingData.color,
      durationType: bookingData.durationType,
      halfDayPeriod: bookingData.halfDayPeriod,
      recurrence: bookingData.recurrence,
      billingClassification: bookingData.billingClassification,
      workType: bookingData.workType,
      taskDescription: bookingData.taskDescription,
      bookingNotes: bookingData.bookingNotes,
      status: bookingData.status,
      priority: bookingData.priority,
      attachments: bookingData.attachments,
      notifyReminders: bookingData.notifyReminders,
      bookingTypeId: bookingData.bookingTypeId,
      customer: bookingData.customer,
      location: bookingData.location,
      addressLine2: bookingData.addressLine2,
      city: bookingData.city,
      county: bookingData.county,
      postcode: bookingData.postcode,
      country: bookingData.country,
      details: bookingData.details,
      bookingReference: bookingData.bookingReference,
      userChar: bookingData.userChar,
      userCharOptionIds: bookingData.userCharOptionIds,
      userNumber: bookingData.userNumber,
      userFlag: bookingData.userFlag,
      userDate: bookingData.userDate,
      userTime: bookingData.userTime,
      userNotes: bookingData.userNotes,
    };
    
    const pasteContractError = contractRefusal(pastedBooking);
    if (pasteContractError) {
      sonnerToast.error('Cannot Paste Booking', { description: pasteContractError });
      return;
    }

    // The paste is waited on rather than fired and forgotten, so the saving overlay
    // covers it and a booking the API refuses is reported instead of appearing to work.
    setIsSavingBooking(true);
    try {
      const pasted = await addBooking(pastedBooking);

      // A booking pasted onto a period with non-working days is saved as one piece per
      // run of working days. Only the first piece comes back from the write, so the
      // range is re-read and the timeline re-synced against it — otherwise the pasted
      // booking shows its first piece alone until the page is refreshed.
      if (pasted?.autoAdjustGroupId) {
        if (currentMode === 'live') refetchLiveBookings();
        setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
      }
    } catch (error) {
      console.error('❌ Failed to paste the booking:', error);
      if (!handleBookingWriteRejected(error, 'create')) {
        sonnerToast.error('Failed to Paste Booking', {
          description: 'Please try again.',
        });
      }
      return;
    } finally {
      setIsSavingBooking(false);
    }

    sonnerToast.success('Booking Pasted', {
      description: `"${bookingData.projectName || bookingData.description || 'Booking'}" has been pasted to ${resource.name}.`,
    });
    
    // Clear the copied booking after paste
    setCopiedBooking(null);
  };

  const handleQuickAddBooking = (resource: Resource, date: Date, startTime?: string, endTime?: string) => {
    if (blockIfPreview()) return;
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    setQuickAddPreselectedDate(date);
    setQuickAddPreselectedResource(resource);
    if (startTime) {
      setQuickAddDefaultStartTime(startTime);
    }
    if (endTime) {
      setQuickAddDefaultEndTime(endTime);
    }
    // Extract project ID if in project-resource mode and store it for Quick Add modal
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    // Store project ID in preselected booking data for Quick Add modal to use
    setPreselectedBookingData({ projectId: projectIdToPreselect });
    
    setIsQuickAddModalOpen(true);
  };

  const handleQuickAddBookingSave = async (data: QuickAddBookingData) => {
    
    try {
      // Check if we have a preselected resource
      if (!quickAddPreselectedResource) {
        sonnerToast.error('No Resource Selected', {
          description: 'Please select a resource to create a booking.',
        });
        return;
      }
      
      // If quick add is disabled (Quick Add Default set to "None"), do not create a booking
      if (settings.quickAddDefault === 'none') {
        return;
      }

      // Get the default booking type from settings
      const defaultBookingTypeId = settings.quickAddDefault;
      const defaultBookingType = settings.bookingTypes.find(bt => String(bt.id) === String(defaultBookingTypeId));
      
      // If no default booking type is set or found, use the first active booking type
      const bookingTypeId = defaultBookingType?.id || settings.bookingTypes.find(bt => bt.active)?.id || settings.bookingTypes[0]?.id;
      
      if (!bookingTypeId) {
        sonnerToast.error('Booking Type not assigned', {
          description: 'No booking type has been assigned. Please contact your administrator to configure booking type permissions.',
        });
        return;
      }
      
      // Prepare start and end times
      let startTime: string;
      let endTime: string;
      
      if (data.isFullDay) {
        // Full day booking - use UTC dates at midnight (00:00:00 UTC)
        // Extract local date values and create UTC dates
        const startDateObj = new Date(data.startDate);
        const startYear = startDateObj.getFullYear();
        const startMonth = startDateObj.getMonth();
        const startDay = startDateObj.getDate();
        
        const endDateObj = new Date(data.endDate);
        const endYear = endDateObj.getFullYear();
        const endMonth = endDateObj.getMonth();
        const endDay = endDateObj.getDate();
        
        // Create UTC dates at midnight - end date + 1 for exclusive end
        const utcStartDate = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));
        const utcEndDate = new Date(Date.UTC(endYear, endMonth, endDay + 1, 0, 0, 0, 0));
        
        startTime = utcStartDate.toISOString();
        endTime = utcEndDate.toISOString();
      } else {
        // Timed booking - combine date with time as UTC wall-clock so the
        // entered time is stored and displayed unchanged regardless of the
        // browser's timezone offset (display path treats times as literal).
        const [startHour, startMinute] = data.startTime!.split(':').map(Number);
        const startDateObj = new Date(data.startDate);
        startTime = new Date(Date.UTC(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate(),
          startHour,
          startMinute,
          0,
          0
        )).toISOString();

        const [endHour, endMinute] = data.endTime!.split(':').map(Number);
        const endDateObj = new Date(data.endDate);
        endTime = new Date(Date.UTC(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate(),
          endHour,
          endMinute,
          0,
          0
        )).toISOString();
      }
      
      // Get the preselected project ID only in project-resource view
      let selectedProject = null;
      
      // In project-resource view, use the preselected project
      if (groupingMode === 'project-resource' && preselectedBookingData?.projectId) {
        selectedProject = projectDetails.find(p => String(p.id) === String(preselectedBookingData.projectId));
        
        if (!selectedProject) {
          sonnerToast.error('No Projects Available', {
            description: 'Please create a project before adding bookings.',
          });
          return;
        }
      }
      // In resource view, leave project unset (null)
      
      // Extract the actual resource ID (in project-resource or customer mode, IDs are composite)
      let actualResourceId = quickAddPreselectedResource.id;
      if (groupingMode === 'project-resource' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-')) {
        const parts = actualResourceId.split('-');
        if (parts.length >= 2) {
          actualResourceId = parts.slice(1).join('-'); // Get everything after the first dash
        }
      } else if (groupingMode === 'customer' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-') && !actualResourceId.startsWith('customer-')) {
        // Match by suffix to handle hyphens in customer names
        const matchedRes = resources.find(r => actualResourceId.endsWith(`-${String(r.id)}`));
        if (matchedRes) {
          actualResourceId = String(matchedRes.id);
        } else {
          const dashIdx = actualResourceId.indexOf('-');
          if (dashIdx !== -1) actualResourceId = actualResourceId.slice(dashIdx + 1);
        }
      }
      
      // Create booking for the preselected resource only
      const newBooking: Omit<Booking, 'id'> = {
        bookingTypeId: bookingTypeId != null ? String(bookingTypeId) : undefined, // Use the default booking type from settings
        resourceId: actualResourceId,
        resourceIds: [actualResourceId],
        projectId: selectedProject?.id != null ? String(selectedProject.id) : null,
        projectName: selectedProject?.name || '',
        clientId: selectedProject?.clientId ?? null, // Auto-populate client from project
        customer: selectedProject?.clientName || undefined,
        description: data.description || '',
        userChar: data.userChar,
        userCharOptionIds: data.userCharOptionIds,
        startTime,
        endTime,
        timezone: selectedTimezone,
        color: selectedProject?.color || '#3b82f6',
        durationType: data.isFullDay ? 'full-day' : 'hourly',
        billingClassification: 'billable',
        workType: 'onsite',
        status: 'confirmed',
        priority: 'medium',
        notifyReminders: false,
      };

      const outOfContract = contractRefusal(newBooking);
      if (outOfContract) {
        sonnerToast.error('Cannot Create Booking', { description: outOfContract });
        return;
      }

      await addBooking(newBooking);
      
      // Close the modal
      setIsQuickAddModalOpen(false);
      
      // Reset preselected resource
      setQuickAddPreselectedResource(undefined);
      
      // Show success message
      const bookingTypeName = defaultBookingType?.name || 'booking';
      sonnerToast.success('Booking Created', {
        description: `Successfully created ${bookingTypeName} for ${quickAddPreselectedResource.name}.`,
      });
      
    } catch (error) {
      console.error('❌ Error creating quick booking:', error);
      if (handleBookingWriteRejected(error, 'create')) return;
      sonnerToast.error('Failed to create booking', {
        description: 'Please try again.',
      });
    }
  };

  const handleSaveBooking = async (booking: Omit<Booking, 'id'>) => {
    const contractError = contractRefusal(booking);
    if (contractError) {
      sonnerToast.error('Cannot Save Booking', { description: contractError });
      return;
    }

    setIsSavingBooking(true);
    
    // ── Recurring booking creation ──────────────────────────────────────
    // The modal sets booking.recurrence (pattern, interval, etc.) but does NOT
    // generate individual occurrences. We detect recurrence here, generate dates,
    // create the first booking immediately, and process the rest in background.
    const hasRecurrence = booking.recurrence && booking.recurrence.pattern && booking.recurrence.pattern !== 'none';

    if (hasRecurrence) {
      const recurrenceGroupId = crypto.randomUUID();

      // Build the first (parent) booking — keeps recurrence settings for editing
      const parentBooking: Omit<Booking, 'id'> = {
        ...booking,
        recurrenceGroupId,
        isRecurrenceParent: true,
      };

      // Generate remaining occurrence dates (excludes the first/parent date)
      const occurrenceDates = clampRecurrenceDatesToContract(
        booking, generateRecurrenceDates(booking, booking as Booking),
      );

      // Check if the original booking's start date falls within the repeat
      // dialog's date range. If the series start date was set later than the
      // booking start, the first booking should be skipped.
      const firstBookingInRange = repeatKeepsOwnSlot(booking);

      const childBookingsFor = (parentBookingId?: number): Omit<Booking, 'id'>[] => {
        const originalStart = new Date(booking.startTime);
        const duration = new Date(booking.endTime).getTime() - originalStart.getTime();
        return occurrenceDates.map((date, index) => {
          const newStart = new Date(date);
          newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
          const ownsRecurrence = !firstBookingInRange && index === 0;
          return {
            ...booking,
            recurrence: ownsRecurrence ? booking.recurrence : undefined,
            startTime: formatDateAsLocalISO(newStart),
            endTime: formatDateAsLocalISO(new Date(newStart.getTime() + duration)),
            recurrenceGroupId,
            isRecurrenceParent: ownsRecurrence,
            parentBookingId,
          };
        });
      };

      try {
        const type = settings.bookingTypes.find(type => String(type.id) === String(booking.bookingTypeId));
        const usesLookups = type?.fieldConfigurations.some(field =>
          userCharIndex(field.fieldName) && (field.isVisible || field.showOnCustomTab));
        if (currentMode === 'live' && usesLookups) {
          await createBatchBookings([
            ...(firstBookingInRange ? [{ ...parentBooking, resourceId: parentBooking.resourceIds?.[0] ?? parentBooking.resourceId }] : []),
            ...childBookingsFor(),
          ]);
          refetchLiveBookings();
          setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
          sonnerToast.success('Recurring bookings created successfully.');
          setPreselectedResource(undefined);
          setPreselectedStartTime(undefined);
          setPreselectedEndTime(undefined);
          setPreselectedBookingData(undefined);
          return;
        }
        // Step 1: Create the first booking immediately (only if within range)
        let parentBookingId: number | undefined;
        let createdParent: Booking | undefined;
        if (firstBookingInRange) {
          if (parentBooking.resourceIds && parentBooking.resourceIds.length > 0) {
            const multiResourceParent: Omit<Booking, 'id'> = {
              ...parentBooking,
              resourceId: parentBooking.resourceIds[0],
              resourceIds: parentBooking.resourceIds,
            };
            createdParent = await addBooking(multiResourceParent);
          } else {
            createdParent = await addBooking(parentBooking);
          }
          if (createdParent) parentBookingId = Number(createdParent.id);
        }

        // The first occurrence is saved as one piece per run of working days when it
        // auto-adjusts, and the write only carries the first of them, so the range is
        // re-read and the timeline re-synced against it — otherwise that occurrence shows
        // its first piece alone until the page is refreshed.
        if (createdParent?.autoAdjustGroupId) {
          if (currentMode === 'live') refetchLiveBookings();
          setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
        }

        const immediateCount = firstBookingInRange ? 1 : 0;

        // Single toast: confirm first booking + inform about background creation
        if (occurrenceDates.length > 0) {
          const bgCount = occurrenceDates.length;
          const msg = immediateCount > 0
            ? `1 booking created successfully. ${bgCount} recurring booking${bgCount !== 1 ? 's' : ''} will be created in the background.`
            : `${bgCount} recurring booking${bgCount !== 1 ? 's' : ''} will be created in the background.`;
          sonnerToast.success(msg);

          // Step 2: Build all child bookings and send to server in a single batch request.
          // Only the parent occurrence carries the repeat settings, so when the
          // booking's own slot is skipped the first generated occurrence has to take
          // that role — otherwise the series has no persisted repeat settings and the
          // tooltip can only fall back to a generic "Recurring booking".
          const childBookings = childBookingsFor(parentBookingId);

          // Fire-and-forget: the server creates the occurrences and their bookkeeping
          // even if the browser closes. The timeline is deliberately left alone when it
          // finishes — the new occurrences appear the next time the user changes the
          // date range or the view.
          void createBatchBookings(childBookings).catch(err => {
            console.error('Server batch creation failed:', err);
          });
        } else {
          sonnerToast.success('Booking Created', {
            description: 'The booking has been successfully created.',
          });
        }

        // Reset preselected values after save
        setPreselectedResource(undefined);
        setPreselectedDate(undefined);
        setPreselectedEndDate(undefined);
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData(undefined);
      } catch (error) {
        console.error('❌ Error creating recurring booking:', error);
        if (isBookingValidationError(error)) throw error;
        if (handleBookingWriteRejected(error, 'create')) {
          setIsSavingBooking(false);
          return;
        }
        sonnerToast.error('Failed to create recurring booking', {
          description: 'Please try again.',
        });
      } finally {
        setIsSavingBooking(false);
      }
      return;
    }
    
    try {
      // Check if multiple resources are selected
      if (booking.resourceIds && booking.resourceIds.length > 0) {
        
        // Create a single booking with the first resource as the primary resourceId.
        // The addBooking function creates BookingResource entries for all resourceIds,
        // so we only need one booking record mapped to multiple resources.
        const multiResourceBooking: Omit<Booking, 'id'> = {
          ...booking,
          resourceId: booking.resourceIds[0], // Primary resource
          resourceIds: booking.resourceIds, // All resources for BookingResource entries
        };
        
        await addBooking(multiResourceBooking);
        
        sonnerToast.success('Booking Created', {
          description: `Successfully created booking across ${booking.resourceIds.length} resource${booking.resourceIds.length > 1 ? 's' : ''}.`,
        });
      } else {
        // Single resource booking (legacy behavior)
        await addBooking(booking);
        sonnerToast.success('Booking Created', {
          description: 'The booking has been successfully created.',
        });
      }
      
      // Refetch live bookings so utilisation recalculates with server data
      if (currentMode === 'live') refetchLiveBookings();

      // Reset preselected values after save
      setPreselectedResource(undefined);
      setPreselectedDate(undefined);
      setPreselectedEndDate(undefined);
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData(undefined);
    } catch (error) {
      console.error('❌ Error adding booking:', error);
      // A booking the API refused for a reason of its own goes back to the form, which
      // stays open with the reason in a dialog so the dates or resources can be corrected.
      if (isBookingValidationError(error)) throw error;
      if (!handleBookingWriteRejected(error, 'create')) {
        sonnerToast.error('Failed to create booking', {
          description: 'Please try again.',
        });
      }
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Every occurrence of a recurrence group. The timeline only holds the visible date
  // range, so in live mode the whole series is resolved from the API and merged with
  // whatever is already on screen.
  const getSeriesOccurrences = async (
    recurrenceGroupId: string,
    fromServerOnly = false
  ): Promise<SeriesOccurrence[]> => {
    const byId = new Map<string, SeriesOccurrence>();
    if (!(fromServerOnly && currentMode === 'live')) {
      for (const b of [...allBookings, ...bookings]) {
        if (b.recurrenceGroupId === recurrenceGroupId) {
          byId.set(String(b.id), { id: String(b.id), startTime: b.startTime });
        }
      }
    }
    if (currentMode === 'live') {
      try {
        const fetched = await bookingsApi.getByRecurrenceGroupId(recurrenceGroupId);
        for (const b of fetched) {
          byId.set(String(b.id), { id: String(b.id), startTime: b.startTime });
        }
      } catch (err) {
        console.error('Failed to fetch recurrence group; using visible occurrences only:', err);
      }
    }
    return Array.from(byId.values());
  };

  // The repeat settings the edit form was filled from, so a save that leaves them as
  // shown compares equal. They are looked up the way the form looks them up: the
  // occurrence's own RecurrenceSettings, then the series', then its parent's, and
  // failing those the recurrence JSON of the parent on screen.
  const getSeriesRecurrence = async (booking: Booking): Promise<RecurrenceSettings | undefined> => {
    if (booking.recurrence && booking.recurrence.pattern !== 'none') return booking.recurrence;
    const groupId = booking.recurrenceGroupId;
    if (!groupId) return undefined;
    const bookingId = parseInt(String(booking.id), 10);
    if (currentMode === 'live' && !isNaN(bookingId)) {
      let rs = await recurrenceSettingsApi.getByBookingId(bookingId).catch(() => null);
      if (!rs) rs = await recurrenceSettingsApi.getByRecurrenceGroupId(groupId).catch(() => null);
      if (!rs && booking.parentBookingId) {
        rs = await recurrenceSettingsApi.getByBookingId(booking.parentBookingId).catch(() => null);
      }
      if (rs) return mapRecurrenceSettingsApi(rs);
    }
    const parent = [...allBookings, ...bookings].find(
      b => b.recurrenceGroupId === groupId && b.recurrence && b.recurrence.pattern !== 'none'
    );
    return parent?.recurrence;
  };

  // Deletes an occurrence and everything after it in the same recurrence group. In live
  // mode the whole stretch goes out as one request that the server completes on its own,
  // instead of one soft-delete round trip per occurrence; demo mode (and a failed batch)
  // falls back to deleting the ids one by one.
  const deleteSeriesOccurrences = async (
    recurrenceGroupId: string | undefined,
    fromStartTime: string,
    ids: string[],
    excludeBookingId?: string
  ): Promise<void> => {
    if (currentMode === 'live' && recurrenceGroupId) {
      try {
        const excludeId = excludeBookingId != null ? Number(excludeBookingId) : undefined;
        await bookingsApi.deleteFutureByRecurrenceGroupId(
          recurrenceGroupId,
          fromStartTime,
          user?.name || 'system',
          Number.isNaN(excludeId) ? undefined : excludeId
        );
        removeBookingsFromState(ids);
        return;
      } catch (err) {
        console.error('Series delete failed; falling back to per-occurrence deletes:', err);
      }
    }

    await Promise.allSettled(
      ids.map(id =>
        deleteBooking(id).catch(err => {
          console.error(`❌ Failed to delete booking ${id}:`, err);
        })
      )
    );
  };

  // Pulls a series' repeat end date back to the day before `cutoffStartTime`, so the
  // occurrences that survive a "delete/remove all future bookings" still describe the
  // series they are actually part of. Both the settings stored for the group and the
  // repeat details carried by individual earlier occurrences are brought in line.
  const shortenSeriesRepeatEnd = async (
    recurrenceGroupId: string | undefined,
    cutoffStartTime: string
  ): Promise<void> => {
    if (!recurrenceGroupId) return;

    const cutoff = new Date(cutoffStartTime);
    const newEnd = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate() - 1);
    const newEndIso = formatDateAsLocalISO(newEnd);

    if (currentMode === 'live') {
      try {
        const rs = await recurrenceSettingsApi.getByRecurrenceGroupId(recurrenceGroupId);
        if (rs) {
          await recurrenceSettingsApi.update(rs.id, {
            ...rs,
            endType: 'on-date',
            endDate: newEndIso,
            endAfterOccurrences: undefined,
          });
        }
      } catch (err) {
        console.error('Failed to shorten the stored repeat end date:', err);
      }
    }

    // Occurrences that carry their own copy of the repeat details. The API returns the
    // recurrence as a JSON string, the timeline as an object.
    const carriers = new Map<string, { startTime: string; recurrence: RecurrenceSettings }>();
    for (const b of [...allBookings, ...bookings]) {
      if (b.recurrenceGroupId === recurrenceGroupId && b.recurrence && b.recurrence.pattern !== 'none') {
        carriers.set(String(b.id), { startTime: b.startTime, recurrence: b.recurrence });
      }
    }
    if (currentMode === 'live') {
      try {
        for (const b of await bookingsApi.getByRecurrenceGroupId(recurrenceGroupId)) {
          if (!b.recurrence) continue;
          try {
            const parsed = JSON.parse(b.recurrence) as RecurrenceSettings;
            if (parsed?.pattern && parsed.pattern !== 'none') {
              carriers.set(String(b.id), { startTime: b.startTime, recurrence: parsed });
            }
          } catch {
            // Unparseable repeat details — nothing to shorten on this occurrence.
          }
        }
      } catch (err) {
        console.error('Failed to fetch recurrence group; shortening visible occurrences only:', err);
      }
    }

    const updates = Array.from(carriers.entries())
      .filter(([, occurrence]) => new Date(occurrence.startTime).getTime() < cutoff.getTime())
      .map(([id, occurrence]) => ({
        id,
        update: {
          recurrence: {
            ...occurrence.recurrence,
            endType: 'on-date' as const,
            endDate: newEndIso,
            occurrences: undefined,
          },
        } as Partial<Booking>,
      }));

    applySeriesUpdatesInBackground(updates);

    // The stored repeat end date changed, so tooltip caches keyed by recurrenceGroupId
    // must refetch to show the new "until ..." line immediately rather than after a reload.
    invalidateRecurrenceSettings();
  };

  // Applies an edit to the remaining occurrences of a series without holding up the
  // dialog. The requests all go out together (authFetch caps how many are in flight)
  // instead of one occurrence after another, and the planner refreshes once they land.
  const applySeriesUpdatesInBackground = (updates: Array<{ id: string; update: Partial<Booking> }>) => {
    if (updates.length === 0) return;
    void Promise.allSettled(
      updates.map(({ id, update }) =>
        updateBooking(id, update).catch(err => {
          console.error(`❌ Failed to update booking ${id}:`, err);
        })
      )
    ).then(() => {
      if (currentMode === 'live') refetchLiveBookings();
    });
  };

  // The start of every occurrence that follows `booking` under its repeat options,
  // worked out as a new repeat booking's are: from the booking's own slot, skipping the
  // resource's non-working days and anything outside its contract.
  const seriesOccurrenceStartTimes = (booking: Partial<Booking>, originalBooking: Booking): string[] => {
    const merged = { ...originalBooking, ...booking };
    const seriesStart = new Date(merged.startTime);
    return clampRecurrenceDatesToContract(merged, generateRecurrenceDates(booking, originalBooking))
      .map(date => {
        const start = new Date(date);
        start.setHours(seriesStart.getHours(), seriesStart.getMinutes(), seriesStart.getSeconds(), seriesStart.getMilliseconds());
        return formatDateAsLocalISO(start);
      });
  };

  // Applies an "all future bookings" edit to the occurrences after the (already saved)
  // edited booking without holding up the dialog. The server updates them all in one
  // request: they keep their ids, occurrences edited on their own keep their own values
  // and read-only ones are left out. With `occurrenceStartTimes` the occurrences move in
  // order onto those dates, extra occurrences are removed and missing ones are created
  // here. The occurrences on screen stay locked until the request settles, and the
  // planner refreshes once.
  const applySeriesFutureInBackground = ({
    groupId,
    originalBooking,
    booking,
    editedPieceIds,
    changes,
    occurrenceStartTimes,
    newRecurrenceGroupId,
    clearRecurrence,
    editedChanges,
    editedRecurrence,
  }: {
    groupId: string;
    originalBooking: Booking;
    booking: Partial<Booking>;
    editedPieceIds: readonly string[];
    changes: Partial<Booking>;
    occurrenceStartTimes?: string[];
    newRecurrenceGroupId?: string;
    clearRecurrence?: boolean;
    editedChanges?: Partial<Booking> & { id: string };
    editedRecurrence?: string;
  }) => {
    const merged = { ...originalBooking, ...booking };
    const editedIds = new Set(editedPieceIds.map(String));
    editedIds.add(String(originalBooking.id));
    const from = new Date(originalBooking.startTime).getTime();
    const visibleFollowing = bookings.filter(b =>
      b.recurrenceGroupId === groupId
      && !editedIds.has(String(b.id))
      && new Date(b.startTime).getTime() >= from);
    if (visibleFollowing.length > 0) {
      setLockedBookingIds(new Set(visibleFollowing.map(b => String(b.id))));
    }

    const save = (async () => {
      try {
        const result = await updateSeriesFuture(groupId, {
          editedBookingId: originalBooking.id,
          editedChanges,
          editedRecurrence,
          excludeBookingIds: [...editedIds],
          fromStartTime: originalBooking.startTime,
          originalStartTime: originalBooking.startTime,
          originalEndTime: originalBooking.endTime,
          newStartTime: merged.startTime,
          newEndTime: merged.endTime,
          occurrenceStartTimes,
          newRecurrenceGroupId,
          clearRecurrence,
          changes,
          visibleOccurrences: dateRangeBookings.filter(b => b.recurrenceGroupId === groupId),
        });
        for (const removedId of result.removed) removeBookingFromCache(String(removedId));

        let created = 0;
        if (result.unmatchedStartTimes.length > 0 && newRecurrenceGroupId) {
          const duration = new Date(merged.endTime).getTime() - new Date(merged.startTime).getTime();
          const {
            id: _id,
            autoAdjustGroupId: _autoAdjustGroupId,
            recurrenceException: _recurrenceException,
            ...template
          } = merged;
          const parentId = Number(originalBooking.id);
          const missing: Omit<Booking, 'id'>[] = result.unmatchedStartTimes.map(start => {
            const startDate = new Date(start);
            return {
              ...template,
              recurrence: undefined,
              recurrenceGroupId: newRecurrenceGroupId,
              isRecurrenceParent: false,
              parentBookingId: Number.isNaN(parentId) ? undefined : parentId,
              startTime: formatDateAsLocalISO(startDate),
              endTime: formatDateAsLocalISO(new Date(startDate.getTime() + duration)),
            };
          });
          await createBatchBookings(missing);
          created = missing.length;
        }

        const skipped = result.skipped.length;
        if (result.errors.length > 0) {
          for (const error of result.errors) console.error('❌ Failed to update a following occurrence:', error);
          sonnerToast.error('Some future bookings were not updated', {
            description: `${result.errors.length} booking${result.errors.length !== 1 ? 's' : ''} could not be updated. Please try again.`,
          });
        }
        const summary = [
          `${result.updated.length} future booking${result.updated.length !== 1 ? 's' : ''} updated`,
          created > 0 ? `${created} added` : '',
          result.removed.length > 0 ? `${result.removed.length} removed` : '',
          skipped > 0 ? `${skipped} left as they were (edited on their own or read-only)` : '',
        ].filter(Boolean).join(', ');
        sonnerToast.success(`${summary}.`);
      } catch (err) {
        if (editedChanges) throw err;
        console.error('❌ Failed to update the following occurrences:', err);
        if (!handleBookingWriteRejected(err, 'modify')) {
          sonnerToast.error('Failed to update the future bookings', {
            description: 'The edited booking was saved. Please try again for the bookings after it.',
          });
        }
      } finally {
        setLockedBookingIds(new Set());
        if (currentMode === 'live') refetchLiveBookings();
      }
    })();
    if (editedChanges) return save;
    void save;
  };

  const performDemandBookingUpdate = async (
    id: string | number,
    originalBooking: Booking,
    demandBookingUpdate: Partial<Booking>,
    detailScope: 'this' | 'demand-all',
    resourceScope?: 'this' | 'all'
  ) => {
    const groupId = originalBooking.demandPlanGroupId;
    if (!groupId) {
      throw new Error('Linked demand booking is missing its demand plan group.');
    }

    if (currentMode === 'demo') {
      const groupBookings = [...new Map(
        [...allBookings, ...bookings]
          .filter(booking => booking.demandPlanGroupId === groupId)
          .map(booking => [String(booking.id), booking]),
      ).values()];
      const resourceChanged = demandBookingUpdate.resourceId !== undefined
        && String(demandBookingUpdate.resourceId) !== String(originalBooking.resourceId);
      const update = { ...demandBookingUpdate };
      let reassignedCount = 0;
      // "All linked" edits and reassignments cover the edited booking and its future
      // siblings only, so each of those bookings — and no earlier one — gets its own
      // history entry.
      const anchorStart = new Date(originalBooking.startTime).getTime();
      const futureGroupBookings = groupBookings.filter(
        booking => new Date(booking.startTime).getTime() >= anchorStart,
      );

      if (resourceChanged && resourceScope === 'all') {
        const resourceId = String(demandBookingUpdate.resourceId);
        await Promise.all(futureGroupBookings.map(async booking => {
          await updateBooking(booking.id, { resourceId, resourceIds: [resourceId] });
          reassignedCount++;
        }));
        delete update.resourceId;
        delete update.resourceIds;
      } else if (resourceChanged && resourceScope === 'this') {
        await updateBooking(String(id), update);
        delete update.resourceId;
        delete update.resourceIds;
      }
      if (!resourceChanged) {
        delete update.resourceId;
        delete update.resourceIds;
      }

      const originalTemporal = {
        startTime: originalBooking.startTime,
        endTime: originalBooking.endTime,
      };
      const updatedTemporal = {
        startTime: update.startTime ?? originalBooking.startTime,
        endTime: update.endTime ?? originalBooking.endTime,
      };
      const temporalPatches = update.startTime !== undefined || update.endTime !== undefined
        ? propagateDemandBookingTemporalUpdate(
          originalTemporal,
          updatedTemporal,
          futureGroupBookings,
        )
        : [];
      delete update.startTime;
      delete update.endTime;
      // The editor's own hour count beats the elapsed span derived from the shifted
      // timestamps, which would otherwise cost a day-based booking at 24h.
      const editedDurationHours = update.durationHours;
      if (temporalPatches.length > 0) delete update.durationHours;
      const applyToAll = detailScope === 'demand-all';
      const targets = applyToAll ? futureGroupBookings : [originalBooking];
      const patchesById = new Map(temporalPatches.map(patch => [String(patch.id), patch]));
      const detailUpdates = targets.map(booking => {
        const temporalPatch = patchesById.get(String(booking.id));
        const temporalUpdate = temporalPatch
          ? {
            startTime: temporalPatch.startTime,
            endTime: temporalPatch.endTime,
            ...(editedDurationHours !== undefined
              ? { durationHours: editedDurationHours }
              : temporalPatch.durationHours !== undefined
                ? { durationHours: temporalPatch.durationHours }
                : {}),
          }
          : {};
        const changes = {
          ...(applyToAll || String(booking.id) === String(id) ? update : {}),
          ...temporalUpdate,
        };
        return Object.keys(changes).length > 0 ? { booking, changes } : undefined;
      }).filter((entry): entry is {
        booking: Booking;
        changes: Partial<Booking>;
      } => entry !== undefined);
      await Promise.all(detailUpdates.map(({ booking, changes }) => updateBooking(booking.id, changes)));
      const updatedCount = detailUpdates.length;

      if (!applyToAll && (updatedCount > 0 || resourceChanged)) {
        sonnerToast.success('Open Demand Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
      } else if (applyToAll && updatedCount > 0) {
        sonnerToast.success('Linked Demand Bookings Updated', {
          description: `${updatedCount} linked demand booking${updatedCount === 1 ? '' : 's'} updated.`,
        });
      } else if (reassignedCount > 0) {
        sonnerToast.success('Linked Demand Bookings Reassigned', {
          description: `${reassignedCount} linked demand booking${reassignedCount === 1 ? '' : 's'} reassigned.`,
        });
      }
      return;
    }

    const resourceChanged = demandBookingUpdate.resourceId !== undefined
      && String(demandBookingUpdate.resourceId) !== String(originalBooking.resourceId);
    const update = { ...demandBookingUpdate };
    let reassignResponse: Awaited<ReturnType<typeof bookingsApi.reassignDemandPlanGroup>> | undefined;

    if (resourceChanged && resourceScope === 'all') {
      reassignResponse = await bookingsApi.reassignDemandPlanGroup(groupId, {
        resourceId: Number(demandBookingUpdate.resourceId),
        fromStartTime: originalBooking.startTime,
      });
      if (reassignResponse.skipped.length > 0) {
        sonnerToast.warning('Some linked demand bookings stayed on their original resource', {
          description: `${reassignResponse.skipped.length} booking${reassignResponse.skipped.length === 1 ? '' : 's'} could not move because the target resource was already booked.`,
        });
      }
      if (reassignResponse.errors.length > 0) {
        sonnerToast.error('Some linked demand bookings could not be reassigned', {
          description: `${reassignResponse.errors.length} booking${reassignResponse.errors.length === 1 ? '' : 's'} failed.`,
        });
      }
      delete update.resourceId;
      delete update.resourceIds;
    }

    if (detailScope === 'demand-all') {
      // A single-resource reassignment still needs to persist the anchor's resource
      // before applying the detail/time changes to the complete group.
      if (resourceChanged && resourceScope === 'this') {
        await updateBooking(String(id), update);
        delete update.resourceId;
        delete update.resourceIds;
      }

      const groupUpdate = buildDemandPlanGroupUpdatePayload(id, originalBooking, update);
      const hasGroupUpdate = Object.keys(groupUpdate.changes).length > 0
        || groupUpdate.originalStartTime !== undefined;
      if (hasGroupUpdate) {
        const response = await bookingsApi.updateDemandPlanGroup(groupId, groupUpdate);
        sonnerToast.success('Linked Demand Bookings Updated', {
          description: `${response.updated.length} linked demand booking${response.updated.length === 1 ? '' : 's'} updated.`,
        });
        if (response.skipped.length > 0 || response.errors.length > 0) {
          sonnerToast.warning('Some linked demand bookings were not updated', {
            description: `${response.skipped.length + response.errors.length} booking${response.skipped.length + response.errors.length === 1 ? '' : 's'} skipped or failed.`,
          });
        }
      } else if (reassignResponse) {
        sonnerToast.success('Linked Demand Bookings Reassigned', {
          description: `${reassignResponse.updated.length} linked demand booking${reassignResponse.updated.length === 1 ? '' : 's'} reassigned.`,
        });
      }
    } else {
      if (resourceChanged && resourceScope === 'all') {
        // The group reassignment already persisted the resource for every booking.
        delete update.resourceId;
        delete update.resourceIds;
      }
      await updateBooking(String(id), update);
      sonnerToast.success('Open Demand Booking Updated', {
        description: 'The booking has been successfully updated.',
      });
    }

    if (currentMode === 'live') refetchLiveBookings();
  };

  const handleDemandMoveScope = (scope: 'this' | 'all') => {
    if (!bookingToMove) return;

    setDemandMoveScope(scope);
    setShowDemandMoveConfirm(false);
    setShowMoveDialog(true);
  };

  const handleCancelDemandMove = () => {
    setShowDemandMoveConfirm(false);
    setBookingToMove(null);
    setDemandMoveScope(null);
  };

  const handleUpdateBooking = async (id: string | number, booking: Partial<Booking>) => {
    setIsSavingBooking(true);
    
    try {
      // If status is being changed and there's an active status filter, add the new status to the filter
      if (booking.status && filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(booking.status)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, booking.status]);
      }
      
      // Find the original booking
      // Resolved against every known booking: a piece of a split booking can lie outside
      // the range currently rendered, and it is still the booking being edited.
      const originalBooking = bookingsById.get(String(id)) ?? bookings.find(b => String(b.id) === String(id));

      if (!originalBooking) {
        console.error('❌ Original booking not found:', id);
        sonnerToast.error('Booking not found', {
          description: 'Could not find the original booking.',
        });
        return;
      }

      // Only a change of dates or resources is held to the contract, so other edits
      // to a booking that predates its resource's contract still save.
      const mergedForContract = { ...originalBooking, ...booking };
      const coveredDays = (b: Partial<Booking>) => {
        const start = new Date(b.startTime ?? '');
        const end = new Date(b.endTime ?? '');
        const endsAtMidnight = end > start && end.getHours() === 0 && end.getMinutes() === 0
          && end.getSeconds() === 0 && end.getMilliseconds() === 0;
        const lastDay = endsAtMidnight ? new Date(end.getTime() - 1) : end;
        return `${start.toDateString()}|${lastDay.toDateString()}`;
      };
      const assignedIds = (b: Partial<Booking>) =>
        bookingResources(b).map(resource => String(resource.id)).sort().join(',');
      const scheduleChanged = coveredDays(mergedForContract) !== coveredDays(originalBooking)
        || assignedIds(mergedForContract) !== assignedIds(originalBooking);
      const updateContractError = scheduleChanged ? contractRefusal(mergedForContract) : null;
      if (updateContractError) {
        sonnerToast.error('Cannot Save Booking', { description: updateContractError });
        return;
      }

      // Determine if recurrence was removed (user cleared recurring info)
      const recurrenceRemoved = originalBooking.recurrenceGroupId && 
        (!booking.recurrence || booking.recurrence.pattern === 'none');

      // Demand-plan groups have their own scope and update endpoint. They must
      // never enter the recurrence edit-all-future flow.
      if (isLinkedDemandBooking(originalBooking) && !originalBooking.recurrenceGroupId) {
        const demandBookingUpdate = { ...booking };
        delete demandBookingUpdate.recurrence;
        delete demandBookingUpdate.recurrenceGroupId;
        delete demandBookingUpdate.parentBookingId;
        delete demandBookingUpdate.isRecurrenceParent;
        delete demandBookingUpdate.recurrencePending;

        const demandDetailScope = editScope === 'demand-all' ? 'demand-all' : 'this';
        const resourceChanged = demandBookingUpdate.resourceId !== undefined
          && String(demandBookingUpdate.resourceId) !== String(originalBooking.resourceId);
        if (shouldPromptDemandReassign(originalBooking, resourceChanged, undefined)) {
          setPendingDemandBookingUpdate({
            id,
            originalBooking,
            update: demandBookingUpdate,
            detailScope: demandDetailScope,
          });
          setShowDemandReassignConfirm(true);
          throw new BookingSaveCancelledError();
        }

        await performDemandBookingUpdate(
          id,
          originalBooking,
          demandBookingUpdate,
          demandDetailScope
        );
        return;
      }

      // The form was opened on a split booking, so the edit is of the one booking its
      // pieces make up: a changed span or resource is saved on the piece that starts first
      // for the server to re-plan the group, and changed details go to every piece.
      const groupPieces = autoAdjustGroupPieces(
        originalBooking,
        await readAutoAdjustGroupPieces([originalBooking]),
      );

      // The booking the form was opened on, which is what the save is read against: the
      // span it holds is the span the user was shown, so a save carrying it back is no
      // move however the pieces of the booking read on the client.
      const editedFrom = bookingModalMode === 'edit'
        && editingBooking
        && String(editingBooking.id) === String(id)
        ? editingBooking
        : undefined;

      // The edited booking saved as the one booking it is: a split booking's changed span
      // or resource goes to the piece that starts first, for the server to re-plan the
      // group, and changed details go to every piece. The pieces the re-plan replaced are
      // still in the store the timeline draws from, so it is re-synced afterwards.
      const saveEditedBooking = async (update: Partial<Booking>) => {
        if (groupPieces.length < 2) {
          await updateBooking(String(id), update);
          return;
        }

        const saves = planAutoAdjustGroupEdit(groupPieces, update, editedFrom);
        if (saves.length === 1) {
          await updateBooking(String(saves[0].id), saves[0].update);
        } else if (saves.length > 1) {
          await updateBookingsBatch(saves.map(save => ({ id: String(save.id), ...save.update })));
        }
        setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
      };

      // A split booking is several rows, so a change to its place in the series goes to
      // every piece it is saved as now, including any a re-plan just wrote.
      const saveEditedPiecesMembership = async (membership: Partial<Booking>) => {
        if (groupPieces.length < 2) return;
        const pieces = await readAutoAdjustGroupPieces(groupPieces);
        const current = autoAdjustGroupPieces(groupPieces[0], pieces);
        for (const piece of current.length > 0 ? current : groupPieces) {
          await updateBooking(String(piece.id), membership);
        }
      };

      if (groupPieces.length > 1 && !originalBooking.recurrenceGroupId) {
        await saveEditedBooking(booking);

        // A re-plan removes the pieces it replaced, and those rows are still in the
        // date-range cache the timeline draws from, so the range is re-read: without it a
        // piece the save deleted stays on screen until a refresh.
        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
        return;
      }

      // ── Case A: Non-recurring booking → becoming recurring ──
      if (!originalBooking.recurrenceGroupId && booking.recurrence && booking.recurrence.pattern !== 'none') {
        const type = settings.bookingTypes.find(type => String(type.id) === String(booking.bookingTypeId ?? originalBooking.bookingTypeId));
        const lookupError = validateUserCharBooking(type, { ...originalBooking, ...booking });
        if (lookupError) {
          sonnerToast.error('Cannot Save Booking', { description: lookupError });
          throw new BookingSaveCancelledError();
        }

        const recurrenceGroupId = crypto.randomUUID();
        if (currentMode === 'live' && type?.fieldConfigurations.some(field =>
          userCharIndex(field.fieldName) && (field.isVisible || field.showOnCustomTab))) {
          await applySeriesFutureInBackground({
            groupId: crypto.randomUUID(), originalBooking, booking,
            editedPieceIds: groupPieces.map(piece => String(piece.id)),
            changes: {}, editedChanges: { ...booking, id: String(id) },
            editedRecurrence: JSON.stringify(booking.recurrence),
            occurrenceStartTimes: seriesOccurrenceStartTimes(booking, originalBooking),
            newRecurrenceGroupId: recurrenceGroupId,
          });
          invalidateRecurrenceSettings();
          return;
        }
        const updatedOriginal: Partial<Booking> = {
          ...booking,
          recurrenceGroupId,
          isRecurrenceParent: true,
        };
        await updateBooking(String(id), updatedOriginal);

        const occurrences = clampRecurrenceDatesToContract(
          { ...originalBooking, ...booking }, generateRecurrenceDates(booking, originalBooking),
        );
        
        if (occurrences.length > 0) {
          sonnerToast.success(
            `Booking updated successfully. ${occurrences.length} recurring booking${occurrences.length !== 1 ? 's' : ''} will be created in the background.`
          );

          const mergedBooking = { ...originalBooking, ...booking };
          const originalStart = new Date(mergedBooking.startTime);
          const originalEnd = new Date(mergedBooking.endTime);
          const duration = originalEnd.getTime() - originalStart.getTime();

          const childBookings: Omit<Booking, 'id'>[] = occurrences.map(date => {
            const newStart = new Date(date);
            newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
            const newEnd = new Date(newStart.getTime() + duration);
            return {
              ...mergedBooking,
              recurrence: undefined,
              startTime: formatDateAsLocalISO(newStart),
              endTime: formatDateAsLocalISO(newEnd),
              recurrenceGroupId,
              isRecurrenceParent: false,
            };
          });

          void createBatchBookings(childBookings).catch(err => {
            console.error('❌ Server batch creation (from update) failed:', err);
          });
        } else {
          sonnerToast.success('Booking Updated', {
            description: 'The booking has been successfully updated.',
          });
        }

        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case B: Recurring booking — editScope = 'this' (only this booking) ──
      if (originalBooking.recurrenceGroupId && editScope === 'this') {

        // A split occurrence is several rows, and they are all the edited booking rather
        // than an occurrence following it.
        const editedPieceIds = new Set(groupPieces.map(piece => String(piece.id)));

        // The last occurrence of a series opens straight in "this booking" mode (there is
        // nothing after it to choose between), so changed repeat settings still have to
        // extend the series from here.
        if (!recurrenceRemoved && booking.recurrence) {
          const seriesOccurrences = await getSeriesOccurrences(originalBooking.recurrenceGroupId);
          const isLastOccurrence = !seriesOccurrences.some(
            o => !editedPieceIds.has(String(o.id)) && new Date(o.startTime) > new Date(originalBooking.startTime)
          );
          if (isLastOccurrence) {
            const seriesRecurrence = await getSeriesRecurrence(originalBooking);
            if (hasRecurrenceOptionsChanged(seriesRecurrence, booking.recurrence)) {
              // The edited booking keeps its id and starts a series of its own from the new
              // options; the earlier occurrences keep theirs, now ending the day before it.
              const newRecurrenceGroupId = crypto.randomUUID();
              const type = settings.bookingTypes.find(type => String(type.id) === String(booking.bookingTypeId ?? originalBooking.bookingTypeId));
              if (currentMode === 'live' && type?.fieldConfigurations.some(field =>
                userCharIndex(field.fieldName) && (field.isVisible || field.showOnCustomTab))) {
                const rootId = String(groupPieces[0]?.id ?? id);
                const editedPatch = groupPieces.length > 1
                  ? planAutoAdjustGroupEdit(groupPieces, booking, editedFrom).find(save => String(save.id) === rootId)?.update ?? booking
                  : booking;
                await applySeriesFutureInBackground({
                  groupId: originalBooking.recurrenceGroupId, originalBooking, booking,
                  editedPieceIds: [...editedPieceIds],
                  changes: seriesDetailChanges(originalBooking, booking),
                  editedChanges: { ...editedPatch, id: rootId },
                  editedRecurrence: JSON.stringify(booking.recurrence),
                  occurrenceStartTimes: seriesOccurrenceStartTimes(booking, originalBooking),
                  newRecurrenceGroupId,
                });
                await shortenSeriesRepeatEnd(originalBooking.recurrenceGroupId, originalBooking.startTime);
                invalidateRecurrenceSettings();
                return;
              }
              const membership: Partial<Booking> = {
                recurrenceGroupId: newRecurrenceGroupId,
                isRecurrenceParent: false,
                parentBookingId: Number(id) || undefined,
              };
              await saveEditedBooking({
                ...booking,
                recurrence: booking.recurrence,
                recurrenceGroupId: newRecurrenceGroupId,
                isRecurrenceParent: true,
                parentBookingId: undefined,
              });
              await saveEditedPiecesMembership(membership);
              await shortenSeriesRepeatEnd(originalBooking.recurrenceGroupId, originalBooking.startTime);
              applySeriesFutureInBackground({
                groupId: originalBooking.recurrenceGroupId,
                originalBooking,
                booking,
                editedPieceIds: [...editedPieceIds],
                changes: {},
                occurrenceStartTimes: seriesOccurrenceStartTimes(booking, originalBooking),
                newRecurrenceGroupId,
              });
              sonnerToast.success('Booking updated. The repeat bookings are being updated in the background.');
              return;
            }
          }
        }

        if (recurrenceRemoved) {
          // The last occurrence opens straight in "this booking" mode, so removing the
          // repeat here mirrors the "all future" removal: the earlier occurrences keep the
          // series but now end the day before this booking. A middle occurrence explicitly
          // chosen as "this booking" only detaches itself and leaves the rest untouched.
          const seriesOccurrences = await getSeriesOccurrences(originalBooking.recurrenceGroupId);
          const isLastOccurrence = !seriesOccurrences.some(
            o => !editedPieceIds.has(String(o.id)) && new Date(o.startTime) > new Date(originalBooking.startTime)
          );

          // User removed recurrence → convert this booking to a normal (non-recurring) booking
          await saveEditedBooking({
            ...booking,
            recurrenceGroupId: undefined,
            isRecurrenceParent: false,
            parentBookingId: undefined,
            recurrence: undefined,
          });

          if (isLastOccurrence) {
            await shortenSeriesRepeatEnd(originalBooking.recurrenceGroupId, originalBooking.startTime);
          }
        } else {
          // Resources added while editing only this occurrence belong to that
          // occurrence alone, so they get their own booking for the occurrence's
          // dates rather than joining the series record (which would give them
          // the whole series' recurrence details).
          const resourceSplit = splitOccurrenceResourceIds(originalBooking, booking.resourceIds);
          const scopeAddedResources = resourceSplit.addedResourceIds.length > 0
            && resourceSplit.keptResourceIds.length > 0;

          // The occurrence now holds values of its own, so "all future" edits of the series
          // leave it as it is.
          if (scopeAddedResources) {
            await updateBooking(String(id), { ...buildOccurrenceUpdate(booking, resourceSplit), recurrenceException: true });
          } else {
            await saveEditedBooking({ ...booking, recurrenceException: true });
          }
          await saveEditedPiecesMembership({ recurrenceException: true });

          if (scopeAddedResources) {
            await addBooking(
              buildOccurrenceScopedBooking(originalBooking, booking, resourceSplit.addedResourceIds)
            );
          }
        }

        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case C: Recurring booking — editScope = 'all-future' ──
      // The series is never recreated: the edited booking is saved here, and the server
      // updates every occurrence after it in one request, keeping their ids.
      if (originalBooking.recurrenceGroupId && editScope === 'all-future') {
        const groupId = originalBooking.recurrenceGroupId;
        const editedPieceIds = groupPieces.length > 1
          ? groupPieces.map(piece => String(piece.id))
          : [String(id)];
        const seriesRecurrence = recurrenceRemoved ? undefined : await getSeriesRecurrence(originalBooking);
        const editKind = recurrenceRemoved
          ? 'recurrence-removed'
          : classifySeriesEdit(originalBooking, booking, seriesRecurrence);

        // What every following occurrence takes besides its place in the series: the
        // changed details, or the whole form when the schedule or resources changed.
        const followingChanges = (): Partial<Booking> => {
          const details = seriesDetailChanges(originalBooking, booking);
          if (!hasScheduleChanged(originalBooking, booking)) return details;
          const { startTime: _startTime, endTime: _endTime, ...patch } = seriesDetailPatch(booking);
          const { userChar: _userChar, userCharOptionIds: _ids, ...other } = patch;
          return { ...other, ...(details.userChar ? { userChar: details.userChar, userCharOptionIds: details.userCharOptionIds } : {}) };
        };

        const lookupType = settings.bookingTypes.find(type => String(type.id) === String(booking.bookingTypeId ?? originalBooking.bookingTypeId));
        const usesLookups = lookupType?.fieldConfigurations.some(field =>
          userCharIndex(field.fieldName) && (field.isVisible || field.showOnCustomTab));
        if (currentMode === 'live' && usesLookups) {
          const rootId = String(groupPieces[0]?.id ?? id);
          const newGroup = editKind === 'recurrence-changed' ? crypto.randomUUID() : undefined;
          const editedPatch = editKind === 'details-only'
            ? seriesDetailChanges(originalBooking, booking)
            : groupPieces.length > 1
              ? planAutoAdjustGroupEdit(groupPieces, booking, editedFrom).find(save => String(save.id) === rootId)?.update ?? booking
              : booking;
          await applySeriesFutureInBackground({
            groupId, originalBooking, booking, editedPieceIds,
            changes: followingChanges(),
            editedChanges: { ...editedPatch, id: rootId },
            editedRecurrence: newGroup && booking.recurrence ? JSON.stringify(booking.recurrence) : undefined,
            occurrenceStartTimes: newGroup ? seriesOccurrenceStartTimes(booking, originalBooking) : undefined,
            newRecurrenceGroupId: newGroup,
            clearRecurrence: editKind === 'recurrence-removed',
          });
          if (newGroup || editKind === 'recurrence-removed') {
            await shortenSeriesRepeatEnd(groupId, originalBooking.startTime);
            invalidateRecurrenceSettings();
          }
          return;
        }

        if (editKind === 'recurrence-removed') {
          // Removing the repeat keeps the edited booking and every one after it as
          // standalone bookings; the earlier occurrences keep the series, now ending the
          // day before this booking.
          const standalone: Partial<Booking> = {
            recurrenceGroupId: undefined,
            isRecurrenceParent: false,
            parentBookingId: undefined,
            recurrence: undefined,
          };
          await saveEditedBooking({ ...booking, ...standalone });
          await saveEditedPiecesMembership(standalone);
          await shortenSeriesRepeatEnd(groupId, originalBooking.startTime);
          applySeriesFutureInBackground({
            groupId,
            originalBooking,
            booking,
            editedPieceIds,
            changes: followingChanges(),
            clearRecurrence: true,
          });
          sonnerToast.success('Booking updated. The future bookings are being kept as standalone bookings in the background.');
          return;
        }

        // New repeat options, or a start moved to another day, place the following
        // occurrences anew: the dates are worked out from the options as for a new repeat
        // booking, and the edited booking heads a series of its own from here.
        const startDayMoved = !!booking.startTime
          && new Date(booking.startTime).toDateString() !== new Date(originalBooking.startTime).toDateString();
        if (booking.recurrence && (editKind === 'recurrence-changed' || startDayMoved)) {
          const newRecurrenceGroupId = crypto.randomUUID();
          // A start moved by hand to another day takes the repeat day with it, as a repeat
          // booking created on that day would repeat on it.
          const recurrence = editKind !== 'recurrence-changed' && booking.startTime
            ? shiftRecurrenceWithStart(booking.recurrence, originalBooking.startTime, booking.startTime)
            : booking.recurrence;
          const repeatBooking: Partial<Booking> = { ...booking, recurrence };
          // As when a repeat booking is created, a slot the options don't produce is not
          // kept: the edited booking moves to the first date they do produce.
          let edited = repeatBooking;
          let occurrenceStartTimes = seriesOccurrenceStartTimes(repeatBooking, originalBooking);
          const mergedEdit = { ...originalBooking, ...repeatBooking };
          if (occurrenceStartTimes.length > 0 && !repeatKeepsOwnSlot(mergedEdit)) {
            const [firstStart, ...rest] = occurrenceStartTimes;
            const duration = new Date(mergedEdit.endTime).getTime() - new Date(mergedEdit.startTime).getTime();
            edited = {
              ...repeatBooking,
              startTime: firstStart,
              endTime: formatDateAsLocalISO(new Date(new Date(firstStart).getTime() + duration)),
            };
            occurrenceStartTimes = rest;
          }
          await saveEditedBooking({
            ...edited,
            recurrence,
            recurrenceGroupId: newRecurrenceGroupId,
            isRecurrenceParent: true,
            parentBookingId: undefined,
          });
          await saveEditedPiecesMembership({
            recurrenceGroupId: newRecurrenceGroupId,
            isRecurrenceParent: false,
            parentBookingId: Number(id) || undefined,
          });
          await shortenSeriesRepeatEnd(groupId, originalBooking.startTime);
          applySeriesFutureInBackground({
            groupId,
            originalBooking,
            booking: edited,
            editedPieceIds,
            changes: followingChanges(),
            occurrenceStartTimes,
            newRecurrenceGroupId,
          });
          sonnerToast.success('Booking updated. The future bookings are being updated in the background.');
          return;
        }

        if (editKind === 'details-only') {
          // Nothing that places the occurrences changed, so only the changed details are
          // patched onto the edited occurrence and every one after it.
          const details = seriesDetailChanges(originalBooking, booking);
          if (Object.keys(details).length === 0) {
            sonnerToast.success('Booking Updated', {
              description: 'Nothing was changed, so the bookings were left as they are.',
            });
            return;
          }

          if (canApplyAsBatchPatch(details)) {
            const { errors } = await updateBookingsBatch(
              editedPieceIds.map(editedId => ({ ...details, id: editedId }))
            );
            if (errors.length > 0) throw new Error(errors.join('; '));
          } else {
            await saveEditedBooking(details);
          }

          applySeriesFutureInBackground({ groupId, originalBooking, booking, editedPieceIds, changes: details });
          sonnerToast.success('Booking updated. The future bookings are being updated in the background.');
          return;
        }

        // The occurrences keep their dates and take the edited booking's new time of day,
        // length, resources and details.
        await saveEditedBooking(booking);
        applySeriesFutureInBackground({
          groupId,
          originalBooking,
          booking,
          editedPieceIds,
          changes: followingChanges(),
        });
        sonnerToast.success('Booking updated. The future bookings are being updated in the background.');
        return;
      }
      
      // ── Case D: Multi-resource booking update (non-recurring) ──
      // With the single-booking model, just update the one booking record.
      // BookingResource entries are managed by the updateBooking context function.
      if (booking.resourceIds && booking.resourceIds.length > 1) {
        await updateBooking(String(id), booking);

        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: `Successfully updated booking across ${booking.resourceIds.length} resources.`,
        });
      } else {
        // ── Case E: Single resource, non-recurring update ──
        await updateBooking(String(id), booking);
        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
      }
    } catch (error) {
      // The user declined a confirmation raised mid-save — let the form handle it.
      if (error instanceof BookingSaveCancelledError) throw error;
      console.error('❌ Error updating booking:', error);
      // A booking the API refused for a reason of its own goes back to the form, which
      // stays open with the reason in a dialog so the dates or resources can be corrected.
      if (isBookingValidationError(error)) throw error;
      if (!handleBookingWriteRejected(error, 'modify')) {
        sonnerToast.error('Failed to update booking', {
          description: 'Please try again.',
        });
      }
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Helper: check if a date is a non-working day for a given resource
  const isNonWorkingDayForResource = (resource: Resource | undefined, date: Date): boolean => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    if (resource?.useCustomWorkingProfile && resource.customWorkingProfile) {
      const hours = resource.customWorkingProfile[dayKey] || 0;
      return hours === 0;
    }

    const hours = settings.globalWorkingProfile?.[dayKey] || 0;
    return hours === 0;
  };

  /**
   * Whether a resource can take a booking over a time window: its contract,
   * working profile, bank holidays and absences. Overlapping the resource's
   * other bookings is allowed, as it is when creating a booking. Used by every
   * path that moves a booking onto a resource, so a drag, the Move dialog and an
   * assignment all refuse the same drops.
   */
  const checkResourceAvailability = (
    resource: Resource,
    startTime: Date,
    endTime: Date,
  ): ResourceAvailabilityResult => canAssignBookingToResource({
    resource,
    startTime,
    endTime,
    globalWorkingProfile: settings.globalWorkingProfile,
    absences,
    bankHolidays: mockBankHolidays,
    businessHoursStart: settings.businessHoursStart,
    businessHoursEnd: settings.businessHoursEnd,
  });

  const bookingResources = (booking: Partial<Booking>): Resource[] => {
    const ids = booking.resourceIds?.length
      ? booking.resourceIds
      : booking.resourceId !== undefined && booking.resourceId !== null ? [booking.resourceId] : [];
    return ids
      .map(resourceId => resources.find(resource => String(resource.id) === String(resourceId)))
      .filter((resource): resource is Resource => Boolean(resource));
  };

  /** Why the booking's own slot falls outside a resource's contract, or null. */
  const contractRefusal = (booking: Partial<Booking>): string | null => {
    if (!booking.startTime || !booking.endTime) return null;
    const startTime = new Date(booking.startTime);
    const endTime = new Date(booking.endTime);
    for (const resource of bookingResources(booking)) {
      const result = checkContractWindow(resource, startTime, endTime);
      if (!result.allowed) return result.message ?? `${resource.name} is outside their contract`;
    }
    return null;
  };

  /**
   * Recurrence dates cut off at the contract window of every resource the series
   * assigns. Each date is the occurrence's start day; its span is the series slot's.
   * A toast reports how many occurrences were left out.
   */
  const clampRecurrenceDatesToContract = (booking: Partial<Booking>, dates: Date[]): Date[] => {
    const seriesResources = bookingResources(booking).filter(hasContractWindow);
    if (dates.length === 0 || seriesResources.length === 0 || !booking.startTime || !booking.endTime) return dates;
    const seriesStart = new Date(booking.startTime);
    const duration = new Date(booking.endTime).getTime() - seriesStart.getTime();
    const occurrences = dates.map(date => {
      const start = new Date(date);
      start.setHours(seriesStart.getHours(), seriesStart.getMinutes(), seriesStart.getSeconds(), seriesStart.getMilliseconds());
      return { date, start, end: new Date(start.getTime() + duration) };
    });
    const kept = seriesResources.reduce(
      (remaining, resource) => clampDatesToContract(resource, remaining).kept,
      occurrences,
    );
    const droppedCount = occurrences.length - kept.length;
    if (droppedCount > 0) {
      const names = seriesResources.map(resource => resource.name).join(', ');
      sonnerToast.info('Series cut off at the contract', {
        description: `${droppedCount} occurrence${droppedCount === 1 ? '' : 's'} outside ${names}'s contract `
          + `${droppedCount === 1 ? 'was' : 'were'} not created.`,
      });
    }
    return kept.map(occurrence => occurrence.date);
  };

  /**
   * Whether a refused window is one the server plans away. A booking type that auto-adjusts
   * is saved over the working days it lands on, so non-working days and bank holidays under
   * the drop are skipped instead of blocking it; the contract window still refuses.
   */
  const isPlannedAwayByAutoAdjust = (
    booking: Booking,
    availability: ResourceAvailabilityResult,
  ): boolean => (
    (availability.reason === 'non-working-day' || availability.reason === 'day-unavailable')
    && autoAdjustsAroundNonWorkingDays(settings.bookingTypes.find(
      type => String(type.id) === String(booking.bookingTypeId),
    ))
  );

  // Whether a date is one the repeat rule itself would produce. The booking the user
  // filled in is only kept as the series' first occurrence when it lands on a day the
  // pattern covers: a weekly repeat on Wednesdays started from a Monday slot must not
  // create that Monday. Daily repeats, and weekly repeats with no weekday selection,
  // take their slot from the booking itself, so every date matches.
  const matchesRecurrenceRule = (recurrence: RecurrenceSettings | undefined, date: Date): boolean => {
    if (!recurrence) return true;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    if (recurrence.pattern === 'weekly') {
      if (!recurrence.daysOfWeek || recurrence.daysOfWeek.length === 0) return true;
      return recurrence.daysOfWeek.some(day => day.toLowerCase() === dayKey);
    }

    if (recurrence.pattern === 'monthly') {
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

      if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
        const { occurrence, weekday } = recurrence.relativeWeekday;
        if (weekday.toLowerCase() !== dayKey) return false;
        if (occurrence === 'last') return date.getDate() + 7 > lastDayOfMonth;
        const ordinals: Record<string, number> = { first: 1, second: 2, third: 3, fourth: 4 };
        return Math.ceil(date.getDate() / 7) === (ordinals[occurrence] ?? 1);
      }

      // "Day N of the month", clamped the same way the generator clamps it.
      const targetDay = Math.min(recurrence.dayOfMonth ?? date.getDate(), lastDayOfMonth);
      return date.getDate() === targetDay;
    }

    return true;
  };

  // Whether a repeat booking keeps the slot it was filled in on as its first occurrence:
  // the slot has to fall within the repeat's date range, on a day the rule produces and
  // on a working day of the resource.
  const repeatKeepsOwnSlot = (booking: Partial<Booking>): boolean => {
    if (!booking.startTime) return true;
    const bookingStart = new Date(booking.startTime);
    const dayOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const seriesStart = booking.recurrence?.startDate ? new Date(booking.recurrence.startDate) : undefined;
    const seriesEnd = booking.recurrence?.endDate ? new Date(booking.recurrence.endDate) : undefined;
    const resourceId = booking.resourceId || booking.resourceIds?.[0];
    const resource = resourceId ? resources.find(r => String(r.id) === String(resourceId)) : undefined;
    return (!seriesStart || dayOf(bookingStart) >= dayOf(seriesStart))
      && (!seriesEnd || dayOf(bookingStart) <= dayOf(seriesEnd))
      && matchesRecurrenceRule(booking.recurrence, bookingStart)
      && !isNonWorkingDayForResource(resource, bookingStart);
  };

  // Generate recurrence dates based on recurrence settings
  const generateRecurrenceDates = (booking: Partial<Booking>, originalBooking: Booking): Date[] =>
    generateRecurrenceDatesPure(booking, originalBooking, {
      resources,
      isNonWorkingDay: isNonWorkingDayForResource,
    });

  // Builds the normalized booking update without side effects.
  function buildBookingTimeUpdate({
    booking,
    newResource,
    newStartTime,
    newEndTime,
    isResize,
    originalResourceId,
  }: {
    booking: Booking;
    newResource: Resource;
    newStartTime: Date;
    newEndTime: Date;
    isResize: boolean;
    originalResourceId?: string | number;
  }): {
    bookingUpdate: Partial<Booking>;
    adjustedStartTime: Date;
    adjustedEndTime: Date;
    originalStartTime: Date;
    originalEndTime: Date;
    hasChanged: boolean;
  } {
    const newResourceIdStr = String(newResource.id);
    const originalResourceIdStr = String(originalResourceId ?? booking.resourceId ?? '');
    const stripTzSuffix = (t: string) => t.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    const originalStartTime = new Date(stripTzSuffix(booking.startTime));
    const originalEndTime = new Date(stripTzSuffix(booking.endTime));

    // For resize operations: pin the non-resized edge to the original stored
    // value.  Bryntum may round or shift the unchanged edge during snap
    // (e.g. 23:59:59.999 → next-day midnight), which causes unintended
    // date drift.  Compare deltas to detect which edge the user dragged.
    let pinnedStartTime = newStartTime;
    let pinnedEndTime = newEndTime;
    if (isResize) {
      const startDelta = Math.abs(newStartTime.getTime() - originalStartTime.getTime());
      const endDelta = Math.abs(newEndTime.getTime() - originalEndTime.getTime());
      if (startDelta <= endDelta) {
        // End was resized → preserve original start exactly
        pinnedStartTime = new Date(originalStartTime);
      } else {
        // Start was resized → preserve original end exactly
        pinnedEndTime = new Date(originalEndTime);
      }
    }

    // Apply date/time logic based on duration type
    // For non-hourly bookings: store start at 00:00:00, end at 23:59:59.999
    // For hourly bookings: use exact times from Bryntum
    let adjustedStartTime: Date;
    let adjustedEndTime: Date;

    // Determine if this is a full-day type booking (not hourly)
    const isFullDayType = booking.durationType === 'full-day' ||
                          booking.durationType === 'half-day-am' ||
                          booking.durationType === 'half-day-pm' ||
                          booking.durationType === 'half-day';

    const isHalfDayType = booking.durationType === 'half-day' ||
                          booking.durationType === 'half-day-am' ||
                          booking.durationType === 'half-day-pm';

    const originalStart = new Date(stripTzSuffix(booking.startTime));
    const originalEnd = new Date(stripTzSuffix(booking.endTime));
    const hasSpecificTime = originalStart.getHours() !== 0 || originalStart.getMinutes() !== 0;

    // Determine if the timeline is currently showing hourly granularity based on
    // calendarView and zoomLevel:
    // - Day view: always hourly (zoomLevel 0 = hourly, zoomLevel 1 = 15-min)
    // - Week/Month view: zoomLevel 0 = daily, zoomLevel >= 1 = hourly
    // - Quarter/Year view: never hourly
    const isTimelineHourlyGranularity =
      calendarView === 'day' ||
      ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1);

    // Treat as hourly if: durationType is 'hourly' OR (durationType is not full-day AND has specific times)
    // Also treat half-day bookings as hourly so their original start time and duration are preserved
    // Also treat resized full-day bookings as hourly when in a zoomed-in hourly view
    // so that the resize respects the snap interval instead of extending to full day
    const isHourlyBooking = booking.durationType === 'hourly' || (!isFullDayType && hasSpecificTime) || isHalfDayType || (isResize && isFullDayType && isTimelineHourlyGranularity);

    if (!isHourlyBooking) {
      // For non-hourly (full-day) bookings: store start at 00:00:00, end at 23:59:59.999
      // Extract the LOCAL date values from the pinned times
      const startYear = pinnedStartTime.getFullYear();
      const startMonth = pinnedStartTime.getMonth();
      const startDay = pinnedStartTime.getDate();

      adjustedStartTime = new Date(startYear, startMonth, startDay, 0, 0, 0, 0);

      // Bryntum uses EXCLUSIVE end dates: a 1-day booking on March 13 has
      // endDate = March 14 00:00. We need to convert to the INCLUSIVE last day
      // at 23:59:59.999 so the stored endTime reflects the actual last day.
      if (pinnedEndTime.getHours() === 0 && pinnedEndTime.getMinutes() === 0 && pinnedEndTime.getSeconds() === 0) {
        const inclusiveEnd = new Date(pinnedEndTime);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        adjustedEndTime = new Date(inclusiveEnd.getFullYear(), inclusiveEnd.getMonth(), inclusiveEnd.getDate(), 23, 59, 59, 999);
      } else {
        const endYear = pinnedEndTime.getFullYear();
        const endMonth = pinnedEndTime.getMonth();
        const endDay = pinnedEndTime.getDate();
        adjustedEndTime = new Date(endYear, endMonth, endDay, 23, 59, 59, 999);
      }
    } else {
      // For hourly bookings: Use the times directly from Bryntum without any conversion
      // Whatever time the user sees/selects is what gets stored

      if (isResize || calendarView === 'day') {
        // Resize operation OR Day view drag - use exact pinned times
        adjustedStartTime = new Date(pinnedStartTime);
        adjustedEndTime = new Date(pinnedEndTime);

        // For half-day and hourly resizes in day-granularity views
        // (week/month/etc.), Bryntum uses exclusive end dates (e.g. next-day
        // midnight for a single day).  Convert to the inclusive last-day end so
        // the booking doesn't visually bleed into the next day.  Day view and
        // zoomed hourly views are excluded, where a midnight end is genuine.
        if (isResize && (isHalfDayType || !isTimelineHourlyGranularity) && calendarView !== 'day') {
          if (adjustedEndTime.getHours() === 0 && adjustedEndTime.getMinutes() === 0 && adjustedEndTime.getSeconds() === 0) {
            const inclusiveEnd = new Date(adjustedEndTime);
            inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
            adjustedEndTime = new Date(inclusiveEnd.getFullYear(), inclusiveEnd.getMonth(), inclusiveEnd.getDate(), 23, 59, 59, 999);
          }
        }
      } else {
        // Non-day view DRAG - preserve original time, change only the date
        const durationMs = originalEnd.getTime() - originalStart.getTime();

        // Get the original time components
        const originalHours = originalStart.getHours();
        const originalMinutes = originalStart.getMinutes();

        // Create new date with the dropped date but original time
        adjustedStartTime = new Date(pinnedStartTime);
        adjustedStartTime.setHours(originalHours, originalMinutes, 0, 0);

        // End time is start time + original duration
        adjustedEndTime = new Date(adjustedStartTime.getTime() + durationMs);
      }
    }

    // Check if anything actually changed (compare as strings for type safety)
    const hasChanged =
      originalResourceIdStr !== newResourceIdStr ||
      originalStartTime.getTime() !== adjustedStartTime.getTime() ||
      originalEndTime.getTime() !== adjustedEndTime.getTime();

    // Prepare the update object with adjusted times
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    // Also update resourceIds so the tooltip and form reflect the new resource
    // Dropping on the Unallocated row strips the resource and everything derived from it
    // (required role and skills) while leaving the status untouched: live data stores the
    // unassignment as no resource at all, demo data as the synthetic 'unallocated' resource.
    // Live mode carries an empty resource list rather than omitting it so the booking's
    // secondary assignments are cleared too.
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      ...buildBookingResourceMoveFields({
        booking,
        targetResource: newResource,
        isLive: currentMode === 'live',
        originalResourceId,
      }),
      status: booking.status,
      startTime: formatDateAsLocalISO(adjustedStartTime),
      endTime: formatDateAsLocalISO(adjustedEndTime),
    };

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (isResize && isHalfDayType) {
      bookingUpdate.durationType = 'hourly';
    } else if (isResize && isFullDayType && isTimelineHourlyGranularity) {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    } else if (isHourlyBooking) {
      bookingUpdate.durationType = 'hourly';
    }

    // When resizing a half-day or full-day booking in hourly view, clear the
    // half-day period since the booking is no longer constrained to AM/PM
    if (isResize && isHalfDayType) {
      bookingUpdate.halfDayPeriod = undefined;
    }

    return {
      bookingUpdate,
      adjustedStartTime,
      adjustedEndTime,
      originalStartTime,
      originalEndTime,
      hasChanged,
    };
  }

  /**
   * Settles the view after a split booking was moved. The server re-plans the booking over
   * the working days at its new place, so the pieces it comes back as are not the ones that
   * moved: the rows the re-plan replaced still sit in the date-range cache the timeline
   * draws from, so they are dropped from it, the range is re-read and the store re-synced.
   * Without it a piece stays on the days it held until the page is refreshed.
   */
  const settleAutoAdjustGroupMove = async (booking: Booking) => {
    const pieceIds = isAutoAdjustGroupPiece(booking)
      ? autoAdjustGroupPieces(booking, [...bookingsById.values()]).map(piece => String(piece.id))
      : [];

    for (const pieceId of pieceIds) removeBookingFromCache(pieceId);
    // The re-read has to land before the store is re-synced, or the store is synced against
    // the rows as they stood before the move.
    if (currentMode === 'live') await refetchLiveBookings();
    if (pieceIds.length > 0) {
      setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
    }
  };

  const handleBookingDragEnd = async (
    draggedBooking: Booking,
    originalResource: Resource,
    newResourceId: string | number,
    droppedStartTime: Date,
    droppedEndTime: Date,
    isResize: boolean = false,
    newProjectId?: string
  ) => {
    // Dragging one piece of a split booking drags the booking: the whole span its pieces
    // cover moves by the distance the piece was dragged and is saved on the piece that
    // starts first, so the server re-plans the booking over the working days at its new
    // place instead of shrinking it to the days that one piece held.
    const groupDrag = isResize
      ? null
      : autoAdjustGroupDrag(
        draggedBooking,
        await readAutoAdjustGroupPieces([draggedBooking]),
        droppedStartTime,
      );
    const booking = groupDrag?.booking ?? draggedBooking;
    const newStartTime = groupDrag?.newStartTime ?? droppedStartTime;
    const newEndTime = groupDrag?.newEndTime ?? droppedEndTime;

    if (booking.recurrenceGroupId || isBookingLocked(booking.id)) {
      cancelDrag();
      return;
    }

    // Convert newResourceId to string for string operations (handles INT IDs after schema change)
    const newResourceIdStr = String(newResourceId);

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-", "customer-", etc.
    if (newResourceIdStr.startsWith('group-') || newResourceIdStr.startsWith('customer-')) {
      // Cancel drag and show error message
      cancelDrag();
      sonnerToast.error('Invalid Drop Location', {
        description: 'Please drop the booking on a specific resource.',
      });
      return;
    }

    // Find resource by comparing as strings (handles INT IDs after schema change)
    const newResource = resources.find(r => String(r.id) === newResourceIdStr);
    if (!newResource) {
      // Cancel drag if resource not found
      cancelDrag();
      return;
    }

    const {
      bookingUpdate,
      adjustedStartTime,
      adjustedEndTime,
      originalStartTime,
      originalEndTime,
      hasChanged,
    } = buildBookingTimeUpdate({
      booking,
      newResource,
      newStartTime,
      newEndTime,
      isResize,
      originalResourceId: originalResource.id,
    });

    // Project view: dropping onto another project's row moves the booking to that project.
    const newProject = newProjectId
      ? projectDetails.find(p => String(p.id) === String(newProjectId))
      : undefined;
    const projectChanged = !!newProject && String(newProject.id) !== String(booking.projectId ?? '');
    if (newProjectId && !newProject) {
      console.warn('[ResourceCalendar] drop target project is not in this screen\'s project list', { newProjectId });
    }

    if (!hasChanged && !projectChanged) {
      // Cancel drag if nothing changed
      cancelDrag();
      return;
    }

    // A move onto another resource has to land somewhere that resource can
    // actually take it; an invalid drop is refused and the booking snaps back to
    // where it came from. Resizes and moves that keep the same resource (a
    // project-row drag, say) keep their existing behaviour, apart from the
    // resource's contract window, which every move and resize must stay inside.
    const movesToAnotherResource =
      !isResize && String(booking.resourceId ?? '') !== String(newResource.id);
    const availability: ResourceAvailabilityResult = movesToAnotherResource
      ? checkResourceAvailability(newResource, adjustedStartTime, adjustedEndTime)
      : checkContractWindow(newResource, adjustedStartTime, adjustedEndTime);
    if (!availability.allowed && !isPlannedAwayByAutoAdjust(booking, availability)) {
      cancelDrag();
      schedulerTimelineRef.current?.forceStoreRefresh();
      sonnerToast.error('Cannot Move Booking', { description: availability.message });
      return;
    }

    cancelDrag();

    // When the resource changes via drag-and-drop, update the role to match the new resource
    const resourceChanged = String(booking.resourceId ?? '') !== String(newResource.id);

    if (projectChanged && newProject) {
      const costCentreValid = isCostCentreValidForProject(
        booking.costCentreId,
        newProject.id,
        projectDetails,
        costCentres,
        currentMode,
      );
      const customerValid = isCustomerValidForProject(
        booking.clientId ?? booking.customer,
        newProject.id,
        projectDetails,
        clients,
        currentMode,
      );
      const bookingType = settings.bookingTypes.find(
        type => String(type.id) === String(booking.bookingTypeId),
      );
      const costCentreCompulsory = isBookingTypeFieldCompulsory(bookingType, 'Cost Centre');
      const customerCompulsory = isBookingTypeFieldCompulsory(bookingType, 'Customer');

      if (
        !costCentreValid
        || !customerValid
        || costCentreCompulsory
        || customerCompulsory
      ) {
        openProjectMoveCostCentreModal(booking, bookingUpdate, newProject);
        return;
      }
      bookingUpdate.projectId = String(newProject.id);
      bookingUpdate.projectName = newProject.name;
    }

    // Check if the Confirm Drag/Drop preference is enabled (user-level overrides global)
    const shouldConfirm = settings.userConfirmDragDrop ?? settings.confirmDragDrop;

    if (shouldConfirm) {
      // Show confirmation dialog with pending update details
      setPendingDragUpdate({
        booking,
        originalResource,
        newResource,
        originalStartTime,
        originalEndTime,
        newStartTime: adjustedStartTime,
        newEndTime: adjustedEndTime,
        isResize,
        newProject: projectChanged ? newProject : undefined,
      });
      setShowDragConfirm(true);
    } else if (
      isLinkedDemandBooking(booking)
      && resourceChanged
    ) {
      setPendingDragUpdate({
        booking,
        originalResource,
        newResource,
        originalStartTime,
        originalEndTime,
        newStartTime: adjustedStartTime,
        newEndTime: adjustedEndTime,
        isResize,
      });
      setShowDemandReassignConfirm(true);
    } else {
      // Apply the update directly without confirmation
      setIsSavingBooking(true);
      updateBooking(booking.id, bookingUpdate).then(async () => {
        await settleAutoAdjustGroupMove(booking);
        sonnerToast.success(isResize ? 'Booking Resized' : 'Booking Moved', {
          description: isResize ? 'The booking has been successfully resized.' : 'The booking has been successfully moved.',
        });
      }).catch((error) => {
        if (handleBookingWriteRejected(error, 'modify')) return;
        sonnerToast.error(isResize ? 'Failed to Resize Booking' : 'Failed to Move Booking', {
          description: 'Please try again.',
        });
      }).finally(() => {
        setIsSavingBooking(false);
      });
    }
  };

  const handleBookingsResizeEnd = async (
    targets: Array<{
      booking: Booking;
      resource: Resource;
      newStartTime: Date;
      newEndTime: Date;
    }>,
  ) => {
    setIsSavingBooking(true);

    try {
      // The new times are computed client-side for every target, then sent as one
      // batch request rather than one request per booking.
      const updates: BookingBatchUpdate[] = [];
      const rejected: string[] = [];
      for (const target of targets) {
        if (target.booking.recurrenceGroupId || isBookingLocked(target.booking.id)) continue;

        const {
          bookingUpdate,
          hasChanged,
        } = buildBookingTimeUpdate({
          booking: target.booking,
          newResource: target.resource,
          newStartTime: target.newStartTime,
          newEndTime: target.newEndTime,
          isResize: true,
        });

        if (!hasChanged) continue;

        const updatedStartTime = bookingUpdate.startTime
          ? new Date(bookingUpdate.startTime).getTime()
          : NaN;
        const updatedEndTime = bookingUpdate.endTime
          ? new Date(bookingUpdate.endTime).getTime()
          : NaN;
        if (
          Number.isFinite(updatedStartTime) &&
          Number.isFinite(updatedEndTime) &&
          updatedStartTime >= updatedEndTime
        ) {
          continue;
        }

        const contract = checkContractWindow(target.resource, new Date(updatedStartTime), new Date(updatedEndTime));
        if (!contract.allowed) {
          rejected.push(contract.message ?? `${target.resource.name} is outside their contract`);
          continue;
        }

        updates.push({ ...bookingUpdate, id: target.booking.id });
      }

      if (rejected.length > 0) {
        schedulerTimelineRef.current?.forceStoreRefresh();
        sonnerToast.error(`Cannot resize ${rejected.length} booking${rejected.length === 1 ? '' : 's'}`, {
          description: rejected.join('\n'),
        });
      }

      if (updates.length === 0) return;

      let updated: Array<string | number> = [];
      let errors: string[] = [];
      try {
        ({ updated, errors } = await updateBookingsBatch(updates));
      } catch (error) {
        if (handleBookingWriteRejected(error, 'modify')) return;
        console.error('❌ Failed to resize the selected bookings:', error);
        sonnerToast.error(`Failed to resize ${updates.length} booking${updates.length === 1 ? '' : 's'}`);
        return;
      }

      if (updated.length > 0) {
        sonnerToast.success('Bookings Resized', {
          description: `${updated.length} booking${updated.length === 1 ? '' : 's'} ${updated.length === 1 ? 'has' : 'have'} been resized.`,
        });
      }
      if (errors.length > 0) {
        sonnerToast.error(`Failed to resize ${errors.length} booking${errors.length === 1 ? '' : 's'}`, {
          description: errors.join('\n'),
        });
      }

      if (currentMode === 'live') refetchLiveBookings();
    } finally {
      setIsSavingBooking(false);
    }
  };

  const handleBookingsDragEnd = async (
    targets: Array<{
      booking: Booking;
      resource: Resource;
      newStartTime: Date;
      newEndTime: Date;
    }>,
    meta?: { skippedCount: number },
  ) => {
    setIsSavingBooking(true);

    try {
      // Same as the multi-select resize: compute every target's new window
      // client-side and send the whole move as one batch request.
      const updates: BookingBatchUpdate[] = [];
      const rejected: string[] = [];
      // A split booking in the drag moves as the one booking the user sees, whichever of
      // its pieces the drag held.
      const moves = planAutoAdjustGroupDrags(
        targets,
        await readAutoAdjustGroupPieces(targets.map(target => target.booking)),
      );
      for (const target of moves) {
        if (target.booking.recurrenceGroupId || isBookingLocked(target.booking.id)) continue;

        const {
          bookingUpdate,
          adjustedStartTime,
          adjustedEndTime,
          hasChanged,
        } = buildBookingTimeUpdate({
          booking: target.booking,
          newResource: target.resource,
          newStartTime: target.newStartTime,
          newEndTime: target.newEndTime,
          isResize: false,
        });

        if (!hasChanged) continue;

        const availability: ResourceAvailabilityResult =
          String(target.resource.id) === String(target.booking.resourceId ?? '')
            ? checkContractWindow(target.resource, adjustedStartTime, adjustedEndTime)
            : checkResourceAvailability(target.resource, adjustedStartTime, adjustedEndTime);
        if (!availability.allowed && !isPlannedAwayByAutoAdjust(target.booking, availability)) {
          rejected.push(availability.message ?? `${target.resource.name} cannot take this booking`);
          continue;
        }

        updates.push({ ...bookingUpdate, id: target.booking.id });
      }

      let updated: Array<string | number> = [];
      let errors: string[] = [];
      if (updates.length > 0) {
        try {
          ({ updated, errors } = await updateBookingsBatch(updates));
        } catch (error) {
          if (handleBookingWriteRejected(error, 'modify')) return;
          console.error('❌ Failed to move the selected bookings:', error);
          sonnerToast.error(`Failed to move ${updates.length} booking${updates.length === 1 ? '' : 's'}`);
          return;
        }
      }

      if (updated.length > 0) {
        sonnerToast.success('Bookings Moved', {
          description: `${updated.length} booking${updated.length === 1 ? '' : 's'} ${updated.length === 1 ? 'has' : 'have'} been moved.`,
        });
      }
      if (errors.length > 0) {
        sonnerToast.error(`Failed to move ${errors.length} booking${errors.length === 1 ? '' : 's'}`, {
          description: errors.join('\n'),
        });
      }
      if (rejected.length > 0) {
        schedulerTimelineRef.current?.forceStoreRefresh();
        sonnerToast.error(`Cannot move ${rejected.length} booking${rejected.length === 1 ? '' : 's'}`, {
          description: rejected.join('\n'),
        });
      }
      const skippedCount = meta?.skippedCount ?? 0;
      if (skippedCount > 0) {
        sonnerToast.warning('Some bookings were not moved', {
          description: `${skippedCount} booking${skippedCount === 1 ? '' : 's'} could not take the same move.`,
        });
      }

      // A split booking is re-planned over the working days it was dropped on, so the
      // days it ends up covering are the server's, not the ones the drag drew: the rows the
      // re-plan replaced still sit in the date-range cache the timeline draws from, so they
      // are dropped from it (the pieces the save kept are read back into the booking state
      // and drawn from there), the range is re-read and the store re-synced. Without it a
      // piece stays on the resource and days it held until the page is refreshed.
      const movedGroupPieceIds = targets
        .filter(target => isAutoAdjustGroupPiece(target.booking))
        .flatMap(target => autoAdjustGroupPieces(target.booking, [...bookingsById.values()]))
        .map(piece => String(piece.id));
      if (movedGroupPieceIds.length > 0) {
        for (const pieceId of new Set(movedGroupPieceIds)) removeBookingFromCache(pieceId);
      }

      // The re-read has to land before the store is re-synced, or the store is synced
      // against the rows as they stood before the drop and a replaced piece is drawn
      // again on the days it held.
      if (currentMode === 'live') await refetchLiveBookings();

      if (movedGroupPieceIds.length > 0) {
        setTimeout(() => schedulerTimelineRef.current?.forceStoreRefresh(), 0);
      }
    } finally {
      setIsSavingBooking(false);
    }
  };

  const handleConfirmDrag = (demandReassignScope?: 'this' | 'all') => {
    if (!pendingDragUpdate) return;

    // newStartTime and newEndTime are already adjusted (UTC times) from handleBookingDragEnd
    const { booking, newResource, newStartTime, newEndTime, isResize: wasResize, newProject } = pendingDragUpdate;
    const resourceChanged = String(booking.resourceId ?? '') !== String(newResource.id);
    const normalizedDemandReassignScope = normalizeDemandReassignScope(demandReassignScope);
    if (shouldPromptDemandReassign(booking, resourceChanged, normalizedDemandReassignScope)) {
      setShowDragConfirm(false);
      setShowDemandReassignConfirm(true);
      return;
    }

    // Prepare the update object - times are already adjusted in handleBookingDragEnd
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    // Also update resourceIds so the tooltip and form reflect the new resource
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      ...buildBookingResourceMoveFields({
        booking,
        targetResource: newResource,
        isLive: currentMode === 'live',
      }),
      status: booking.status,
      startTime: formatDateAsLocalISO(newStartTime),
      endTime: formatDateAsLocalISO(newEndTime),
      // Project view: dropping on another project's row also moves the booking's project.
      // The cost centre was already validated against it in handleBookingDragEnd.
      ...(newProject
        ? { projectId: String(newProject.id), projectName: newProject.name }
        : {}),
    };

    const confirmIsHalfDay = booking.durationType === 'half-day' ||
                             booking.durationType === 'half-day-am' ||
                             booking.durationType === 'half-day-pm';
    const confirmIsFullDay = booking.durationType === 'full-day' ||
                             booking.durationType === 'half-day-am' ||
                             booking.durationType === 'half-day-pm' ||
                             booking.durationType === 'half-day';
    const confirmIsTimelineHourly =
      calendarView === 'day' ||
      ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1);

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (wasResize && confirmIsHalfDay) {
      bookingUpdate.durationType = 'hourly';
    } else if (wasResize && confirmIsFullDay && confirmIsTimelineHourly) {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    }

    if (wasResize && confirmIsHalfDay) {
      bookingUpdate.halfDayPeriod = undefined;
    }

    // Update the booking - this will trigger a smooth transition
    setShowDragConfirm(false);
    setShowDemandReassignConfirm(false);
    setPendingDragUpdate(null);
    setIsSavingBooking(true);
    const saveDrag = async () => {
      if (currentMode === 'live' && normalizedDemandReassignScope === 'all' && booking.demandPlanGroupId) {
        const response = await bookingsApi.reassignDemandPlanGroup(booking.demandPlanGroupId, {
          resourceId: Number(newResource.id),
          fromStartTime: booking.startTime,
        });
        const bookingTimeUpdate = { ...bookingUpdate };
        delete bookingTimeUpdate.resourceId;
        delete bookingTimeUpdate.resourceIds;
        await updateBooking(booking.id, bookingTimeUpdate);
        sonnerToast.success('Linked Demand Bookings Reassigned', {
          description: `${response.updated.length} linked demand booking${response.updated.length === 1 ? '' : 's'} reassigned.`,
        });
        if (response.skipped.length > 0) {
          sonnerToast.warning('Some linked demand bookings stayed on their original resource', {
            description: `${response.skipped.length} booking${response.skipped.length === 1 ? '' : 's'} could not move because the target resource was already booked.`,
          });
        }
        if (response.errors.length > 0) {
          sonnerToast.error('Some linked demand bookings could not be reassigned', {
            description: `${response.errors.length} booking${response.errors.length === 1 ? '' : 's'} failed.`,
          });
        }
      } else if (currentMode === 'demo' && normalizedDemandReassignScope === 'all' && booking.demandPlanGroupId) {
        const groupBookings = [...new Map(
          [...allBookings, ...bookings]
            .filter(groupBooking => groupBooking.demandPlanGroupId === booking.demandPlanGroupId)
            .map(groupBooking => [String(groupBooking.id), groupBooking]),
        ).values()];
        await Promise.all(groupBookings.map(groupBooking => updateBooking(groupBooking.id, {
          resourceId: String(newResource.id),
          resourceIds: [String(newResource.id)],
        })));
        const bookingTimeUpdate = { ...bookingUpdate };
        delete bookingTimeUpdate.resourceId;
        delete bookingTimeUpdate.resourceIds;
        await updateBooking(booking.id, bookingTimeUpdate);
        sonnerToast.success('Linked Demand Bookings Reassigned', {
          description: `${groupBookings.length} linked demand booking${groupBookings.length === 1 ? '' : 's'} reassigned.`,
        });
      } else {
        await updateBooking(booking.id, bookingUpdate);
        sonnerToast.success('Booking Moved', {
          description: 'The booking has been successfully moved.',
        });
      }
      await settleAutoAdjustGroupMove(booking);
    };
    saveDrag().catch((error) => {
      if (handleBookingWriteRejected(error, 'modify')) return;
      sonnerToast.error('Failed to Move Booking', {
        description: 'Please try again.',
      });
    }).finally(() => {
      setIsSavingBooking(false);
    });
  };

  const handleDiscardDrag = () => {
    // Re-sync the Bryntum event store from the React-derived data so the
    // drag/resize-modified event snaps back to its original position.
    // This is more robust than revertEvent because it uses the same data
    // source (bryntumEvents) and handles grouping, timezone shifts, and
    // half-day display clamping automatically.
    if (schedulerTimelineRef.current) {
      schedulerTimelineRef.current.forceStoreRefresh();
    }
    
    // Clean up the dialog state
    setShowDragConfirm(false);
    setShowDemandReassignConfirm(false);
    setPendingDragUpdate(null);
    
    sonnerToast.info('Changes Discarded', {
      description: 'The booking was not moved.',
    });
  };

  const handleCancelDemandReassign = () => {
    if (pendingDemandBookingUpdate) {
      setShowDemandReassignConfirm(false);
      setPendingDemandBookingUpdate(null);
      return;
    }
    handleDiscardDrag();
  };

  const handleReassignThisDemand = () => {
    if (pendingDemandBookingUpdate) {
      const pendingUpdate = pendingDemandBookingUpdate;
      setShowDemandReassignConfirm(false);
      setPendingDemandBookingUpdate(null);
      setIsSavingBooking(true);
      void performDemandBookingUpdate(
        pendingUpdate.id,
        pendingUpdate.originalBooking,
        pendingUpdate.update,
        pendingUpdate.detailScope,
        'this'
      ).then(() => {
        setIsBookingModalOpen(false);
      }).catch((error) => {
        if (handleBookingWriteRejected(error, 'modify')) return;
        sonnerToast.error('Failed to update Open Demand Booking', {
          description: 'Please try again.',
        });
      }).finally(() => {
        setIsSavingBooking(false);
      });
      return;
    }
    handleConfirmDrag('this');
  };

  const handleReassignAllDemand = () => {
    if (pendingDemandBookingUpdate) {
      const pendingUpdate = pendingDemandBookingUpdate;
      setShowDemandReassignConfirm(false);
      setPendingDemandBookingUpdate(null);
      setIsSavingBooking(true);
      void performDemandBookingUpdate(
        pendingUpdate.id,
        pendingUpdate.originalBooking,
        pendingUpdate.update,
        pendingUpdate.detailScope,
        'all'
      ).then(() => {
        setIsBookingModalOpen(false);
      }).catch((error) => {
        if (handleBookingWriteRejected(error, 'modify')) return;
        sonnerToast.error('Failed to update Open Demand Booking', {
          description: 'Please try again.',
        });
      }).finally(() => {
        setIsSavingBooking(false);
      });
      return;
    }
    handleConfirmDrag('all');
  };

  const getDateLabel = () => {
    switch (calendarView) {
      case 'day':
        return format(selectedDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        return `Week of ${format(selectedDate, 'MMM d, yyyy')}`;
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      case 'quarter':
        return `Q${Math.floor(selectedDate.getMonth() / 3) + 1} ${format(selectedDate, 'yyyy')}`;
      case 'year':
        return format(selectedDate, 'yyyy');
      default:
        return '';
    }
  };

  const handleDatePickerSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setIsDatePickerOpen(false);
    }
  };

  const handleDatePickerOpenChange = (open: boolean) => {
    if (open) setDatePickerMonth(selectedDate);
    setIsDatePickerOpen(open);
  };

  // Handler for Copy Booking from context menu
  const handleCopyBooking = async (booking: Booking) => {
    if (!canCopyBooking(booking, false, [...bookingsById.values()])) return;

    // A split auto-adjust booking is copied as the whole group's span, so pasting it
    // reproduces the entire booking and not just the piece that was right-clicked.
    setCopiedBooking(autoAdjustGroupEditTarget(booking, await readAutoAdjustGroupPieces([booking])));
    sonnerToast.success('Booking Copied', {
      description: `"${booking.projectName || 'Booking'}" has been copied. Right-click on an empty cell to paste.`,
    });
  };

  // Handler for Add Booking from context menu - opens the booking form
  const handleAddBookingFromMenu = (resource: Resource, date: Date) => {
    if (blockIfPreview()) return;
    if (!userCanCreateBooking) {
      sonnerToast.error('Permission Denied', {
        description: "You don't have permission to create bookings.",
      });
      return;
    }
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }

    // Extract customer name if in customer mode so the booking form's customer
    // field is pre-populated with the customer whose row was right-clicked.
    // Customer-mode IDs are composite "customerName-resourceId"; the customer
    // name can contain hyphens, so strip the real resource ID by suffix match.
    let customerToPreselect: string | undefined;
    let effectiveResource = resource;
    if (groupingMode === 'customer' && String(resource.id).includes('-') &&
      !String(resource.id).startsWith('group-') && !String(resource.id).startsWith('customer-')) {
      const composite = String(resource.id);
      const matchedRes = resources.find(r => composite.endsWith(`-${String(r.id)}`));
      const realResourceId = matchedRes
        ? String(matchedRes.id)
        : composite.slice(composite.indexOf('-') + 1);
      const derivedCustomer = composite.slice(0, composite.length - realResourceId.length - 1);
      // 'Unassigned' is the placeholder bucket for bookings without a customer —
      // don't pre-populate the field with it.
      if (derivedCustomer && derivedCustomer !== 'Unassigned') {
        customerToPreselect = derivedCustomer;
      }
      // Restore the real resource ID so the resource is correctly pre-selected.
      effectiveResource = { ...resource, id: realResourceId };
    }

    setPreselectedResource(effectiveResource);
    setPreselectedDate(date);
    setPreselectedEndDate(date);
    
    // Set duration type based on calendar view
    if (calendarView === 'day') {
      // Day view: Use the clicked time slot and add 15 minutes for end time
      const startHour = date.getHours();
      const startMinute = date.getMinutes();
      
      // Calculate end time: 15 minutes after start time
      let endHour: number;
      let endMinute: number;
      endMinute = startMinute + 15;
      if (endMinute >= 60) {
        endHour = startHour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startHour;
      }
      
      setPreselectedStartTime({ hour: startHour, minute: startMinute });
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect,
        customer: customerToPreselect 
      });
    } else {
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect,
        customer: customerToPreselect 
      });
    }
    
    setBookingModalMode('create');
    setEditingBooking(undefined);
    setIsBookingModalOpen(true);
  };

  const renderCalendarView = () => {
    // Merge preview ghosts into the filtered bookings list
    const bookingsWithPreview = planPreviewScenario
      ? [...filteredData.filteredBookings, ...previewGhostBookings]
      : filteredData.filteredBookings;

    return (
      <BryntumSchedulerTimeline
        ref={schedulerTimelineRef}
        selectedDate={selectedDate}
        calendarView={calendarView}
        zoomLevel={zoomLevel}
        selectedTimezone={selectedTimezone}
        enableTimezones={settings.enableTimezones}
        resources={filteredData.filteredResources}
        resourceWindowResetToken={resourceWindowResetToken}
        onLoadedResourceIdsChange={handleLoadedResourceIdsChange}
        onEventsRendered={handleEventsRendered}
        bookings={bookingsWithPreview}
        projectDetails={projectDetails}
        lockedBookingIds={lockedBookingIds}
        resourceTimeRanges={combinedResourceTimeRanges}
        timeRanges={timeRanges}
        groupingMode={groupingMode}

        schedulerGroups={schedulerGroups}
        statusConfigs={settings.statusConfigs}
        isHeatmapMode={isHeatmapMode}
        absences={absences}
        bankHolidays={mockBankHolidays}
        searchQuery={bookingSearchQuery}
        onEditBooking={handleEditBooking}
        onResolveConflict={handleResolveConflict}
        onDeleteBooking={handleDeleteBooking}
        onDeleteBookings={handleDeleteBookings}
        onDuplicateBooking={handleDuplicateBooking}
        onMoveBooking={handleMoveBooking}
        onStatusChange={handleStatusChange}
        onStatusChangeBookings={handleStatusChangeBookings}
        onCopyBooking={handleCopyBooking}
        onSlotClick={handleSlotClick}
        onDragSelect={handleDragSelect}
        onBookingDragEnd={handleBookingDragEnd}
        onBookingsDragEnd={handleBookingsDragEnd}
        onBookingsResizeEnd={handleBookingsResizeEnd}
        onAddBooking={handleAddBookingFromMenu}
        onQuickAddBooking={handleQuickAddBooking}
        onPasteBooking={handlePasteBooking}
        copiedBooking={copiedBooking}
        whatIfScenario={effectiveWhatIfScenario}
        bookingTypeUsages={bookingTypeUsages}
        canCreateBooking={userCanCreateBooking && !isPreviewMode}
        quickAddEnabled={settings.quickAddDefault !== 'none'}
        canEditBooking={userCanEditBooking && !isPreviewMode}
        canDeleteBooking={userCanDeleteBooking && !isPreviewMode}
        canOverrideReadOnly={userCanOverrideReadOnly}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Plan Preview Banner */}
      {planPreviewScenario && (
        <div className="shrink-0 border-b bg-muted/40">
          <div className="flex items-center gap-3 px-4 py-2.5">
            {/* Icon */}
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border bg-background">
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-foreground">
                  Preview mode
                </span>
                <span className="text-xs text-muted-foreground">—</span>
                <span className="text-xs font-medium text-foreground truncate">
                  {planPreviewScenario.scenario.label}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  for {planPreviewScenario.requestProjectName} · {planPreviewScenario.requestRole}
                </span>
                <Badge variant="secondary" className="text-[10px] font-medium px-1.5 py-0 h-5 gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground shrink-0" />
                  {previewGhostBookings.length} proposed bookings shown as dashed
                </Badge>
              </div>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={onPlanPreviewClose}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to plan
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground"
                onClick={onPlanPreviewClose}
                title="Dismiss preview"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header Controls */}
      <div className="shrink-0 border-b bg-background" data-tour="planner-toolbar">
        <div className="flex items-center justify-between px-3 py-2 gap-2 w-full">
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Popover open={isDatePickerOpen} onOpenChange={handleDatePickerOpenChange}>
            <PopoverTrigger asChild>
              <button
                className="text-base font-semibold min-w-[140px] shrink-0 text-left hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors whitespace-nowrap"
                title="Click to select a date"
              >
                {getDateLabel()}
                <CalendarIcon className="h-4 w-4 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDatePickerSelect}
                month={datePickerMonth}
                onMonthChange={setDatePickerMonth}
                captionLayout="dropdown"
                startMonth={datePickerNavMonths.startMonth}
                endMonth={datePickerNavMonths.endMonth}
              />
            </PopoverContent>
          </Popover>

          {/* Booking Quick Search */}
            <div className="relative w-[160px] shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={bookingSearchQuery}
                onChange={(e) => setBookingSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
              {bookingSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setBookingSearchQuery('')}
                  title="Clear search"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Feature Feedback & Data Mode Toggle */}
          <DataModeToggle screen="calendar" />
          <FeatureFeedbackCollector feature={isHeatmapMode ? 'Heatmap' : 'Planner'} />

          {/* Booking Search Results Counter */}
          {bookingSearchQuery && (
            <Badge variant="secondary" className="h-8 px-3 text-sm font-semibold whitespace-nowrap">
              {filteredData.filteredBookings.length} / {bookings.length} Bookings
            </Badge>
          )}

          {/* Create Booking Button — hidden when the user's role cannot create
              bookings, or while previewing a proposed plan (read-only). */}
          {userCanCreateBooking && !isPreviewMode && (
            <Button data-tour="add-booking-btn" onClick={handleCreateBooking} size="sm" className="h-8 px-2.5 gap-1">
              <Plus className="h-4 w-4" />
              {currentMode === 'demo' ? 'Add Booking' : 'New Booking'}
            </Button>
          )}

          {/* Current Timezone Selector - Shows next to Create Booking button */}
          <CurrentTimezoneSelector
            selectedTimezone={selectedTimezone}
            onTimezoneChange={handleTimezoneChange}
          />

          {/* Zoom Controls */}
          <div className="flex items-center gap-1.5">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomOut}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === 0}
              className="h-8 px-2"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[50px] text-center">
              {getZoomLabels()[zoomLevel]}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomIn}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === getMaxZoomLevel()}
              className="h-8 px-2"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>

          {/* View Selector */}
          <Select value={calendarView} onValueChange={(value) => setCalendarView(value as CalendarView)}>
            <SelectTrigger className="w-[100px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[240px]" position="popper" sideOffset={4} align="start">
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>
          {/* Filter Drawer Trigger — far right, aligns with right-side slide-in */}
          <CalendarFilterDrawerTrigger
            activeFilterCount={
              filters.resourceTypes.length +
              filters.departments.length +
              filters.resources.length +
              filters.roles.length +
              filters.projectStatuses.length +
              filters.projects.length +
              filters.costCentreStatuses.length +
              filters.costCentres.length +
              filters.bookingTypes.length +
              filters.bookingStatuses.length +
              (filters.utilisationRange[0] !== 0 || filters.utilisationRange[1] !== 100 || filters.overUtilisedOnly ? 1 : 0) +
              (hasActiveAdvancedFilters ? advancedFilters.conditions.length : 0)
            }
            onClick={() => setIsFilterTrayOpen(true)}
          />
          <HelpButton />
        </div>
        </div>
      </div>

      {/* Filter Drawer (replaces collapsible tray) */}
      <CalendarFilterDrawer
        open={isFilterTrayOpen}
        onOpenChange={setIsFilterTrayOpen}
      />

      {/* ── Burnout risk banners — rendered via BurnoutRiskPanel below (portal slot) ── */}
      {isHeatmapMode && (
        <div id="burnout-banner-slot" data-tour="whatif-panel" className="shrink-0" />
      )}

      {/* ── What-if amber banner — shown in calendar view when a scenario is active ── */}
      {whatIfScenario && (
        <div data-tour="conflict-panel" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '7px 16px',
          flexShrink: 0,
          background: 'linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.04) 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.30)',
          borderLeft: '3px solid #f59e0b',
        }}>
          {/* Left: icon + what-if pill + description */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              backgroundColor: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 800,
            }}>⟳</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 9px', borderRadius: 20, flexShrink: 0,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
              textTransform: 'uppercase' as const,
              backgroundColor: '#f59e0b', color: '#fff',
            }}>
              What-if mode
            </span>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#78350f', lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {whatIfScenario.type === 'rebalance'
                ? inPlaceBookingUpdates.length > 0
                  ? `${whatIfScenario.projectName ?? 'Project'} — ${
                      (whatIfScenario.bookingDelays?.length ?? 0) > 0 ? 'start delayed' : 'hours reduced'
                    } for ${whatIfScenario.atRiskResourceName ?? '…'} · not yet saved`
                  : `${whatIfScenario.projectName ?? 'Project'} — move from ${whatIfScenario.atRiskResourceName ?? '…'} to ${effectiveWhatIfScenario?.candidateResourceName ?? whatIfScenario.candidateResourceName ?? '…'} · not yet saved`
                : `${whatIfScenario.freeResourceName ?? 'Resource'} becomes free W${whatIfScenario.freeFromWeek ?? '?'} · assign new work · not yet saved`
              }
            </span>
            {reductionSummary && (
              <WhatIfReductionSummary summary={reductionSummary} bookingsById={bookingsById} />
            )}
          </div>
          {/* Right: Commit + Discard buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              onClick={handleWhatIfCommit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                backgroundColor: '#0d9488', color: '#fff',
                border: 'none', letterSpacing: '0.01em',
                transition: 'opacity 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            >
              ✓ Commit changes
            </button>
            <button
              onClick={handleWhatIfDiscard}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                backgroundColor: 'transparent', color: '#ef4444',
                border: '1px solid #ef4444',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239,68,68,0.06)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
            >
              Discard scenario
            </button>
          </div>
        </div>
      )}

      {/* Calendar View + (optionally) Burnout Suggestions Panel side-by-side */}
      <div className="flex-1 flex overflow-hidden" data-tour={isHeatmapMode ? 'heatmap-grid' : 'planner-calendar'}>
        {/* Main calendar grid */}
        <div
          ref={schedulerDomRef}
          className="flex-1 overflow-hidden calendar-grid-container relative"
        >
          {!isCompanyLoading && !hasCompanyAccess ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="text-muted-foreground text-sm">
                  First, create at least one company and grant the user access to it.
                </p>
                {canViewIntegrations() && canManageIntegrations() && onNavigateToIntegrations ? (
                  <Button onClick={onNavigateToIntegrations}>
                    Go to Integrations
                  </Button>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Contact an administrator to set this up.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Grouping mode selector — absolutely positioned over Bryntum's column header.
                  Pure React inside our own DOM tree, so it's always visible and interactive. */}
              {/* Grouping Select — spans the full 45px header height so it vertically
                  centres to match the collapse button. Measurements:
                    column=240px, cell-padding-left=12px, cell-padding-right=12px
                    button=32px → button sits at right: 12px (right edge = 228px from left)
                    gap=8px → select right edge = 228-8=220px
                    select left = 8px → select width = 220-8 = 212px                    */}
              <div data-tour="planner-grouping" className="absolute z-50 flex items-center" style={{ top: groupingSelectTop, left: 8, width: 170, height: 32 }}>
                <Select
                  value={groupingMode}
                  onValueChange={(mode) => {
                    setGroupingMode(mode as typeof groupingMode);
                    setHasUserChangedGrouping(true);
                  }}
                >
                  <SelectTrigger className="h-8 w-full text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="project-resource">Project</SelectItem>
                    <SelectItem value="resource">Resource</SelectItem>
                    <SelectItem value="role-resource">Role</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {renderCalendarView()}
              {(isSavingBooking || isDeletingBooking || (hasCompletedFirstLoad.current && (isLoadingBookings || isPaintingEvents || (currentMode !== 'demo' && isContextLoading) || isGroupsLoading))) && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center">
                  <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm font-medium">{isSavingBooking ? 'Saving booking...' : isDeletingBooking ? 'Deleting booking...' : (isContextLoading || isGroupsLoading) ? 'Loading data...' : 'Loading bookings...'}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Burnout risk warnings — always active in heatmap mode.
            When whatIfEnabled + onNavigateToCalendarWithWhatIf are available,
            the suggestions panel and what-if overlay are also rendered. */}
        {isHeatmapMode && (
          <div className="contents">
            <BurnoutRiskPanelWrapper
              schedulerDomRef={schedulerDomRef}
              resources={filteredData.filteredResources}
              bookings={filteredData.filteredBookings}
              globalWorkingProfile={settings.globalWorkingProfile}
              onNavigateToCalendar={whatIfEnabled && onNavigateToCalendarWithWhatIf ? onNavigateToCalendarWithWhatIf : undefined}
              initialResolvedCommit={initialResolvedCommit}
              onResolvedCommitConsumed={onResolvedCommitConsumed}
            />
          </div>
        )}
      </div>

      {/* Heatmap Legend — sits flush below the grid, only in heatmap mode */}
      <HeatmapLegend visible={isHeatmapMode} />

      {/* Comprehensive Booking Modal */}
      <ComprehensiveBookingModal
        key="comprehensive-booking-modal"
        open={isBookingModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsBookingModalOpen(false);
            // Ensure any temporary/phantom events are removed from the timeline
            schedulerTimelineRef.current?.cleanupPhantomEvents?.();
            // Reset preselected values when modal closes
            setBookingModalMode('create');
            setEditingBooking(undefined);
            setEditContextResourceId(undefined);
            setPreselectedResource(undefined);
            setPreselectedDate(undefined);
            setPreselectedEndDate(undefined);
            setPreselectedStartTime(undefined);
            setPreselectedEndTime(undefined);
            setPreselectedBookingData(undefined);
          }
        }}
        onSave={handleSaveBooking}
        onUpdate={handleUpdateBooking}
        onDelete={handleDeleteBooking}
        resources={resources}
        projectDetails={projectDetails}
        costCentres={costCentres}
        existingBookings={bookings}
                preselectedResource={preselectedResource}
                preselectedDate={preselectedDate}
                preselectedEndDate={preselectedEndDate}
                preselectedStartTime={preselectedStartTime ? `${String(preselectedStartTime.hour).padStart(2, '0')}:${String(preselectedStartTime.minute).padStart(2, '0')}` : undefined}
        preselectedEndTime={preselectedEndTime ? `${String(preselectedEndTime.hour).padStart(2, '0')}:${String(preselectedEndTime.minute).padStart(2, '0')}` : undefined}
        preselectedBookingData={preselectedBookingData}
        editingBooking={editingBooking}
        editContextResourceId={editContextResourceId}
        mode={bookingModalMode}
        dataMode={currentMode}
        defaultTimezone={selectedTimezone}
      />

      {/* Drag Confirmation Dialog */}
      <BookingDragConfirmDialog
        open={showDragConfirm}
        booking={pendingDragUpdate?.booking || null}
        originalResource={pendingDragUpdate?.originalResource}
        newResource={pendingDragUpdate?.newResource}
        originalStartTime={pendingDragUpdate?.originalStartTime || null}
        originalEndTime={pendingDragUpdate?.originalEndTime || null}
        newStartTime={pendingDragUpdate?.newStartTime || null}
        newEndTime={pendingDragUpdate?.newEndTime || null}
        onConfirm={() => handleConfirmDrag()}
        onDiscard={handleDiscardDrag}
      />
      <ReassignDemandBookingConfirmDialog
        open={showDemandReassignConfirm}
        onCancel={handleCancelDemandReassign}
        onReassignThisBooking={handleReassignThisDemand}
        onReassignAllLinkedBookings={handleReassignAllDemand}
      />

      {/* Quick Add Booking Modal */}
      <QuickAddBookingModal
        open={isQuickAddModalOpen}
        onClose={() => setIsQuickAddModalOpen(false)}
        onSave={handleQuickAddBookingSave}
        resource={quickAddPreselectedResource}
        bookingType={settings.bookingTypes.find(type => String(type.id) === String(settings.quickAddDefault))
          ?? settings.bookingTypes.find(type => type.active) ?? settings.bookingTypes[0]}
        defaultStartTime={quickAddDefaultStartTime}
        defaultEndTime={quickAddDefaultEndTime}
        preselectedDate={quickAddPreselectedDate}
      />

      {/* Delete Booking Confirmation Dialog */}
      <DeleteBookingConfirmDialog
        open={showDeleteConfirm}
        booking={bookingToDelete}
        resource={bookingToDelete ? resources.find(r => String(r.id) === String(bookingToDelete.resourceId)) : null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onDeleteOnlyThis={handleDeleteOnlyThis}
        onDeleteAllFuture={handleDeleteAllFuture}
        onDeleteOnlyThisDemand={handleDeleteOnlyThisDemand}
        onDeleteAllLinkedDemand={handleDeleteAllLinkedDemand}
        onDeleteForThisResource={handleDeleteForThisResource}
        onDeleteForAllResources={handleDeleteForAllResources}
      />

      {/* Bulk Delete Confirmation Dialog (Ctrl/Cmd multi-selection) */}
      <DeleteBookingsConfirmDialog
        open={bookingsToDelete.length > 0}
        bookings={bookingsToDelete}
        onConfirm={handleConfirmBulkDelete}
        onCancel={handleCancelBulkDelete}
      />

      <EditBookingConfirmDialog
        open={showEditConfirm}
        onCancel={handleCancelEditConfirm}
        onEditThisBooking={handleEditThisBooking}
        onEditAllFutureBookings={handleEditAllFutureBookings}
      />
      <EditDemandBookingConfirmDialog
        open={showDemandEditConfirm}
        onCancel={handleCancelDemandEditConfirm}
        onEditThisBooking={handleEditThisDemandBooking}
        onEditAllLinkedBookings={handleEditAllDemandBookings}
      />
      <MoveDemandBookingConfirmDialog
        open={showDemandMoveConfirm}
        onCancel={handleCancelDemandMove}
        onMoveThisBooking={() => handleDemandMoveScope('this')}
        onMoveAllFutureBookings={() => handleDemandMoveScope('all')}
      />

      {/* Move Booking Dialog */}
      <MoveBookingDialog
        open={showMoveDialog}
        onClose={handleCancelMove}
        booking={bookingToMove}
        resources={resources}
        projects={projectDetails}
        costCentres={costCentres}
        isProjectView={groupingMode === 'project-resource'}
        dataMode={currentMode}
        onMove={handleConfirmMove}
        movedSpan={bookingToMoveSpan}
        skipsNonWorkingDays={bookingToMoveSkipsNonWorkingDays}
      />

      {/* Resolve Conflict — Batch Assignment Modal */}
      {bookingsToResolve.length > 0 && (
        <AIBatchSuggestModal
          open={showResolveDialog}
          onOpenChange={(open) => {
            setShowResolveDialog(open);
            if (!open) setBookingsToResolve([]);
          }}
          bookings={bookingsToResolve as unknown as import('@/hooks/useUnallocatedBookings').UnallocatedBooking[]}
          resources={resources}
          existingBookings={bookings}
          projects={projectDetails as unknown as import('@/types').Project[]}
          onConfirm={handleResolveConfirm}
        />
      )}
    </div>
  );
}
